import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import {
  MdPlayArrow,
  MdPause,
  MdSkipNext,
  MdVolumeUp,
  MdVolumeDown,
  MdVolumeOff,
  MdSubtitles,
  MdSettings,
  MdPictureInPictureAlt,
  MdCropLandscape,
  MdOutlineWebAsset,
  MdFullscreen,
  MdFullscreenExit,
  MdCheckCircle,
  MdThumbUp,
  MdOutlineThumbUp,
  MdThumbDown,
  MdOutlineThumbDown,
  MdOutlineBookmarkAdd,
  MdBookmarkAdded,
  MdOutlinedFlag,
  MdClose,
  MdSort,
  MdArrowDropDown,
  MdArrowDropUp,
  MdCheck,
  MdOutlineAnalytics,
  MdOutlineEdit,
  MdSpeed,
  MdHighQuality,
  MdChevronRight,
  MdArrowBack,
  MdNotificationsActive,
  MdOutlineAlbum,
  MdOutlineFileDownload,
  MdDownloadDone,
  MdInfoOutline,
  MdOpenInNew,
  MdReplay,
} from 'react-icons/md';
import { RiShareForwardLine } from 'react-icons/ri';
import { recordWatchHistoryItem } from './HistoryPage';
import { getAvatarColor } from './utils/avatarUtils';
import { getActiveAccount, PRIMARY_VALID_TOKEN, SECONDARY_VALID_TOKEN, AVAILABLE_ACCOUNTS, getAvailableAccounts, getActiveUserToken } from './utils/accountUtils';
import {
  isDownloaded,
  addDownloadedVideo,
  removeDownloadedVideo,
  getDownloadSettings,
  saveDownloadSettings,
  QUALITY_PRESETS,
  estimateVideoSize,
} from './utils/downloadUtils';
import Toast from './Toast';
import ShareModal from './ShareModal';
import ReportModal from './ReportModal';
import SignInPromptModal from './SignInPromptModal';
import { HiOutlineUserCircle } from 'react-icons/hi2';
import Hls from 'hls.js';
import { API_BASE_URL, normalizeMediaUrl, USE_LIVE_BACKEND } from './config';
import { fetchPublicAlbums } from './api/studioApi';
import './WatchPage.css';

// Complete YouTube Video Quality Options with 4K, HD, SD & Framerates
export const ALL_QUALITY_OPTIONS = [
  { id: '2160p', height: 2160, label: '2160p60 4K', fullLabel: '2160p60 4K', shortLabel: '2160p 4K', badge: '4K ULTRA HD', fps: '60fps', is4K: true, isHD: true, filter: 'contrast(1.05) saturate(1.04)', rendering: '-webkit-optimize-contrast' },
  { id: '1440p', height: 1440, label: '1440p60 QHD', fullLabel: '1440p60 QHD', shortLabel: '1440p QHD', badge: 'QHD', fps: '60fps', isHD: true, filter: 'contrast(1.03) saturate(1.02)', rendering: '-webkit-optimize-contrast' },
  { id: '1080p', height: 1080, label: '1080p60 HD', fullLabel: '1080p60 HD', shortLabel: '1080p HD', badge: 'FULL HD', fps: '60fps', isHD: true, filter: 'none', rendering: 'auto' },
  { id: '720p', height: 720, label: '720p60 HD', fullLabel: '720p60 HD', shortLabel: '720p HD', badge: 'HD', fps: '60fps', isHD: true, filter: 'none', rendering: 'auto' },
  { id: '480p', height: 480, label: '480p', fullLabel: '480p (SD)', shortLabel: '480p', badge: 'SD', fps: '30fps', isSD: true, filter: 'none', rendering: 'auto' },
  { id: '360p', height: 360, label: '360p', fullLabel: '360p (SD)', shortLabel: '360p', badge: 'SD', fps: '30fps', isSD: true, filter: 'none', rendering: 'auto' },
  { id: '240p', height: 240, label: '240p', fullLabel: '240p (SD)', shortLabel: '240p', badge: 'SD', fps: '30fps', isSD: true, filter: 'none', rendering: 'auto' },
  { id: '144p', height: 144, label: '144p', fullLabel: '144p (SD)', shortLabel: '144p', badge: 'SD', fps: '30fps', isSD: true, filter: 'none', rendering: 'auto' },
];

export const QUALITY_OPTIONS = ALL_QUALITY_OPTIONS;

// Save & Remove Watch Later Helper Functions
export function saveToWatchLater(video) {
  if (!video || !video.id) return;
  try {
    const saved = localStorage.getItem('speedotube_watch_later_list');
    const list = saved ? JSON.parse(saved) : [];
    const exists = list.some((item) => String(item.id) === String(video.id));
    if (!exists) {
      const newItem = {
        id: String(video.id),
        videoUrlId: video.videoUrlId,
        title: video.title,
        channelName: video.channelName,
        channelAvatar: video.channelAvatar,
        verified: video.verified,
        views: video.views,
        duration: video.duration,
        thumbnailUrl: video.thumbnailUrl,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem('speedotube_watch_later_list', JSON.stringify([newItem, ...list]));
    }
    localStorage.setItem(`speedotube_watch_later_${video.id}`, 'true');
    window.dispatchEvent(new Event('speedotube_watch_later_updated'));
    window.dispatchEvent(new Event('speedotube_playlist_created'));
  } catch (e) {
    console.warn('saveToWatchLater error:', e);
  }
}

export function removeFromWatchLater(videoId) {
  if (!videoId) return;
  try {
    const saved = localStorage.getItem('speedotube_watch_later_list');
    const list = saved ? JSON.parse(saved) : [];
    const filtered = list.filter((item) => String(item.id) !== String(videoId));
    localStorage.setItem('speedotube_watch_later_list', JSON.stringify(filtered));
    localStorage.removeItem(`speedotube_watch_later_${videoId}`);
    window.dispatchEvent(new Event('speedotube_watch_later_updated'));
    window.dispatchEvent(new Event('speedotube_playlist_created'));
  } catch (e) {
    console.warn('removeFromWatchLater error:', e);
  }
}

// Helper storage keys scoped by active user ID to prevent cross-account like collision
export function getLikeStorageKey(videoId, userId) {
  const uId = userId || getActiveAccount()?.userId || getActiveAccount()?.id || 'guest';
  return `speedotube_liked_${uId}_${videoId}`;
}

export function getDislikeStorageKey(videoId, userId) {
  const uId = userId || getActiveAccount()?.userId || getActiveAccount()?.id || 'guest';
  return `speedotube_disliked_${uId}_${videoId}`;
}

// Save & Remove Liked Videos Helper Functions
export function saveToLikedVideos(video, userId) {
  if (!video || !video.id) return;
  try {
    const uId = userId || getActiveAccount()?.userId || getActiveAccount()?.id;
    if (!uId) return;
    const key = `speedotube_liked_videos_${uId}`;
    const saved = localStorage.getItem(key);
    let list = [];
    if (saved) {
      const parsed = JSON.parse(saved);
      list = Array.isArray(parsed) ? parsed : [];
    }
    const exists = list.some((item) => String(item.id) === String(video.id));
    if (!exists) {
      const newItem = {
        id: String(video.id),
        videoUrlId: video.videoUrlId,
        title: video.title,
        channelName: video.channelName,
        channelAvatar: video.channelAvatar,
        verified: video.verified,
        views: video.views,
        duration: video.duration,
        thumbnailUrl: video.thumbnailUrl,
        likedAt: new Date().toISOString(),
      };
      list = [newItem, ...list];
      localStorage.setItem(key, JSON.stringify(list));
    }
    localStorage.setItem(getLikeStorageKey(video.id, uId), 'true');
    window.dispatchEvent(new Event('speedotube_liked_videos_updated'));
    window.dispatchEvent(new Event('speedotube_playlist_created'));
  } catch (e) {
    console.warn('saveToLikedVideos error:', e);
  }
}

export function removeFromLikedVideos(videoId, userId) {
  if (!videoId) return;
  try {
    const uId = userId || getActiveAccount()?.userId || getActiveAccount()?.id;
    if (!uId) return;
    const key = `speedotube_liked_videos_${uId}`;
    const saved = localStorage.getItem(key);
    let list = [];
    if (saved) {
      const parsed = JSON.parse(saved);
      list = Array.isArray(parsed) ? parsed : [];
    }
    const filtered = list.filter((item) => String(item.id) !== String(videoId));
    localStorage.setItem(key, JSON.stringify(filtered));
    localStorage.removeItem(getLikeStorageKey(videoId, uId));
    window.dispatchEvent(new Event('speedotube_liked_videos_updated'));
    window.dispatchEvent(new Event('speedotube_playlist_created'));
  } catch (e) {
    console.warn('removeFromLikedVideos error:', e);
  }
}

const isLiveEnvironment = USE_LIVE_BACKEND || API_BASE_URL.includes('speedoprime.com') || API_BASE_URL.includes('103.88.81.5') || API_BASE_URL.includes('103.88.81.7') || API_BASE_URL === '';
export const DEFAULT_VIDEO_URL_ID = isLiveEnvironment ? 'ZGHDUiAEqHb' : 'HyRyzOCHONj';

// Default fallback video URL (1080p Full HD)
const DEFAULT_FALLBACK_VIDEO_URL = isLiveEnvironment
  ? (normalizeMediaUrl('/drive/videos/TQQd1789104739913/14614733-uhd_3840_2160_24fps.mp4') || `${API_BASE_URL}/drive/videos/TQQd1789104739913/14614733-uhd_3840_2160_24fps.mp4`)
  : (normalizeMediaUrl('/drive/videos/main_rahoon_196.mp4') || `${API_BASE_URL}/drive/videos/main_rahoon_196.mp4`);
const DEFAULT_FALLBACK_THUMBNAIL = normalizeMediaUrl('/drive/thumbnails/thumb_1790547804208_819.jpg') || '/img/banner.png';

// JWT Token helper for authenticated telemetry actions (re-exported from accountUtils)
export { getActiveUserToken };

export const VALID_LIVE_TOKEN = PRIMARY_VALID_TOKEN;
const AUTH_TOKEN = VALID_LIVE_TOKEN;

function getApiHeaders(withAuth = false, tokenOverride = null) {
  const headers = {
    token: 'c7e993808a344da9d59cfa20fa8b6061',
    devicetype: '0',
    request_type: 'USER',
  };
  if (withAuth) {
    const isLoggedOut = typeof window !== 'undefined' && localStorage.getItem('speedotube_logged_out') === 'true';
    if (!isLoggedOut) {
      const userToken = tokenOverride || getActiveUserToken();
      if (userToken && userToken.length >= 20) {
        headers['authorization'] = `Bearer ${userToken}`;
      }
    }
  }
  return headers;
}

// Helper to format video upload date
function formatUploadDate(dateString) {
  if (!dateString) return 'Oct 18, 2024';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return 'Oct 18, 2024';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Helper to format seconds to mm:ss or hh:mm:ss
function formatTime(sec) {
  if (!sec || isNaN(sec) || sec < 0) return '0:00';
  const total = Math.floor(sec);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const padSec = seconds < 10 ? `0${seconds}` : `${seconds}`;
  if (hours > 0) {
    const padMin = minutes < 10 ? `0${minutes}` : `${minutes}`;
    return `${hours}:${padMin}:${padSec}`;
  }
  return `${minutes}:${padSec}`;
}

// Helper to format views like YouTube
export function formatViewsCount(count) {
  if (count === null || count === undefined) return '0 views';
  const num = parseInt(count, 10);
  if (isNaN(num) || num <= 0) return '0 views';
  if (num === 1) return '1 view';
  if (num >= 1000000000) {
    return `${(num / 1000000000).toFixed(1).replace(/\.0$/, '')}B views`;
  }
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1).replace(/\.0$/, '')}M views`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1).replace(/\.0$/, '')}K views`;
  }
  return `${num.toLocaleString()} views`;
}

// Helper to format likes count like YouTube
export function formatLikesCount(count) {
  if (count === null || count === undefined) return '0';
  const num = parseInt(count, 10);
  if (isNaN(num) || num <= 0) return '0';
  if (num >= 1000000000) {
    return `${(num / 1000000000).toFixed(1).replace(/\.0$/, '')}B`;
  }
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  return `${num.toLocaleString()}`;
}

// Helper to format live channel subscribers count like YouTube
export function formatSubscribersCount(count) {
  if (count === null || count === undefined) return '0 subscribers';
  const num = parseInt(count, 10);
  if (isNaN(num) || num <= 0) return '0 subscribers';
  if (num === 1) return '1 subscriber';
  if (num >= 1000000000) {
    return `${(num / 1000000000).toFixed(1).replace(/\.0$/, '')}B subscribers`;
  }
  if (num >= 1000000) {
    const formatted = (num / 1000000).toFixed(2).replace(/\.00$/, '').replace(/(\.[1-9])0$/, '$1');
    return `${formatted}M subscribers`;
  }
  if (num >= 1000) {
    const formatted = (num / 1000).toFixed(1).replace(/\.0$/, '');
    return `${formatted}K subscribers`;
  }
  return `${num.toLocaleString()} subscribers`;
}

// Helper for relative timestamps
function timeAgo(dateString) {
  if (!dateString) return 'Just now';
  const diff = (Date.now() - new Date(dateString).getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) {
    const mins = Math.floor(diff / 60);
    return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  }
  if (diff < 86400) {
    const hrs = Math.floor(diff / 3600);
    return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  }
  if (diff < 2592000) {
    const days = Math.floor(diff / 86400);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }
  const months = Math.floor(diff / 2592000);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

export default function WatchPage({
  videoUrlId,
  activePath,
  userProfile,
  isLoggedIn,
  onLogin,
  onNavigate,
  musicSession,
  onToggleMusicPlay,
  onSelectMusicTrack,
  onSeekMusic,
  onLoadAlbum,
  onPauseMusic,
  onCloseMiniplayer,
}) {
  const [videoData, setVideoData] = useState(null);
  const [, setIsLoadingVideo] = useState(true);
  const [ecosystemVideos, setEcosystemVideos] = useState([]);

  // Check if current user is signed in (single source of truth)
  const isUserAuthenticated = useMemo(() => {
    if (typeof isLoggedIn === 'boolean') return isLoggedIn;
    if (typeof window !== 'undefined' && localStorage.getItem('speedotube_logged_out') === 'true') {
      return false;
    }
    if (userProfile && (userProfile.id || userProfile.token || userProfile.userId)) {
      return true;
    }
    const token = typeof window !== 'undefined' ? localStorage.getItem('speedotube_user_token') : null;
    const account = typeof window !== 'undefined' ? localStorage.getItem('speedotube_user_account') : null;
    return Boolean(token && account && localStorage.getItem('speedotube_logged_out') !== 'true');
  }, [isLoggedIn, userProfile]);

  // Smooth Sign-In Prompt Modal State
  const [signInPrompt, setSignInPrompt] = useState({
    isOpen: false,
    action: 'general',
    targetName: '',
  });

  const openSignInPrompt = useCallback((action = 'general', targetName = '') => {
    setSignInPrompt({
      isOpen: true,
      action,
      targetName,
    });
  }, []);

  const closeSignInPrompt = useCallback(() => {
    setSignInPrompt((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handlePromptSignIn = useCallback(() => {
    closeSignInPrompt();
    if (onLogin) {
      onLogin();
    } else if (onNavigate) {
      onNavigate('/auth');
    }
  }, [closeSignInPrompt, onLogin, onNavigate]);

  // Detect Music Album Mode (?album=3 or ?album=alb_... or ?albumUrlId=...)
  const albumIdParam = useMemo(() => {
    const searchStr = activePath && activePath.includes('?') 
      ? activePath.substring(activePath.indexOf('?')) 
      : window.location.search;
    const urlParams = new URLSearchParams(searchStr);
    return urlParams.get('album') || urlParams.get('albumUrlId');
  }, [activePath]);

  const isAlbumMode = Boolean(albumIdParam);

  // When in Video mode, immediately ensure the Album Miniplayer is closed and album audio stopped
  useEffect(() => {
    if (!isAlbumMode) {
      if (onCloseMiniplayer) {
        onCloseMiniplayer();
      } else if (onPauseMusic) {
        onPauseMusic();
      }
    }
  }, [isAlbumMode, onCloseMiniplayer, onPauseMusic]);

  // Album Audio & Playback States (Synchronized with Global Music Session)
  const [albumData, setAlbumData] = useState(() => musicSession?.album || null);
  const currentTrackIndex = musicSession?.currentTrackIndex ?? 0;
  const isAudioPlaying = Boolean(musicSession?.isPlaying);
  const audioCurrentTime = musicSession?.currentTime || 0;
  const audioDuration = musicSession?.duration || albumData?.tracks?.[currentTrackIndex]?.durationSec || 243;


  // Player States
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const playerContainerRef = useRef(null);
  const secondaryColRef = useRef(null);
  const primaryColRef = useRef(null);
  const descCardRef = useRef(null);
  const ecosystemRef = useRef(null);
  const prevRectRef = useRef(null);
  const prevSecondaryRectRef = useRef(null);
  const prevPrimaryRectRef = useRef(null);
  const prevEcosystemRectRef = useRef(null);
  const progressBarRef = useRef(null);
  const hideControlsTimerRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferedEnd, setBufferedEnd] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [isTheaterMode, setIsTheaterMode] = useState(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('theater') === '1') return true;
      return localStorage.getItem('yt_theater_mode') === 'true';
    } catch (e) {
      return false;
    }
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [autoplayNext, setAutoplayNext] = useState(() => {
    try {
      return localStorage.getItem('speedotube_autoplay') !== 'false';
    } catch (e) {
      return true;
    }
  });

  // Mid-roll In-Video Advertisements State (CRM-Controlled)
  const [adBanners, setAdBanners] = useState([]);
  const [adCuePoint, setAdCuePoint] = useState(() => 60 + Math.floor(Math.random() * 25)); // Randomly 60s - 85s
  const [isAdConsumed, setIsAdConsumed] = useState(false);
  const isAdConsumedRef = useRef(false);
  const [adState, setAdState] = useState({
    isActive: false,
    banner: null,
    countdown: 5,
    canSkip: false,
    elapsed: 0,
    duration: 15,
    showInfoModal: false,
  });
  const adTriggeredRef = useRef(false);
  const adIntervalRef = useRef(null);
  const adCuePointRef = useRef(adCuePoint);
  const adPostResumeTimeRef = useRef(null);
  const isChangingQualityRef = useRef(false);
  const qualityChangeTimerRef = useRef(null);

  useEffect(() => {
    adCuePointRef.current = adCuePoint;
  }, [adCuePoint]);

  useEffect(() => {
    isAdConsumedRef.current = isAdConsumed;
  }, [isAdConsumed]);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [isSettingsClosing, setIsSettingsClosing] = useState(false);
  const [settingsSubmenu, setSettingsSubmenu] = useState('main'); // 'main' | 'speed' | 'quality'
  const [submenuDirection, setSubmenuDirection] = useState('forward'); // 'forward' | 'backward'
  const closeSettingsTimeoutRef = useRef(null);

  const closeSettingsMenu = useCallback(() => {
    if (isSettingsClosing) return;
    setIsSettingsClosing(true);
    if (closeSettingsTimeoutRef.current) clearTimeout(closeSettingsTimeoutRef.current);
    closeSettingsTimeoutRef.current = setTimeout(() => {
      setShowSettingsMenu(false);
      setIsSettingsClosing(false);
      setSettingsSubmenu('main');
    }, 180);
  }, [isSettingsClosing]);

  const toggleSettingsMenu = useCallback(() => {
    if (showSettingsMenu) {
      closeSettingsMenu();
    } else {
      if (closeSettingsTimeoutRef.current) clearTimeout(closeSettingsTimeoutRef.current);
      setIsSettingsClosing(false);
      setSubmenuDirection('forward');
      setSettingsSubmenu('main');
      setShowSettingsMenu(true);
    }
  }, [showSettingsMenu, closeSettingsMenu]);

  const openSubmenu = useCallback((menu) => {
    setSubmenuDirection('forward');
    setSettingsSubmenu(menu);
  }, []);

  const goBackToMainMenu = useCallback(() => {
    setSubmenuDirection('backward');
    setSettingsSubmenu('main');
  }, []);
  const [activeQuality, setActiveQuality] = useState('auto');
  const [videoMaxHeight, setVideoMaxHeight] = useState(1080);
  const [qualityFilter, setQualityFilter] = useState('none');
  const [qualityRendering, setQualityRendering] = useState('auto');

  // Dynamically compute available quality options based on the video's actual resolution
  const qualityOptions = useMemo(() => {
    const filtered = ALL_QUALITY_OPTIONS.filter((q) => q.height <= videoMaxHeight);
    const topOpt = filtered[0] || ALL_QUALITY_OPTIONS.find((q) => q.id === '1080p');
    const autoOption = {
      id: 'auto',
      label: 'Auto',
      fullLabel: `Auto (${topOpt.label})`,
      shortLabel: 'Auto',
      badge: 'AUTO',
      fps: topOpt.fps,
      is4K: topOpt.is4K,
      isHD: topOpt.isHD,
      isSD: topOpt.isSD,
      height: topOpt.height,
      filter: topOpt.filter,
      rendering: topOpt.rendering,
    };
    return [autoOption, ...filtered];
  }, [videoMaxHeight]);

  // Helper to dynamically detect and apply source resolution from HTML5 video element.
  // Never downgrades videoMaxHeight below 1080p (or 2160p for 4K) when playing downscaled streams.
  const updateResolutionFromElement = useCallback((videoEl) => {
    if (!videoEl) return;
    const vH = videoEl.videoHeight || 0;
    const vW = videoEl.videoWidth || 0;

    if (vH > 0 && vW > 0) {
      // Do NOT reduce maximum capability when user chose a specific downscaled stream or is changing quality
      if (isChangingQualityRef.current || (activeQuality && activeQuality !== 'auto')) {
        return;
      }
      const effH = vW >= vH ? vH : vW;
      let detectedH = 1080;
      if (effH >= 2000) detectedH = 2160;
      else if (effH >= 1300) detectedH = 1440;
      else if (effH >= 900)  detectedH = 1080;

      // Only upgrade max height (e.g. if 4K stream detected), never downgrade below 1080
      setVideoMaxHeight((prev) => Math.max(prev || 1080, detectedH));
    }
  }, [activeQuality]);

  // Synchronize max quality whenever the video identity (ID) changes.
  // Does NOT re-run on streamUrl change, so switching quality within a video never resets options.
  useEffect(() => {
    if (!videoData?.id) return;
    const is4K = videoData.videoUrlId === 'HyRyzOCHONj' ||
                 Boolean(videoData.title && /(2160p|4k|4K)/i.test(videoData.title)) ||
                 Boolean(videoData.streamUrl && /(2160p|4k)/i.test(videoData.streamUrl));

    const initialMax = is4K ? 2160 : 1080;
    setVideoMaxHeight(initialMax);
    setActiveQuality((prev) => (!is4K && (prev === '2160p' || prev === '1440p') ? '1080p' : prev));

    if (videoRef.current && (videoRef.current.videoHeight > 0 || videoRef.current.readyState >= 1)) {
      updateResolutionFromElement(videoRef.current);
    }
  }, [videoData?.id, updateResolutionFromElement]);

  // Synchronize browser tab title dynamically with active video or album title
  useEffect(() => {
    if (videoData?.title) {
      document.title = `${videoData.title} - SpeedoTube`;
    }
  }, [videoData?.title]);

  // Ensure active quality never exceeds the video's maximum available resolution
  useEffect(() => {
    if (videoMaxHeight <= 1080 && (activeQuality === '2160p' || activeQuality === '1440p')) {
      setActiveQuality('1080p');
    }
    const currentOpt = qualityOptions.find((q) => q.id === activeQuality);
    if (!currentOpt && activeQuality !== 'auto') {
      const topOpt = qualityOptions.find((q) => q.id !== 'auto') || qualityOptions[0];
      if (topOpt) {
        setActiveQuality(topOpt.id);
      }
    }
  }, [qualityOptions, activeQuality, videoMaxHeight]);
  const [qualityOsd, setQualityOsd] = useState({ show: false, label: '', fps: '', badge: '' });
  const qualityOsdTimerRef = useRef(null);
  const [centerRipple, setCenterRipple] = useState(null); // { type, id }
  const settingsWrapRef = useRef(null);
  const rippleTimerRef = useRef(null);
  const autoPlayOnLoadRef = useRef(false);
  const [autoplayCountdown, setAutoplayCountdown] = useState(null); // { secondsLeft: 5, nextVideo }
  const autoplayTimerRef = useRef(null);

  // Real-time FPS measurement refs
  const fpsLastTotalFramesRef = useRef(0);
  const fpsLastTimestampRef = useRef(0);
  const fpsRafRef = useRef(null);
  const [realFps, setRealFps] = useState(null); // e.g. 30 or 60

  const cancelAutoplayCountdown = useCallback(() => {
    if (autoplayTimerRef.current) {
      clearInterval(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }
    setAutoplayCountdown(null);
  }, []);

  // YouTube-Style Interactive Button Animation States & Refs
  const [likeAnimType, setLikeAnimType] = useState(null); // 'liked' | 'unliked' | null
  const [likeAnimKey, setLikeAnimKey] = useState(0);
  const [isDislikeAnimating, setIsDislikeAnimating] = useState(false);
  const [subAnimType, setSubAnimType] = useState(null); // 'subscribing' | 'unsubscribing' | null
  const [subAnimKey, setSubAnimKey] = useState(0);
  const [isSaveAnimating, setIsSaveAnimating] = useState(false);
  const likeAnimTimerRef = useRef(null);
  const dislikeAnimTimerRef = useRef(null);
  const subAnimTimerRef = useRef(null);
  const saveAnimTimerRef = useRef(null);

  // Outside click listener to dismiss Settings Popup cleanly with exit transition
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (settingsWrapRef.current && !settingsWrapRef.current.contains(e.target)) {
        closeSettingsMenu();
      }
    };
    if (showSettingsMenu) {
      document.addEventListener('mousedown', handleOutsideClick);
      return () => document.removeEventListener('mousedown', handleOutsideClick);
    }
  }, [showSettingsMenu, closeSettingsMenu]);

  // Scrubber Hover Preview
  const [scrubHoverTime, setScrubHoverTime] = useState(null);
  const [scrubHoverPos, setScrubHoverPos] = useState(0);
  const isDraggingScrubRef = useRef(false);
  const [isDraggingScrub, setIsDraggingScrub] = useState(false);

  // Social & Real-time Telemetry States
  const [isLiked, setIsLiked] = useState(false);
  const [isDisliked, setIsDisliked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [viewsCount, setViewsCount] = useState(0);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [isSavedToWatchLater, setIsSavedToWatchLater] = useState(false);
  const [isDescExpanded, setIsDescExpanded] = useState(false);

  // Dynamic Description Tags (Computed from the actual video data being watched)
  const displayTags = useMemo(() => {
    if (isAlbumMode) {
      const albumTitle = (albumData?.title || 'Album').split('|')[0].replace(/[^a-zA-Z0-9]/g, '');
      const artist = (albumData?.artist || 'Artist').replace(/[^a-zA-Z0-9]/g, '');
      return `#${albumTitle} #${artist} #SpeedoMusic`;
    }

    // 1. Priority 1: Extract any #hashtags written directly inside the real video description
    if (videoData?.description) {
      const descHashtags = (videoData.description.match(/#[a-zA-Z0-9_]+/g) || []);
      if (descHashtags.length > 0) {
        return descHashtags.slice(0, 5).join(' ');
      }
    }

    // 2. Priority 2: Use real tags stored on the video from database or upload form
    if (videoData?.tags) {
      let rawTags = videoData.tags;
      if (typeof rawTags === 'string') {
        try {
          const parsed = JSON.parse(rawTags);
          if (Array.isArray(parsed)) rawTags = parsed;
          else rawTags = rawTags.split(',');
        } catch (e) {
          rawTags = rawTags.split(',');
        }
      }
      if (Array.isArray(rawTags)) {
        const formatted = rawTags
          .map((t) => String(t).trim().replace(/^#/, '').replace(/\s+/g, ''))
          .filter((t) => t.length > 0)
          .slice(0, 5)
          .map((t) => `#${t}`);
        if (formatted.length > 0) {
          return formatted.join(' ');
        }
      }
    }

    // 3. Priority 3: Derive clean hashtags from the real video title keywords
    if (videoData?.title) {
      const cleanWords = videoData.title
        .replace(/[^\w\s]/gi, ' ')
        .split(/\s+/)
        .filter((w) => w.length >= 3 && !/^(official|video|audio|full|song|feat|with|from|and|the)$/i.test(w))
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

      if (cleanWords.length > 0) {
        const generated = [];
        for (let i = 0; i < cleanWords.length && generated.length < 4; i += 2) {
          const tag = cleanWords[i] + (cleanWords[i + 1] ? cleanWords[i + 1] : '');
          generated.push(`#${tag}`);
        }
        if (generated.length > 0) {
          return generated.join(' ');
        }
      }
    }

    // Priority 4: Category fallback
    if (videoData?.category_name) {
      return `#${videoData.category_name.charAt(0).toUpperCase() + videoData.category_name.slice(1)} #SpeedoTube`;
    }
    return '#SpeedoTube';
  }, [isAlbumMode, albumData, videoData]);

  // Dynamic Music Credits in Description Box (Derived from the real uploaded video metadata)
  const musicCredits = useMemo(() => {
    // 1. Album Mode
    if (isAlbumMode) {
      return {
        song: albumData?.tracks?.[currentTrackIndex]?.title || albumData?.title || 'Music Track',
        artists: albumData?.artist || videoData?.channelName || 'Artist',
        album: albumData?.title || 'Speedo Music Album',
        license: albumData?.recordLabel || 'Speedo Music Records',
      };
    }

    // 2. Video with explicit music_metadata stored in video object
    if (videoData?.music_metadata) {
      return videoData.music_metadata;
    }

    // 3. Determine if the uploaded video is a music video based on category or title
    const categoryName = (videoData?.category_name || '').toLowerCase();
    const isMusicCategory = categoryName === 'music' || categoryName === 'music & audio' || videoData?.category_id === 14;
    const title = videoData?.title || '';
    const isMusicTitle = /(song|music|audio|album|feat|ft\.|soundtrack|remix|acoustic|singer)/i.test(title);

    // If it's NOT a music video, do NOT display a fake music card
    if (!isMusicCategory && !isMusicTitle) {
      return null;
    }

    // 4. Intelligently extract song, artists, and album from the real uploaded video's title and channel
    let songName = '';
    let artistsName = '';

    // Split title by common delimiters: " _ ", " | ", " - ", " : "
    const segments = title.split(/\s*[_|–—:-]\s*/).map((s) => s.trim()).filter(Boolean);

    if (segments.length >= 2) {
      songName = segments[0].replace(/\b(Full Video|Official Video|Official Audio|Video Song|Lyrical Video|Audio)\b/gi, '').trim();
      const artistSegment = segments[segments.length - 1];
      if (artistSegment && artistSegment.length > 2) {
        artistsName = artistSegment.replace(/\b(Full Video|Official Video|Audio|Song)\b/gi, '').trim();
      }
    } else {
      songName = title.replace(/\b(Full Video|Official Video|Official Audio|Video Song|Lyrical Video|Audio)\b/gi, '').trim();
    }

    if (!songName) {
      songName = title.slice(0, 35).trim();
    }

    if (!artistsName) {
      artistsName = videoData?.channelName || 'SpeedoTube Artist';
    }

    const albumName = `${songName} - Single`;
    const channel = videoData?.channelName || 'Creator';
    const licenseText = videoData?.license || `SpeedoTube Music Licensing (on behalf of ${channel})`;

    return {
      song: songName,
      artists: artistsName,
      album: albumName,
      license: licenseText,
    };
  }, [isAlbumMode, albumData, currentTrackIndex, videoData]);

  const viewRecordedRef = useRef({});
  // YouTube 30-Second Playback Watch Threshold & Anti-Spam Tracking
  const accumulatedWatchTimeRef = useRef(0);
  const lastPlaybackTimestampRef = useRef(null);
  const viewRegisteredRef = useRef(false);
  const playButtonSpamGuardRef = useRef(0);

  // Active Logged-in Account state with real-time reactivity
  const [activeAccount, setActiveAccountState] = useState(() => {
    return userProfile || getActiveAccount();
  });

  useEffect(() => {
    if (userProfile) setActiveAccountState(userProfile);
  }, [userProfile]);

  // Dynamic Role Detection: True ONLY if the logged-in viewer is actually the creator of this video
  const isOwner = useMemo(() => {
    const isLoggedOut = typeof window !== 'undefined' && localStorage.getItem('speedotube_logged_out') === 'true';
    if (isLoggedOut || !isUserAuthenticated) return false;
    const currentUserAccount = activeAccount || userProfile || getActiveAccount();
    if (!currentUserAccount) return false;

    const currentUserId = currentUserAccount?.id || currentUserAccount?.userId;
    const currentLegacyId = currentUserAccount?.legacyId;
    const currentChannelId = currentUserAccount?.channelId || currentUserAccount?.channel_id;
    const currentName = (currentUserAccount?.name || currentUserAccount?.fullName || '').trim().toLowerCase();
    const currentCleanName = currentName.replace(/[^a-z0-9]/gi, '');
    const currentHandle = (currentUserAccount?.handle || currentUserAccount?.handleName || currentUserAccount?.username || '')
      .trim()
      .toLowerCase()
      .replace(/^@/, '');

    const videoUserId = videoData?.userId;
    const videoChannelId = videoData?.channelId;
    const videoChannelName = (videoData?.channelName || '').trim().toLowerCase();
    const videoCleanChannelName = videoChannelName.replace(/[^a-z0-9]/gi, '');
    const videoHandle = (videoData?.handleName || '').trim().toLowerCase().replace(/^@/, '');
    const videoTitle = (videoData?.title || '').toLowerCase();

    // Match by User ID or Legacy ID
    if (currentUserId && videoUserId && String(currentUserId) === String(videoUserId)) return true;
    if (currentLegacyId && videoUserId && String(currentLegacyId) === String(videoUserId)) return true;

    // Match by Channel ID
    if (currentChannelId && videoChannelId && String(currentChannelId) === String(videoChannelId)) return true;

    // Match by Channel handle or name
    if (currentHandle && videoHandle && (currentHandle === videoHandle || currentHandle.includes(videoHandle) || videoHandle.includes(currentHandle))) return true;
    if (currentName && videoChannelName && (currentName === videoChannelName || currentName.includes(videoChannelName) || videoChannelName.includes(currentName))) return true;
    if (currentCleanName && videoCleanChannelName && currentCleanName === videoCleanChannelName) return true;

    // Cross-account ecosystem match: Aukat dikhadi owns Channel 40 / User 3 & 198 / "Main Rahoon Ya Na Rahoon"
    const isCurrentAukat = currentChannelId === 40 || String(currentChannelId) === '40' || currentUserId === 198 || currentLegacyId === 3 || currentHandle.includes('aukat') || currentName.includes('aukat');
    const isVideoAukat = videoChannelId === 40 || String(videoChannelId) === '40' || videoUserId === 3 || videoUserId === 198 || videoHandle.includes('aukat') || videoChannelName.includes('aukat') || videoTitle.includes('rahoon');
    if (isCurrentAukat && isVideoAukat) return true;

    // Cross-account ecosystem match: Prathu owns Channel 38 / User 1 & 147 / "APT."
    const isCurrentPrathu = currentChannelId === 38 || String(currentChannelId) === '38' || currentUserId === 147 || currentLegacyId === 1 || currentHandle.includes('prath') || currentName.includes('prath');
    const isVideoPrathu = videoChannelId === 38 || String(videoChannelId) === '38' || videoUserId === 1 || videoUserId === 147 || videoHandle.includes('prath') || videoChannelName.includes('prath') || videoTitle.includes('apt');
    if (isCurrentPrathu && isVideoPrathu) return true;

    return false;
  }, [videoData, activeAccount, userProfile, isUserAuthenticated]);

  // Comments State - Real live database comments only
  const [comments, setComments] = useState([]);
  const [, setIsCommentsLoading] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [activeReplyId, setActiveReplyId] = useState(null);
  const [replyInputText, setReplyInputText] = useState({});
  const [isSubmittingReply, setIsSubmittingReply] = useState({});
  const [repliesMap, setRepliesMap] = useState({});
  const [expandedReplies, setExpandedReplies] = useState({});
  const [loadingReplies, setLoadingReplies] = useState({});
  const [sortBy, setSortBy] = useState('top'); // 'top' | 'newest'
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const sortMenuRef = useRef(null);

  // Close comments sort menu on outside click or Escape
  useEffect(() => {
    if (!isSortMenuOpen) return;
    const handleClickOutside = (e) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target)) {
        setIsSortMenuOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsSortMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSortMenuOpen]);

  // Dynamically sorted comments (Top comments vs Newest first)
  const sortedComments = useMemo(() => {
    if (!comments || comments.length === 0) return [];

    // Deduplicate comments to prevent any comment appearing twice (server vs optimistic local)
    const seenIds = new Set();
    const seenContent = new Set();
    const uniqueList = [];

    for (const c of comments) {
      if (!c) continue;
      const cId = String(c.id || '');
      if (cId && seenIds.has(cId)) continue;
      if (cId) seenIds.add(cId);

      // Check author + trimmed text uniqueness for optimistic vs server comments
      const textKey = `${(c.handle || c.author || '').toLowerCase()}_${(c.text || '').trim()}`;
      if (textKey && seenContent.has(textKey)) continue;
      if (textKey) seenContent.add(textKey);

      uniqueList.push(c);
    }

    if (sortBy === 'top') {
      return uniqueList.sort((a, b) => {
        const diffLikes = (b.likes || 0) - (a.likes || 0);
        if (diffLikes !== 0) return diffLikes;
        const timeA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
        const timeB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
        return timeB - timeA;
      });
    } else {
      return uniqueList.sort((a, b) => {
        const timeA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
        const timeB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
        if (timeA !== timeB) return timeB - timeA;
        return Number(b.id || 0) - Number(a.id || 0);
      });
    }
  }, [comments, sortBy]);

  // Report Modal State
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isCommentFocused, setIsCommentFocused] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const isSubmittingCommentRef = useRef(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Toast Notification
  const [toast, setToast] = useState({ isOpen: false, message: '', icon: null, toastKey: 0 });

  const handleCloseReportModal = useCallback(() => {
    setIsReportModalOpen(false);
  }, []);

  const handleCloseShareModal = useCallback(() => {
    setIsShareModalOpen(false);
  }, []);

  const handleCloseToast = useCallback(() => {
    setToast(prev => ({ ...prev, isOpen: false }));
  }, []);

  const showToast = useCallback((message, icon = null) => {
    setToast({
      isOpen: true,
      message,
      icon,
      toastKey: Date.now(),
    });
  }, []);

  // Download State & Handlers
  const [isDownloadedState, setIsDownloadedState] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isDownloadModalClosing, setIsDownloadModalClosing] = useState(false);
  const [selectedDownloadQuality, setSelectedDownloadQuality] = useState('720p');
  const [rememberDownloadQuality, setRememberDownloadQuality] = useState(false);
  const dlClosingTimerRef = useRef(null);

  const openDownloadModal = () => {
    if (dlClosingTimerRef.current) clearTimeout(dlClosingTimerRef.current);
    setIsDownloadModalClosing(false);
    setIsDownloadModalOpen(true);
  };

  const closeDownloadModal = () => {
    if (isDownloadModalClosing) return;
    setIsDownloadModalClosing(true);
    dlClosingTimerRef.current = setTimeout(() => {
      setIsDownloadModalOpen(false);
      setIsDownloadModalClosing(false);
    }, 220);
  };

  useEffect(() => {
    return () => {
      if (dlClosingTimerRef.current) clearTimeout(dlClosingTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const currentId = videoData?.id || videoUrlId;
    if (currentId) {
      setIsDownloadedState(isDownloaded(currentId));
    }
    const handleDownloadsChanged = () => {
      const vid = videoData?.id || videoUrlId;
      if (vid) {
        setIsDownloadedState(isDownloaded(vid));
      }
    };
    window.addEventListener('speedotube_downloads_updated', handleDownloadsChanged);
    return () => window.removeEventListener('speedotube_downloads_updated', handleDownloadsChanged);
  }, [videoData?.id, videoUrlId]);

  const executeDownload = useCallback((quality) => {
    setIsDownloading(true);
    setTimeout(() => {
      const vidObj = {
        id: videoData?.id || videoUrlId || 'HyRyzOCHONj',
        videoUrlId: videoUrlId || videoData?.video_url_id || videoData?.id || 'HyRyzOCHONj',
        title: videoData?.title || 'SpeedoTube Video',
        channelName: videoData?.channel_name || videoData?.channelName || 'Speedo Creator',
        channelAvatar: videoData?.channel_avatar || null,
        thumbnailUrl: normalizeMediaUrl(videoData?.thumbnail_url) || DEFAULT_FALLBACK_THUMBNAIL,
        duration: typeof videoData?.duration === 'number' ? formatTime(videoData.duration) : (videoData?.duration || '2:53'),
        views: formatViewsCount(videoData?.views),
      };
      addDownloadedVideo(vidObj, quality);
      setIsDownloading(false);
      setIsDownloadedState(true);
      showToast('Downloaded to Downloads', <MdDownloadDone size={18} style={{ color: '#3ea6ff' }} />);
    }, 450);
  }, [videoData, videoUrlId, showToast]);

  const handleDownloadClick = useCallback(() => {
    if (!isUserAuthenticated) {
      openSignInPrompt('general', videoData?.title || '');
      return;
    }
    const currentId = videoData?.id || videoUrlId;
    if (isDownloadedState) {
      removeDownloadedVideo(currentId);
      setIsDownloadedState(false);
      showToast('Removed from Downloads', <MdOutlineFileDownload size={18} />);
      return;
    }

    const settings = getDownloadSettings();
    if (settings.defaultQuality && settings.defaultQuality !== 'ask') {
      executeDownload(settings.defaultQuality);
    } else {
      openDownloadModal();
    }
  }, [isUserAuthenticated, openSignInPrompt, isDownloadedState, videoData, videoUrlId, executeDownload, showToast]);

  const handleConfirmDownloadModal = useCallback(() => {
    if (rememberDownloadQuality) {
      saveDownloadSettings({ ...getDownloadSettings(), defaultQuality: selectedDownloadQuality });
    }
    closeDownloadModal();
    executeDownload(selectedDownloadQuality);
  }, [rememberDownloadQuality, selectedDownloadQuality, executeDownload]);

  // Smooth Theater Mode Toggle with Fluid FLIP Transition
  const toggleTheaterMode = useCallback(() => {
    if (playerContainerRef.current) {
      prevRectRef.current = playerContainerRef.current.getBoundingClientRect();
    }
    if (secondaryColRef.current) {
      prevSecondaryRectRef.current = secondaryColRef.current.getBoundingClientRect();
    }
    if (primaryColRef.current) {
      prevPrimaryRectRef.current = primaryColRef.current.getBoundingClientRect();
    }
    if (ecosystemRef.current) {
      prevEcosystemRectRef.current = ecosystemRef.current.getBoundingClientRect();
    }
    setIsTheaterMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('yt_theater_mode', String(next));
      } catch (e) {}
      showToast(next ? 'Theater mode on' : 'Default view on');
      return next;
    });
  }, []);

  // FLIP Animation: Smoothly interpolates the player, sidebar, primary and ecosystem between modes
  useLayoutEffect(() => {
    // 1. Interpolate Player Stage & Box
    if (prevRectRef.current && playerContainerRef.current) {
      const first = prevRectRef.current;
      const last = playerContainerRef.current.getBoundingClientRect();
      prevRectRef.current = null;

      const deltaX = first.left - last.left;
      const deltaY = first.top - last.top;
      const deltaW = first.width / (last.width || 1);
      const deltaH = first.height / (last.height || 1);

      if (
        Math.abs(deltaX) > 1 ||
        Math.abs(deltaY) > 1 ||
        Math.abs(deltaW - 1) > 0.01 ||
        Math.abs(deltaH - 1) > 0.01
      ) {
        const el = playerContainerRef.current;
        el.style.transformOrigin = 'top left';
        el.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${deltaW}, ${deltaH})`;
        el.style.transition = 'none';

        requestAnimationFrame(() => {
          el.style.transition = 'transform 360ms cubic-bezier(0.4, 0, 0.2, 1), border-radius 300ms ease';
          el.style.transform = '';

          const onEnd = () => {
            el.style.transition = '';
            el.style.transformOrigin = '';
            el.removeEventListener('transitionend', onEnd);
          };
          el.addEventListener('transitionend', onEnd);
        });
      }
    }

    // 2. Interpolate Secondary (Comments) Column
    if (prevSecondaryRectRef.current && secondaryColRef.current) {
      const first = prevSecondaryRectRef.current;
      const last = secondaryColRef.current.getBoundingClientRect();
      prevSecondaryRectRef.current = null;

      const deltaY = first.top - last.top;
      if (Math.abs(deltaY) > 2) {
        const secEl = secondaryColRef.current;
        secEl.style.transform = `translateY(${deltaY}px)`;
        secEl.style.transition = 'none';

        requestAnimationFrame(() => {
          secEl.style.transition = 'transform 360ms cubic-bezier(0.4, 0, 0.2, 1)';
          secEl.style.transform = '';

          const onEndSec = () => {
            secEl.style.transition = '';
            secEl.removeEventListener('transitionend', onEndSec);
          };
          secEl.addEventListener('transitionend', onEndSec);
        });
      }
    }

    // 3. Interpolate Primary (Title, Actions, Description) Column
    if (prevPrimaryRectRef.current && primaryColRef.current) {
      const first = prevPrimaryRectRef.current;
      const last = primaryColRef.current.getBoundingClientRect();
      prevPrimaryRectRef.current = null;

      const deltaY = first.top - last.top;
      if (Math.abs(deltaY) > 2) {
        const primEl = primaryColRef.current;
        primEl.style.transform = `translateY(${deltaY}px)`;
        primEl.style.transition = 'none';

        requestAnimationFrame(() => {
          primEl.style.transition = 'transform 360ms cubic-bezier(0.4, 0, 0.2, 1)';
          primEl.style.transform = '';

          const onEndPrim = () => {
            primEl.style.transition = '';
            primEl.removeEventListener('transitionend', onEndPrim);
          };
          primEl.addEventListener('transitionend', onEndPrim);
        });
      }
    }

    // 4. Interpolate Ecosystem Section ("More Videos you may Like")
    if (prevEcosystemRectRef.current && ecosystemRef.current) {
      const first = prevEcosystemRectRef.current;
      const last = ecosystemRef.current.getBoundingClientRect();
      prevEcosystemRectRef.current = null;

      const deltaY = first.top - last.top;
      if (Math.abs(deltaY) > 2) {
        const ecoEl = ecosystemRef.current;
        ecoEl.style.transform = `translateY(${deltaY}px)`;
        ecoEl.style.transition = 'none';

        requestAnimationFrame(() => {
          ecoEl.style.transition = 'transform 360ms cubic-bezier(0.4, 0, 0.2, 1)';
          ecoEl.style.transform = '';

          const onEndEco = () => {
            ecoEl.style.transition = '';
            ecoEl.removeEventListener('transitionend', onEndEco);
          };
          ecoEl.addEventListener('transitionend', onEndEco);
        });
      }
    }
  }, [isTheaterMode]);

  // Synchronize Comments Column Height to Align Perfectly Till the Bottom of Description Card
  useLayoutEffect(() => {
    const updateCommentsHeight = () => {
      if (isTheaterMode) return;

      if (descCardRef.current && playerContainerRef.current) {
        const playerRect = playerContainerRef.current.getBoundingClientRect();
        const descRect = descCardRef.current.getBoundingClientRect();
        const alignedHeight = Math.round(descRect.bottom - playerRect.top);

        if (alignedHeight > 100) {
          document.documentElement.style.setProperty('--comments-aligned-height', `${alignedHeight}px`);
          document.documentElement.style.setProperty('--player-box-height', `${alignedHeight}px`);
          return;
        }
      }

      // Fallback calculation using offset heights if bounding rects are not ready
      if (playerContainerRef.current && primaryColRef.current) {
        const playerH = playerContainerRef.current.offsetHeight || 0;
        const primaryH = primaryColRef.current.offsetHeight || 0;
        const total = playerH + primaryH + 16;
        if (total > 100) {
          document.documentElement.style.setProperty('--comments-aligned-height', `${total}px`);
          document.documentElement.style.setProperty('--player-box-height', `${total}px`);
        }
      }
    };

    updateCommentsHeight();

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateCommentsHeight();
      });

      if (playerContainerRef.current) resizeObserver.observe(playerContainerRef.current);
      if (descCardRef.current) resizeObserver.observe(descCardRef.current);
      if (primaryColRef.current) resizeObserver.observe(primaryColRef.current);
    }

    // Keep smoothly updated during description expand/collapse animation (350ms)
    let animFrameId = null;
    const startTime = performance.now();
    const animateHeight = (now) => {
      updateCommentsHeight();
      if (now - startTime < 400) {
        animFrameId = requestAnimationFrame(animateHeight);
      }
    };
    animFrameId = requestAnimationFrame(animateHeight);

    window.addEventListener('resize', updateCommentsHeight);
    return () => {
      window.removeEventListener('resize', updateCommentsHeight);
      if (resizeObserver) resizeObserver.disconnect();
      if (animFrameId) cancelAnimationFrame(animFrameId);
    };
  }, [isTheaterMode, isDescExpanded, videoData]);

  // 1. Fetch Real-time Comments from Backend & Synchronize with local user storage
  const fetchComments = useCallback(async (vidId, accountOverride = null) => {
    if (!vidId) return;
    setIsCommentsLoading(true);
    const currentActive = accountOverride || activeAccount || getActiveAccount();
    const curToken = currentActive?.token || getActiveUserToken(currentActive);
    const uId = currentActive?.userId || currentActive?.id || null;
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/comments/videos/${vidId}`, {
        headers: getApiHeaders(true, curToken),
      });
      if (res.ok) {
        const json = await res.json();
        const list = json?.data?.comments || [];
        const mapped = list.map((c) => {
          let author = c.channel_name || c.name || c.author_name || c.username?.replace('@', '') || 'SpeedoUser';
          if (author === 'Anonymous' || !author) {
            author = 'SpeedoUser';
          }
          let handle = c.handle_name || c.username || `@${author.toLowerCase().replace(/[^a-z0-9]/gi, '')}`;
          let avatar = normalizeMediaUrl(c.avatar_url) || '';

          const isCurrentUser = (currentActive?.userId && c.user_id && (String(c.user_id) === String(currentActive.userId) || String(c.user_id) === String(currentActive.legacyId))) ||
                                (currentActive?.id && c.user_id && (String(c.user_id) === String(currentActive.id) || String(c.user_id) === String(currentActive.legacyId))) ||
                                (currentActive?.phone && c.phone && String(c.phone) === String(currentActive.phone));

          if (isCurrentUser) {
            author = currentActive.name || currentActive.fullName || author;
            handle = currentActive.handle || handle;
            avatar = currentActive.avatar_url || currentActive.avatarUrl || currentActive.profile_image || avatar;
          } else {
            const accounts = typeof getAvailableAccounts === 'function' ? getAvailableAccounts() : AVAILABLE_ACCOUNTS;
            const matched = accounts.find(
              (a) =>
                (c.user_id && (a.userId === c.user_id || a.id === c.user_id || a.legacyId === c.user_id)) ||
                (c.phone && a.phone === c.phone) ||
                (c.username && a.username?.toLowerCase() === c.username?.toLowerCase())
            );
            if (matched) {
              author = matched.name || matched.fullName || author;
              handle = matched.handle || handle;
              avatar = matched.avatar_url || matched.avatarUrl || matched.profile_image || avatar;
            }
          }

          return {
            id: String(c.id),
            author,
            handle,
            avatar,
            timeAgo: timeAgo(c.created_date),
            createdDate: c.created_date || c.created_at || new Date().toISOString(),
            text: c.text,
            likes: Number(c.like_count) || 0,
            dislikes: Number(c.dislike_count) || 0,
            userLiked: Boolean(c.user_liked),
            userDisliked: Boolean(c.user_disliked),
            hasReplies: Boolean(c.has_replies),
            replyCount: Number(c.reply_count || c.replies_count || (c.has_replies ? 1 : 0)),
          };
        });

        // Load any comments posted by user locally for this video (scoped to current user account)
        let localComments = [];
        try {
          const raw = localStorage.getItem(`speedotube_user_comments_${uId}_${vidId}`);
          if (raw) localComments = JSON.parse(raw) || [];
        } catch (e) {}

        // Filter out any local comments that have already been persisted to the server
        const unpersistedLocal = localComments.filter((lc) => {
          const alreadyOnServer = mapped.some(
            (sc) =>
              String(sc.id) === String(lc.id) ||
              (sc.text?.trim() === lc.text?.trim() &&
                ((sc.handle && lc.handle && sc.handle.toLowerCase() === lc.handle.toLowerCase()) ||
                 (sc.author && lc.author && sc.author.toLowerCase() === lc.author.toLowerCase())))
          );
          return !alreadyOnServer;
        });

        // Clean up localStorage to discard any comments now safely on server
        try {
          if (unpersistedLocal.length === 0) {
            localStorage.removeItem(`speedotube_user_comments_${uId}_${vidId}`);
          } else {
            localStorage.setItem(`speedotube_user_comments_${uId}_${vidId}`, JSON.stringify(unpersistedLocal));
          }
          // Remove legacy unscoped comments key
          localStorage.removeItem(`speedotube_user_comments_${vidId}`);
        } catch (e) {}

        const combined = [...unpersistedLocal, ...mapped];
        setComments(combined);
        return;
      }
    } catch (e) {
      console.warn('Failed to load real-time comments from backend:', e);
    } finally {
      setIsCommentsLoading(false);
    }

    // Fallback: check local storage comments for this video
    try {
      const raw = localStorage.getItem(`speedotube_user_comments_${uId}_${vidId}`);
      if (raw) {
        setComments(JSON.parse(raw) || []);
        return;
      }
    } catch (e) {}
    setComments([]);
  }, [activeAccount]);

  // 2. YouTube-style Legitimate View Registration (30-second continuous playback requirement)
  const checkAndRegisterLegitimateView = useCallback(async () => {
    if (viewRegisteredRef.current || !videoData?.id) return;

    const vidId = videoData.id;

    if (isAlbumMode) {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/albums/${vidId}/view`, {
          method: 'POST',
          headers: getApiHeaders(true),
        });

        if (res.ok) {
          const json = await res.json();
          viewRegisteredRef.current = true;

          if (json?.data?.view_count !== undefined) {
            setViewsCount(json.data.view_count);
          } else {
            setViewsCount((prev) => prev + 1);
          }
        }
      } catch (e) {
        console.warn('Real-time legitimate album view API error:', e);
      }
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/videos/${vidId}/view`, {
        method: 'POST',
        headers: getApiHeaders(true),
      });

      if (res.ok) {
        const json = await res.json();
        viewRegisteredRef.current = true;

        if (json?.data?.view_count !== undefined) {
          setViewsCount(json.data.view_count);
        } else {
          setViewsCount((prev) => prev + 1);
        }

        // Send watch-time telemetry with valid ISO timestamps
        const now = Date.now();
        const durationSec = Math.round(accumulatedWatchTimeRef.current || 30);
        await fetch(`${API_BASE_URL}/api/v1/watch-time`, {
          method: 'POST',
          headers: {
            ...getApiHeaders(true),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            video_id: vidId,
            start_time: new Date(now - durationSec * 1000).toISOString(),
            end_time: new Date(now).toISOString(),
            start_sec: 0,
            end_sec: durationSec,
          }),
        }).catch(() => {});
      }
    } catch (e) {
      console.warn('Real-time legitimate view API error:', e);
    }
  }, [isAlbumMode, videoData?.id]);

  // 3. Fetch Live Channel Subscribers directly from Backend API
  const fetchLiveChannelSubscribers = useCallback(async (handle) => {
    if (!handle) return;
    try {
      const normalized = handle.startsWith('@') ? handle : `@${handle}`;
      const res = await fetch(`${API_BASE_URL}/api/v1/channels/${normalized}`, {
        headers: getApiHeaders(false),
      });
      if (res.ok) {
        const json = await res.json();
        if (json?.data?.channel && json.data.channel.subscribers !== undefined) {
          const liveCount = Number(json.data.channel.subscribers);
          if (!isNaN(liveCount)) {
            setSubscriberCount(liveCount);
          }
        }
      }
    } catch (e) {
      console.warn('Live subscriber query error:', e);
    }
  }, []);

  // 4. Fetch Video Metadata from Backend API
  const fetchVideoDetails = useCallback(async (id, accountOverride = null) => {
    setIsLoadingVideo(true);
    const targetAccount = accountOverride || activeAccount || getActiveAccount();
    const targetToken = targetAccount?.token || getActiveUserToken(targetAccount);
    const targetUserId = targetAccount?.userId || targetAccount?.id || null;
    const isAuth = Boolean(targetToken && (typeof window === 'undefined' || localStorage.getItem('speedotube_logged_out') !== 'true'));
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/videos/${id}`, {
        headers: getApiHeaders(isAuth, targetToken),
      });

      if (res.ok) {
        const json = await res.json();
        const vid = json?.data?.video;
        if (vid) {
          let playableUrl = normalizeMediaUrl(vid.video_url);
          let directMp4Url = vid.video_144_url && vid.video_144_url.includes('.')
            ? normalizeMediaUrl(vid.video_144_url)
            : DEFAULT_FALLBACK_VIDEO_URL;

          if (!playableUrl || playableUrl.endsWith('/') || !playableUrl.includes('.')) {
            playableUrl = directMp4Url;
          }

          const parsedViews = parseInt(vid.views, 10);
          const parsedLikes = parseInt(vid.like_count, 10);
          const parsedSubscribers = parseInt(vid.subscriber_count, 10);

          setVideoData({
            id: vid.id,
            userId: vid.user_id,
            videoUrlId: vid.video_url_id,
            title: vid.title || "Video",
            channelName: vid.channel_name || "Channel",
            channelAvatar: normalizeMediaUrl(vid.avatar_url) || "",
            channelId: vid.channel_id || null,
            handleName: vid.handle_name || "",
            verified: Boolean(vid.verified !== undefined ? vid.verified : true),
            views: !isNaN(parsedViews) ? parsedViews : 0,
            duration: vid.duration || 173,
            thumbnailUrl: normalizeMediaUrl(vid.thumbnail_url) || DEFAULT_FALLBACK_THUMBNAIL,
            description: vid.description || (vid.title ? `${vid.title}\n\nWatch and enjoy on SpeedoTube.` : "SpeedoTube Video"),
            createdDate: vid.created_date || new Date().toISOString(),
            streamUrl: playableUrl,
            mp4Url: directMp4Url,
            // Store original filename for quality switching — used to build "auto" quality URL
            originalFilename: vid.video_original_filename || vid.video_filename || null,
          });

          if (!isNaN(parsedViews)) {
            setViewsCount(parsedViews);
          }

          const uId = targetUserId;
          const isLocallyLiked = localStorage.getItem(getLikeStorageKey(vid.id, uId)) === 'true';
          const isLocallyDisliked = localStorage.getItem(getDislikeStorageKey(vid.id, uId)) === 'true';

          let resolvedLiked = false;
          let resolvedDisliked = false;

          if (vid.user_liked !== undefined && vid.user_liked !== null) {
            resolvedLiked = Boolean(vid.user_liked);
            resolvedDisliked = Boolean(vid.user_disliked);
            // Synchronize localStorage with server truth
            if (resolvedLiked) {
              localStorage.setItem(getLikeStorageKey(vid.id, uId), 'true');
              localStorage.removeItem(getDislikeStorageKey(vid.id, uId));
            } else {
              localStorage.removeItem(getLikeStorageKey(vid.id, uId));
            }
            if (resolvedDisliked) {
              localStorage.setItem(getDislikeStorageKey(vid.id, uId), 'true');
              localStorage.removeItem(getLikeStorageKey(vid.id, uId));
            } else {
              localStorage.removeItem(getDislikeStorageKey(vid.id, uId));
            }
          } else {
            if (isLocallyLiked && !isLocallyDisliked) {
              resolvedLiked = true;
            } else if (isLocallyDisliked && !isLocallyLiked) {
              resolvedDisliked = true;
            }
          }

          let baseLikes = !isNaN(parsedLikes) ? parsedLikes : 0;
          setLikeCount(baseLikes);
          setIsLiked(resolvedLiked);
          setIsDisliked(resolvedDisliked);
          if (isUserAuthenticated && vid.user_subscribed !== undefined) {
            setIsSubscribed(Boolean(vid.user_subscribed));
          } else if (!isUserAuthenticated) {
            setIsSubscribed(false);
          }
          if (vid.isWatchLater !== undefined) {
            setIsSavedToWatchLater(Boolean(vid.isWatchLater));
          } else {
            const isSavedLocal = localStorage.getItem(`speedotube_watch_later_${vid.id}`) === 'true';
            setIsSavedToWatchLater(isSavedLocal);
          }
          if (!isNaN(parsedSubscribers)) {
            setSubscriberCount(parsedSubscribers);
          }
          if (vid.handle_name) {
            fetchLiveChannelSubscribers(vid.handle_name);
          }

          // Fetch comments only - do NOT register view on load
          fetchComments(vid.id);
          accumulatedWatchTimeRef.current = 0;
          lastPlaybackTimestampRef.current = null;
          viewRegisteredRef.current = false;

          // Record in watch history
          recordWatchHistoryItem({
            id: String(vid.id),
            title: vid.title,
            channelName: vid.channel_name,
            channelAvatar: normalizeMediaUrl(vid.avatar_url),
            verified: Boolean(vid.verified),
            views: formatViewsCount(!isNaN(parsedViews) ? parsedViews : 0),
            duration: formatTime(vid.duration || 173),
            thumbnailUrl: normalizeMediaUrl(vid.thumbnail_url) || DEFAULT_FALLBACK_THUMBNAIL,
          });
          return;
        }
      }
    } catch (e) {
      console.warn('Failed to load video details from backend:', e);
    } finally {
      setIsLoadingVideo(false);
    }

    // Default Fallback
    setVideoData({
      id: isLiveEnvironment ? 261 : 157,
      videoUrlId: id || DEFAULT_VIDEO_URL_ID,
      title: isLiveEnvironment ? "Train Station" : "ROSÉ & Bruno Mars - APT. (Official Music Video)",
      channelName: isLiveEnvironment ? "speedotube3April" : "Pratham",
      channelAvatar: "",
      channelId: isLiveEnvironment ? 43 : 38,
      handleName: isLiveEnvironment ? "@speedonet" : "@Pratham",
      verified: true,
      views: 0,
      duration: isLiveEnvironment ? 16 : 173,
      thumbnailUrl: DEFAULT_FALLBACK_THUMBNAIL,
      description: isLiveEnvironment ? "Train Station\n\nWatch and enjoy on SpeedoTube." : "Official Music Video for 'APT.' by ROSÉ & Bruno Mars.\n\nListen to 'APT.': https://rose.lnk.to/APT\n\nDirected by Daniel Ramos & Bruno Mars\nProduced by Atlantic Records & THEBLACKLABEL\n\n#ROSE #BrunoMars #APT #OfficialMusicVideo",
      createdDate: new Date().toISOString(),
      streamUrl: DEFAULT_FALLBACK_VIDEO_URL,
    });
    setViewsCount(0);
    setLikeCount(0);
    fetchComments(isLiveEnvironment ? 261 : 157);
    accumulatedWatchTimeRef.current = 0;
    lastPlaybackTimestampRef.current = null;
    viewRegisteredRef.current = false;
  }, [fetchComments, fetchLiveChannelSubscribers, isUserAuthenticated]);

  // 4. Fetch ONLY Real Approved Videos from SpeedoTube Ecosystem
  const fetchEcosystemVideos = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/new-videos?limit=12&offset=0`, {
        headers: getApiHeaders(false),
      });

      if (res.ok) {
        const json = await res.json();
        const liveList = json?.data?.videos || [];
        // User requested to hide existing old videos
        const validList = liveList.filter((v) => v && v.is_new_upload === true);
        if (validList.length > 0) {
          const mapped = validList.map((v) => ({
            id: String(v.id),
            videoUrlId: v.video_url_id,
            title: v.title || 'SpeedoTube Ecosystem Video',
            channelName: v.channel_name || 'Speedo Creator',
            channelAvatar: v.channel_avatar || '',
            handleName: v.handle_name || '',
            channelId: v.channel_id || null,
            verified: v.verified !== undefined ? Boolean(Number(v.verified)) : true,
            views: formatViewsCount(v.views !== undefined ? v.views : 0),
            timestamp: timeAgo(v.created_date),
            duration: formatTime(v.duration || 173),
            thumbnailUrl: normalizeMediaUrl(v.thumbnail_url) || DEFAULT_FALLBACK_THUMBNAIL,
          }));
          setEcosystemVideos(mapped);
          return;
        } else {
          setEcosystemVideos([]);
          return;
        }
      }
    } catch (e) {
      console.warn('Failed to load ecosystem videos:', e);
    }

    setEcosystemVideos([]);
  }, []);

  // 5. Fetch Music Album Details & Tracks from Backend / Database
  const fetchAlbumDetails = useCallback(async (albId) => {
    setIsLoadingVideo(true);
    let found = null;
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/albums/${albId}`, {
        headers: getApiHeaders(true),
      });
      if (res.ok) {
        const json = await res.json();
        if (json?.data) {
          found = json.data;
        }
      }
    } catch (e) {
      console.warn('Failed to fetch album by id from backend:', e);
    }

    if (!found) {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/albums`, {
          headers: getApiHeaders(true),
        });
        if (res.ok) {
          const json = await res.json();
          if (Array.isArray(json?.data) && json.data.length > 0) {
            found = json.data.find(
              (a) => String(a.id) === String(albId) || String(a.album_url_id) === String(albId)
            );
          }
        }
      } catch (e) {
        console.warn('Failed to fetch album details from backend:', e);
      }
    }

    if (!found) {
      try {
        const publicAlbums = await fetchPublicAlbums();
        found = publicAlbums.find(
          (a) => String(a.id) === String(albId) || String(a.albumUrlId) === String(albId) || String(a.dbId) === String(albId)
        );
      } catch (e) {}
    }

    if (!found) {
      console.warn('Album not found on live server:', albId);
      setAlbumData(null);
      setIsLoadingVideo(false);
      return;
    }

    // Real metrics strictly from Database (zero dummy values!)
    const realPlays = Number(found?.plays !== undefined && found?.plays !== null ? found.plays : (found?.views !== undefined ? found.views : 0));
    const realLikes = Number(found?.like_count !== undefined && found?.like_count !== null ? found.like_count : 0);
    const realDislikes = Number(found?.dislike_count !== undefined && found?.dislike_count !== null ? found.dislike_count : 0);
    const userLiked = Boolean(found?.user_liked);
    const userDisliked = Boolean(found?.user_disliked);

    const resolvedAlbum = {
      id: found.id,
      albumUrlId: found.album_url_id || found.albumUrlId || `alb_${found.id}`,
      title: found.title || 'Untitled Album',
      artist: found.artist || 'Artist',
      creator: found.creator || found.name || 'Creator',
      genre: found.genre || 'Music',
      releaseYear: found.release_year || found.releaseYear || new Date().getFullYear().toString(),
      recordLabel: found.record_label || found.recordLabel || 'Speedo Music Records',
      coverUrl: normalizeMediaUrl(found.cover_url || found.coverUrl) || '',
      playboxBannerUrl: normalizeMediaUrl(found.playbox_banner_url || found.playboxBannerUrl || found.cover_url || found.coverUrl) || '',
      description: found.description || '',
      plays: realPlays,
      likeCount: realLikes,
      dislikeCount: realDislikes,
      userLiked: userLiked,
      userDisliked: userDisliked,
      tracks: Array.isArray(found.tracks) && found.tracks.length > 0
        ? found.tracks.map((t, idx) => ({
            id: t.id || idx + 1,
            trackNumber: t.track_number || t.trackNumber || idx + 1,
            title: t.title || `Track ${idx + 1}`,
            artist: t.artist || found.artist || 'Artist',
            duration: t.duration || '03:30',
            durationSec: t.duration_sec || t.durationSec || 210,
            audioUrl: normalizeMediaUrl(t.audio_url || t.audioUrl || t.url) || '',
            url: normalizeMediaUrl(t.audio_url || t.audioUrl || t.url) || '',
          }))
        : [],
    };

    setAlbumData(resolvedAlbum);
    if (onLoadAlbum) {
      onLoadAlbum(resolvedAlbum, false);
    }

    // Synchronize standard videoData for seamless channel metadata, subscribe, like, and description integration
    setVideoData({
      id: resolvedAlbum.id,
      videoUrlId: resolvedAlbum.albumUrlId,
      title: resolvedAlbum.title,
      channelName: resolvedAlbum.creator || resolvedAlbum.artist || found?.name || 'Creator',
      channelAvatar: normalizeMediaUrl(found?.avatar_url) || '',
      channelId: found?.channel_id || null,
      handleName: found?.handle_name || '',
      verified: true,
      views: realPlays,
      duration: resolvedAlbum.tracks[0]?.durationSec || 243,
      thumbnailUrl: resolvedAlbum.coverUrl,
      description: resolvedAlbum.description,
      createdDate: found?.created_date || new Date().toISOString(),
      streamUrl: '',
    });

    setViewsCount(realPlays);
    setLikeCount(realLikes);
    setIsLiked(userLiked);
    setIsDisliked(userDisliked);

    if (found?.handle_name) {
      fetchLiveChannelSubscribers(found.handle_name);
    } else {
      fetchLiveChannelSubscribers('@Pratham');
    }

    fetchComments(resolvedAlbum.id || 3);
    accumulatedWatchTimeRef.current = 0;
    lastPlaybackTimestampRef.current = null;
    viewRegisteredRef.current = false;
    setIsLoadingVideo(false);
  }, [fetchComments, onLoadAlbum, fetchLiveChannelSubscribers]);

  // Center Banner Play / Pause Toggle
  const toggleAlbumPlay = useCallback(() => {
    if (onLoadAlbum && albumData && (!musicSession?.album || musicSession.album.id !== albumData.id)) {
      onLoadAlbum(albumData, true);
      return;
    }
    if (onToggleMusicPlay) {
      onToggleMusicPlay();
    }
  }, [onToggleMusicPlay, onLoadAlbum, albumData, musicSession?.album]);

  // Interactive Track Click
  const handlePlayAlbumTrack = useCallback((index) => {
    if (onLoadAlbum && albumData && (!musicSession?.album || musicSession.album.id !== albumData.id)) {
      onLoadAlbum(albumData, false);
    }
    if (onSelectMusicTrack) {
      onSelectMusicTrack(index, true);
    }
  }, [onSelectMusicTrack, onLoadAlbum, albumData, musicSession?.album]);

  // 6. Legitimate Continuous Listening Watch Time & View Threshold for Music Album
  useEffect(() => {
    if (!isAlbumMode || !isAudioPlaying || viewRegisteredRef.current) return;

    const now = performance.now();
    if (lastPlaybackTimestampRef.current === null) {
      lastPlaybackTimestampRef.current = now;
      return;
    }

    const deltaSec = (now - lastPlaybackTimestampRef.current) / 1000;
    lastPlaybackTimestampRef.current = now;

    if (deltaSec > 0 && deltaSec < 2.0) {
      accumulatedWatchTimeRef.current += deltaSec;
    }

    const targetDuration = audioDuration || 243;
    const watchThreshold = Math.min(30, targetDuration * 0.5);

    if (accumulatedWatchTimeRef.current >= watchThreshold && !viewRegisteredRef.current) {
      checkAndRegisterLegitimateView();
    }
  }, [isAlbumMode, isAudioPlaying, audioCurrentTime, audioDuration, checkAndRegisterLegitimateView]);

  // Synchronize album view if recorded by App background engine
  useEffect(() => {
    const handleAlbumViewUpdate = (e) => {
      const albId = e.detail?.albumId;
      const count = e.detail?.viewCount;
      if (isAlbumMode && videoData?.id && (String(videoData.id) === String(albId) || String(videoData.videoUrlId) === String(albId))) {
        if (count !== undefined) {
          setViewsCount(count);
          viewRegisteredRef.current = true;
        }
      }
    };
    window.addEventListener('speedotube_album_view_counted', handleAlbumViewUpdate);
    return () => window.removeEventListener('speedotube_album_view_counted', handleAlbumViewUpdate);
  }, [isAlbumMode, videoData?.id, videoData?.videoUrlId]);

  // Load Video or Music Album on Mount or ID Change
  useEffect(() => {
    if (isAlbumMode) {
      fetchAlbumDetails(albumIdParam);
    } else {
      const idToLoad = videoUrlId || new URLSearchParams(window.location.search).get('v') || DEFAULT_VIDEO_URL_ID;
      fetchVideoDetails(idToLoad);
    }
    fetchEcosystemVideos();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [isAlbumMode, albumIdParam, videoUrlId, fetchAlbumDetails, fetchVideoDetails, fetchEcosystemVideos]);

  // Autoplay video once metadata is ready

  // Fetch active CRM ad banners for in-video mid-roll
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/ad_banners?type=video`, {
          headers: {
            token: 'c7e993808a344da9d59cfa20fa8b6061',
            devicetype: '0',
            request_type: 'USER',
          },
        });
        if (res.ok) {
          const json = await res.json();
          if (json?.status === 200 && Array.isArray(json?.data) && json.data.length > 0) {
            setAdBanners(json.data);
            return;
          }
        }
      } catch (e) {
        console.warn('Could not load ad banners for in-video ads:', e.message);
      }
    };
    fetchBanners();
    const handleRefresh = () => fetchBanners();
    window.addEventListener('speedotube_banners_updated', handleRefresh);
    window.addEventListener('focus', handleRefresh);
    return () => {
      window.removeEventListener('speedotube_banners_updated', handleRefresh);
      window.removeEventListener('focus', handleRefresh);
    };
  }, []);

  // Calculate random mid-roll cue point at any minute across the entire video
  const calculateRandomAdCue = useCallback((vidDur) => {
    if (!vidDur || vidDur <= 20) return 15;
    const totalMinutes = Math.floor(vidDur / 60);

    if (totalMinutes >= 2) {
      // Pick any random minute from minute 1 up to the last playable minute (totalMinutes - 1)
      const chosenMinute = 1 + Math.floor(Math.random() * (totalMinutes - 1));
      const randomSecondInMinute = Math.floor(Math.random() * 50);
      const cue = Math.min(Math.floor(vidDur - 10), chosenMinute * 60 + randomSecondInMinute);
      return cue;
    } else if (totalMinutes === 1) {
      const minSec = 20;
      const maxSec = Math.max(minSec + 5, Math.floor(vidDur - 10));
      return Math.floor(minSec + Math.random() * (maxSec - minSec));
    } else {
      return Math.floor(vidDur * (0.35 + Math.random() * 0.3));
    }
  }, []);

  // Reset ad state and calculate new random mid-roll cue point at any minute on video change
  useEffect(() => {
    const currentDur = videoRef.current?.duration || duration || 120;
    const newCue = calculateRandomAdCue(currentDur);
    setAdCuePoint(newCue);
    adCuePointRef.current = newCue;
    setIsAdConsumed(false);
    isAdConsumedRef.current = false;
    adTriggeredRef.current = false;
    adPostResumeTimeRef.current = null;
    if (adIntervalRef.current) {
      clearInterval(adIntervalRef.current);
      adIntervalRef.current = null;
    }
    setAdState({
      isActive: false,
      banner: null,
      countdown: 5,
      canSkip: false,
      elapsed: 0,
      duration: 15,
      showInfoModal: false,
    });
  }, [videoUrlId, calculateRandomAdCue]);

  // Trigger mid-roll ad playback (pauses video and runs 5s skip countdown)
  const triggerMidrollAd = useCallback(() => {
    if (!videoRef.current || adState.isActive || isChangingQualityRef.current) return;

    let banner = null;
    if (adBanners.length > 0) {
      const idx = Math.floor(Math.random() * adBanners.length);
      banner = adBanners[idx];
    } else {
      banner = {
        id: 'default_ad',
        title: 'Speedo TV',
        description: 'Watch Documentaries, Movies & Live Streams in 60 FPS Ultra HD.',
        link: 'https://www.speedotv.in/',
        ad_banner_url: `${process.env.PUBLIC_URL || ''}/img/ad_banner-1750162675963.jpeg`,
      };
    }

    let rawUrl = banner.ad_banner_url || '';
    if (rawUrl.includes('/drive/ad_banner/')) {
      rawUrl = `${API_BASE_URL}/drive/ad_banner/` + rawUrl.split('/drive/ad_banner/')[1];
    }
    const cleanImg = normalizeMediaUrl(rawUrl.replace(/([^:])\/\/+/g, '$1/')) || `${process.env.PUBLIC_URL || ''}/img/banner.png`;
    const resolvedBanner = { ...banner, ad_banner_url: cleanImg };

    videoRef.current.pause();
    setIsPlaying(false);
    adTriggeredRef.current = true;
    isAdConsumedRef.current = true;
    setIsAdConsumed(true);

    if (adIntervalRef.current) clearInterval(adIntervalRef.current);

    setAdState({
      isActive: true,
      banner: resolvedBanner,
      countdown: 5,
      canSkip: false,
      elapsed: 0,
      duration: 15,
      showInfoModal: false,
    });

    let secondsElapsed = 0;
    adIntervalRef.current = setInterval(() => {
      secondsElapsed += 1;
      setAdState((prev) => {
        if (!prev.isActive) return prev;
        const newCountdown = Math.max(0, 5 - secondsElapsed);
        const canSkipNow = secondsElapsed >= 5;

        // Auto-finish after 15 seconds if unskipped
        if (secondsElapsed >= 15) {
          clearInterval(adIntervalRef.current);
          adIntervalRef.current = null;
          const resumeTime = adPostResumeTimeRef.current;
          adPostResumeTimeRef.current = null;
          if (videoRef.current) {
            if (resumeTime !== null && !isNaN(resumeTime)) {
              videoRef.current.currentTime = resumeTime;
              setCurrentTime(resumeTime);
            }
            videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
          }
          return { ...prev, isActive: false, showInfoModal: false };
        }

        return {
          ...prev,
          countdown: newCountdown,
          canSkip: canSkipNow,
          elapsed: secondsElapsed,
        };
      });
    }, 1000);
  }, [adBanners, adState.isActive]);

  // Skip Ad handler with precise jump landing
  const handleSkipAd = useCallback(() => {
    if (adIntervalRef.current) {
      clearInterval(adIntervalRef.current);
      adIntervalRef.current = null;
    }
    const resumeTime = adPostResumeTimeRef.current;
    adPostResumeTimeRef.current = null;

    setAdState((prev) => ({ ...prev, isActive: false, showInfoModal: false }));
    if (videoRef.current) {
      if (resumeTime !== null && !isNaN(resumeTime)) {
        videoRef.current.currentTime = resumeTime;
        setCurrentTime(resumeTime);
      }
      videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }, []);

  // Developer / Test trigger support
  useEffect(() => {
    const handleTestAd = () => {
      triggerMidrollAd();
    };
    window.addEventListener('speedotube_test_ad', handleTestAd);
    return () => window.removeEventListener('speedotube_test_ad', handleTestAd);
  }, [triggerMidrollAd]);

  const handleLoadedMetadata = () => {
    if (onCloseMiniplayer) {
      onCloseMiniplayer();
    } else if (onPauseMusic) {
      onPauseMusic();
    }
    if (videoRef.current) {
      const vidDur = videoRef.current.duration || 173;
      setDuration(vidDur);

      // Dynamically detect source resolution from HTML5 video element
      updateResolutionFromElement(videoRef.current);

      // ── Real-time FPS measurement via getVideoPlaybackQuality() ──────────────
      // Stop any previous measurement loop
      if (fpsRafRef.current) {
        cancelAnimationFrame(fpsRafRef.current);
        fpsRafRef.current = null;
      }
      fpsLastTotalFramesRef.current = 0;
      fpsLastTimestampRef.current = 0;
      setRealFps(null);

      const measureFps = (timestamp) => {
        const vidEl = videoRef.current;
        if (!vidEl) return;
        const quality = vidEl.getVideoPlaybackQuality
          ? vidEl.getVideoPlaybackQuality()
          : null;
        if (quality) {
          const totalFrames = quality.totalVideoFrames || 0;
          if (fpsLastTimestampRef.current > 0 && fpsLastTotalFramesRef.current > 0) {
            const elapsed = (timestamp - fpsLastTimestampRef.current) / 1000; // seconds
            if (elapsed >= 1.0) {
              const framesDelta = totalFrames - fpsLastTotalFramesRef.current;
              const fps = Math.round(framesDelta / elapsed);
              if (fps > 0 && fps <= 120) {
                setRealFps(fps);
              }
              fpsLastTotalFramesRef.current = totalFrames;
              fpsLastTimestampRef.current = timestamp;
            }
          } else {
            // First tick — seed the counters
            fpsLastTotalFramesRef.current = totalFrames;
            fpsLastTimestampRef.current = timestamp;
          }
        }
        fpsRafRef.current = requestAnimationFrame(measureFps);
      };
      fpsRafRef.current = requestAnimationFrame(measureFps);
      // ── End FPS measurement ─────────────────────────────────────────────────


      if (!isChangingQualityRef.current) {
        // mid-roll cue point setup: ONLY if not in quality-change mode and not already initialized
        if (!adCuePointRef.current) {
          const randomCue = calculateRandomAdCue(vidDur);
          setAdCuePoint(randomCue);
          adCuePointRef.current = randomCue;
          adTriggeredRef.current = false;
          adPostResumeTimeRef.current = null;
        }

        // Check for start timestamp from URL query param (?t=15s or &t=15 or start=15)
        const urlParams = new URLSearchParams(window.location.search);
        const tParam = urlParams.get('t') || urlParams.get('start');
        if (tParam) {
          const seekSeconds = parseInt(tParam.replace(/s$/i, ''), 10);
          if (!isNaN(seekSeconds) && seekSeconds > 0) {
            videoRef.current.currentTime = seekSeconds;
            setCurrentTime(seekSeconds);
          }
        }

        videoRef.current.playbackRate = playbackRate;
        lastPlaybackTimestampRef.current = performance.now();
        videoRef.current.play().then(() => {
          setIsPlaying(true);
        }).catch((err) => {
          console.log('Autoplay unmuted blocked, falling back to muted autoplay:', err);
          if (videoRef.current) {
            videoRef.current.muted = true;
            setIsMuted(true);
            videoRef.current.play().then(() => {
              setIsPlaying(true);
              lastPlaybackTimestampRef.current = performance.now();
            }).catch(() => {});
          }
        });
      } else {
        videoRef.current.playbackRate = playbackRate;
      }
    }
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const curTime = videoRef.current.currentTime;
    if (!isDraggingScrubRef.current && !isDraggingScrub) {
      setCurrentTime(curTime);
      if (videoRef.current.buffered.length > 0) {
        setBufferedEnd(videoRef.current.buffered.end(videoRef.current.buffered.length - 1));
      }
    }

    // Mid-roll In-Video Advertisement Check (Natural playback reaching ad cue)
    const adCue = adCuePointRef.current || adCuePoint;
    if (
      !isChangingQualityRef.current &&
      !isAdConsumedRef.current &&
      !adTriggeredRef.current &&
      !adState.isActive &&
      adCue > 0 &&
      curTime >= adCue &&
      curTime > 0
    ) {
      adTriggeredRef.current = true;
      isAdConsumedRef.current = true;
      setIsAdConsumed(true);
      triggerMidrollAd();
    }

    // Accumulate legitimate forward watch time (strictly when video is active and rolling forward)
    if (!videoRef.current.paused) {
      const now = performance.now();
      if (lastPlaybackTimestampRef.current === null) {
        lastPlaybackTimestampRef.current = now;
        return;
      }

      const deltaSec = (now - lastPlaybackTimestampRef.current) / 1000;
      lastPlaybackTimestampRef.current = now;

      // Only count small continuous forward slices (0s to 2.0s), ignore scrubber seek leaps!
      if (deltaSec > 0 && deltaSec < 2.0) {
        accumulatedWatchTimeRef.current += deltaSec;
      }

      // Check YouTube 30-second playback threshold
      const targetDuration = videoRef.current.duration || duration || 30;
      const watchThreshold = Math.min(30, targetDuration * 0.5);

      if (accumulatedWatchTimeRef.current >= watchThreshold && !viewRegisteredRef.current) {
        checkAndRegisterLegitimateView();
      }
    }
  };

  const triggerCenterRipple = useCallback((type) => {
    if (rippleTimerRef.current) {
      clearTimeout(rippleTimerRef.current);
    }
    const id = Date.now();
    setCenterRipple({ type, id });
    rippleTimerRef.current = setTimeout(() => {
      setCenterRipple(null);
    }, 500);
  }, []);

  // Play / Pause Toggle with Deliberate Spam Guard & Watch Time Accounting
  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    const now = Date.now();
    // Guard against rapid button spamming (< 150ms)
    if (now - playButtonSpamGuardRef.current < 150) {
      return;
    }
    playButtonSpamGuardRef.current = now;

    if (autoplayCountdown) {
      cancelAutoplayCountdown();
    }

    if (videoRef.current.paused || videoRef.current.ended) {
      if (videoRef.current.ended) {
        videoRef.current.currentTime = 0;
        adPostResumeTimeRef.current = null;
      }
      if (onCloseMiniplayer) {
        onCloseMiniplayer();
      } else if (onPauseMusic) {
        onPauseMusic();
      }
      lastPlaybackTimestampRef.current = performance.now();
      // If user clicks play, unmute the video if it was only muted due to browser autoplay restriction
      if (videoRef.current.muted && !isMuted) {
        videoRef.current.muted = false;
      }
      const p = videoRef.current.play();
      if (p !== undefined) {
        p.then(() => {
          setIsPlaying(true);
          triggerCenterRipple('play');
        }).catch((err) => {
          console.warn('Unmuted play rejected, attempting muted play:', err);
          videoRef.current.muted = true;
          setIsMuted(true);
          videoRef.current.play().then(() => {
            setIsPlaying(true);
          }).catch(() => {
            setIsPlaying(false);
          });
        });
      }
    } else {
      if (lastPlaybackTimestampRef.current !== null) {
        const elapsed = (performance.now() - lastPlaybackTimestampRef.current) / 1000;
        if (elapsed > 0 && elapsed < 2.0) {
          accumulatedWatchTimeRef.current += elapsed;
        }
        lastPlaybackTimestampRef.current = null;
      }
      videoRef.current.pause();
      setIsPlaying(false);
      triggerCenterRipple('pause');
    }
  }, [onCloseMiniplayer, onPauseMusic, isMuted, triggerCenterRipple]);

  // Direct Unmute Action
  const handleUnmute = useCallback((e) => {
    if (e) e.stopPropagation();
    const videoEl = videoRef.current;
    if (!videoEl) return;
    videoEl.muted = false;
    setIsMuted(false);
    setVolume((prev) => (prev === 0 ? 1 : prev));
  }, []);

  // Volume Change with intuitive Ball Knob
  const handleVolumeChange = (newVol) => {
    const vol = Math.max(0, Math.min(1, newVol));
    setVolume(vol);
    if (videoRef.current) {
      videoRef.current.volume = vol;
      videoRef.current.muted = vol === 0;
      setIsMuted(vol === 0);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    videoRef.current.muted = newMuted;
    if (!newMuted && volume === 0) {
      setVolume(0.5);
      videoRef.current.volume = 0.5;
    }
  };

  // Precise Video Seeking with Mid-roll Ad Cue Protection Algorithm
  const seekVideo = useCallback((targetTime) => {
    cancelAutoplayCountdown();
    if (!videoRef.current) return;
    const currentDur = videoRef.current.duration || duration || 1;
    const clampedTime = Math.max(0, Math.min(currentDur, targetTime));
    const curTime = videoRef.current.currentTime;
    const adCue = adCuePointRef.current || adCuePoint;

    // During resolution/quality changes, never trigger ads
    if (isChangingQualityRef.current) {
      videoRef.current.currentTime = clampedTime;
      setCurrentTime(clampedTime);
      return;
    }

    // When the user directly jumps ahead of the yellow ad patch line anywhere ahead of it:
    // If the video was currently before the ad cue (or at beginning) and user jumps at or past the cue point:
    const isJumpingPastAd = adCue > 0 && curTime < adCue && clampedTime >= (adCue - 0.5);

    if (!isAdConsumedRef.current && !adTriggeredRef.current && !adState.isActive && isJumpingPastAd) {
      adPostResumeTimeRef.current = clampedTime;
      adTriggeredRef.current = true;
      isAdConsumedRef.current = true;
      setIsAdConsumed(true);
      setCurrentTime(clampedTime);
      triggerMidrollAd();
      return;
    }

    videoRef.current.currentTime = clampedTime;
    setCurrentTime(clampedTime);
  }, [cancelAutoplayCountdown, duration, adState.isActive, triggerMidrollAd, adCuePoint, isAdConsumed]);

  // Scrubber Seeking & Smooth Scrub-Dragging
  const getScrubberPercentFromClientX = useCallback((clientX) => {
    if (!progressBarRef.current) return 0;
    const rect = progressBarRef.current.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    const clickX = clientX - rect.left;
    return Math.max(0, Math.min(1, clickX / rect.width));
  }, []);

  const handleScrubberClick = (e) => {
    if (!progressBarRef.current || !videoRef.current) return;
    const pct = getScrubberPercentFromClientX(e.clientX);
    const currentDur = videoRef.current.duration || duration || 1;
    seekVideo(pct * currentDur);
  };

  const handleScrubberMouseDown = (e) => {
    if (e.button !== 0 || !progressBarRef.current || !videoRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    isDraggingScrubRef.current = true;
    setIsDraggingScrub(true);

    const initialPct = getScrubberPercentFromClientX(e.clientX);
    const currentDur = videoRef.current.duration || duration || 1;
    const initialTargetTime = initialPct * currentDur;
    setCurrentTime(initialTargetTime);
    setScrubHoverPos(e.clientX - progressBarRef.current.getBoundingClientRect().left);
    setScrubHoverTime(initialTargetTime);

    const onMove = (moveEvt) => {
      if (!isDraggingScrubRef.current || !progressBarRef.current || !videoRef.current) return;
      moveEvt.preventDefault();
      const movePct = getScrubberPercentFromClientX(moveEvt.clientX);
      const dur = videoRef.current.duration || duration || 1;
      const moveTime = movePct * dur;
      setCurrentTime(moveTime);
      setScrubHoverPos(moveEvt.clientX - progressBarRef.current.getBoundingClientRect().left);
      setScrubHoverTime(moveTime);
    };

    const onUp = (upEvt) => {
      isDraggingScrubRef.current = false;
      setIsDraggingScrub(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);

      const upPct = getScrubberPercentFromClientX(upEvt.clientX);
      const dur = videoRef.current ? (videoRef.current.duration || duration || 1) : (duration || 1);
      seekVideo(upPct * dur);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleScrubberTouchStart = (e) => {
    if (!progressBarRef.current || !videoRef.current || !e.touches[0]) return;
    isDraggingScrubRef.current = true;
    setIsDraggingScrub(true);
    const touch = e.touches[0];
    const initialPct = getScrubberPercentFromClientX(touch.clientX);
    const currentDur = videoRef.current.duration || duration || 1;
    setCurrentTime(initialPct * currentDur);

    const onTouchMove = (moveEvt) => {
      if (!isDraggingScrubRef.current || !moveEvt.touches[0] || !videoRef.current) return;
      const movePct = getScrubberPercentFromClientX(moveEvt.touches[0].clientX);
      const dur = videoRef.current.duration || duration || 1;
      setCurrentTime(movePct * dur);
    };

    const onTouchEnd = (endEvt) => {
      isDraggingScrubRef.current = false;
      setIsDraggingScrub(false);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      const touchEnd = endEvt.changedTouches?.[0];
      if (touchEnd && videoRef.current) {
        const endPct = getScrubberPercentFromClientX(touchEnd.clientX);
        const dur = videoRef.current.duration || duration || 1;
        seekVideo(endPct * dur);
      }
    };

    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
  };

  const handleScrubberMouseMove = (e) => {
    if (!progressBarRef.current) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const hoverX = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(1, hoverX / rect.width));
    setScrubHoverPos(hoverX);
    setScrubHoverTime(pct * (duration || 1));
  };

  const handleScrubberMouseLeave = () => {
    if (!isDraggingScrubRef.current) {
      setScrubHoverTime(null);
    }
  };

  // Auto-hide Controls
  const handleMouseMovePlayer = () => {
    setShowControls(true);
    if (hideControlsTimerRef.current) clearTimeout(hideControlsTimerRef.current);
    if (isPlaying) {
      hideControlsTimerRef.current = setTimeout(() => {
        setShowControls(false);
        setShowSettingsMenu(false);
      }, 3000);
    }
  };

  const handleMouseLeavePlayer = () => {
    if (isPlaying) {
      setShowControls(false);
      setShowSettingsMenu(false);
    }
  };

  // Fullscreen Toggle
  const toggleFullscreen = () => {
    if (!playerContainerRef.current) return;
    if (!document.fullscreenElement) {
      playerContainerRef.current.requestFullscreen?.().then(() => {
        setIsFullscreen(true);
      }).catch((e) => console.log('Fullscreen error:', e));
    } else {
      document.exitFullscreen?.().then(() => {
        setIsFullscreen(false);
      }).catch((e) => console.log('Exit fullscreen error:', e));
    }
  };

  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  // Keyboard Shortcuts: t for smooth theater mode, space/k for play, etc.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      if (e.key === ' ' || e.key === 'k') {
        e.preventDefault();
        if (isAlbumMode) {
          toggleAlbumPlay();
        } else {
          togglePlay();
        }
      } else if (e.key === 't') {
        e.preventDefault();
        toggleTheaterMode();
      } else if (e.key === 'f') {
        e.preventDefault();
        toggleFullscreen();
      } else if (e.key === 'm') {
        e.preventDefault();
        toggleMute();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (videoRef.current) {
          seekVideo(videoRef.current.currentTime + 5);
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (videoRef.current) {
          seekVideo(videoRef.current.currentTime - 5);
        }
      } else if (e.key === 'Escape') {
        if (autoplayCountdown) {
          e.preventDefault();
          cancelAutoplayCountdown();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleTheaterMode, duration, autoplayCountdown, cancelAutoplayCountdown, seekVideo]);

  // Miniplayer (Picture-in-Picture)
  const togglePiP = async () => {
    if (!videoRef.current) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (document.pictureInPictureEnabled) {
        await videoRef.current.requestPictureInPicture();
      }
    } catch (e) {
      console.warn('PiP not supported or failed:', e);
    }
  };

  // Switch Playback Speed
  const handleSpeedSelect = (rate) => {
    setPlaybackRate(rate);
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
    }
    showToast(`Playback speed set to ${rate === 1 ? 'Normal' : `${rate}x`}`);
    closeSettingsMenu();
  };

  // Handle video element load errors with fallback
  const handleVideoError = useCallback(() => {
    console.warn('Video failed to load current quality stream, falling back to direct MP4');
    const videoEl = videoRef.current;
    if (videoEl) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      const fallback = videoData?.mp4Url || DEFAULT_FALLBACK_VIDEO_URL;
      if (videoEl.src !== fallback) {
        setVideoData((prev) => (prev ? { ...prev, streamUrl: fallback } : prev));
        videoEl.src = fallback;
        videoEl.load();
        const p = videoEl.play();
        if (p !== undefined) {
          p.catch(() => {
            videoEl.muted = true;
            setIsMuted(true);
            videoEl.play().catch(() => {});
          });
        }
      }
    }
  }, [videoData?.mp4Url]);

  // Attach HLS stream (.m3u8) or direct video source
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !videoData?.streamUrl) return;

    const streamUrl = videoData.streamUrl;
    const isHls = typeof streamUrl === 'string' && streamUrl.includes('.m3u8');

    // Clean up any existing HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const startPlayback = () => {
      const playPromise = videoEl.play();
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          console.log('Unmuted autoplay prevented by browser policy, attempting muted autoplay:', err);
          videoEl.muted = true;
          setIsMuted(true);
          videoEl.play().catch(() => {});
        });
      }
    };

    if (isHls && Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
      });

      hls.loadSource(streamUrl);
      hls.attachMedia(videoEl);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        startPlayback();
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.warn('HLS fatal stream error, attempting recovery or fallback:', data);
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.log('Fatal network error encountered, restarting load...');
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log('Fatal media error encountered, recovering media...');
              hls.recoverMediaError();
              break;
            default:
              console.warn('Unrecoverable HLS error, switching to direct MP4');
              hls.destroy();
              hlsRef.current = null;
              const fallback = videoData?.mp4Url || DEFAULT_FALLBACK_VIDEO_URL;
              if (videoEl.src !== fallback) {
                setVideoData((prev) => (prev ? { ...prev, streamUrl: fallback } : prev));
                videoEl.src = fallback;
                videoEl.load();
                startPlayback();
              }
              break;
          }
        }
      });

      hlsRef.current = hls;
    } else if (isHls && videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      videoEl.src = streamUrl;
      videoEl.load();
      startPlayback();
    } else {
      videoEl.src = streamUrl;
      videoEl.load();
      startPlayback();
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [videoData?.streamUrl, videoData?.mp4Url]);

  // Switch Video Quality (Supports 4K, 1440p, 1080p, 720p, 480p, 360p, 240p, 144p with FPS)
  const handleQualitySelect = (quality) => {
    setActiveQuality(quality.id);
    setQualityFilter(quality.filter || 'none');
    setQualityRendering(quality.rendering || 'auto');

    isChangingQualityRef.current = true;
    if (qualityChangeTimerRef.current) clearTimeout(qualityChangeTimerRef.current);
    qualityChangeTimerRef.current = setTimeout(() => {
      isChangingQualityRef.current = false;
    }, 2500);

    const videoEl = videoRef.current;
    if (videoEl) {
      const curTime = videoEl.currentTime || 0;
      const wasPlaying = !videoEl.paused;
      const currentCue = adCuePointRef.current || adCuePoint;

      // If user was already at or past the ad cue point, preserve adTriggeredRef so switching quality never triggers ad
      if (currentCue > 0 && curTime >= (currentCue - 1)) {
        adTriggeredRef.current = true;
      }

      const currentSrc = videoData?.streamUrl || videoEl.src || DEFAULT_FALLBACK_VIDEO_URL;
      const [baseUrl] = currentSrc.split('?');

      // ── Derive the canonical folder URL from videoUrlId ─────────────────────
      // This works even when streamUrl is a flat file like /drive/videos/main_rahoon_196.mp4
      // The videoUrlId (e.g. "nOAClbRbfTU") always maps to /drive/videos/<videoUrlId>/
      const videoUrlId = videoData?.videoUrlId;
      let folderUrl = null;

      if (videoUrlId && videoUrlId !== 'HyRyzOCHONj' && videoUrlId !== DEFAULT_VIDEO_URL_ID) {
        // Use the canonical subfolder for this video
        folderUrl = normalizeMediaUrl(`/drive/videos/${videoUrlId}`);
      } else if (videoUrlId === 'HyRyzOCHONj' || videoUrlId === DEFAULT_VIDEO_URL_ID) {
        // APT video lives in /drive/videos/HyRyzOCHONj/
        folderUrl = normalizeMediaUrl(`/drive/videos/${videoUrlId}`);
      } else {
        // Fallback: try to derive folder from current URL
        const lastSlash = baseUrl.lastIndexOf('/');
        const derivedDir = lastSlash !== -1 ? baseUrl.substring(0, lastSlash) : baseUrl;
        const afterVideos = derivedDir.split('/drive/videos/')[1];
        if (afterVideos && afterVideos.length > 0) {
          folderUrl = derivedDir;
        }
      }

      // Only switch resolution if we have a valid folder with per-quality files
      if (folderUrl) {
        // Determine target filename
        let targetFile;
        if (quality.id === 'auto') {
          // Auto = original source file
          if (videoUrlId === 'HyRyzOCHONj') {
            targetFile = 'apt_video.mp4';
          } else {
            // Use originalFilename from videoData, or fall back to video_original_filename
            targetFile = videoData?.originalFilename || 'main_rahoon_196.mp4';
          }
        } else {
          // Specific quality (1080p, 720p, 480p, 360p, 240p, 144p, 1440p, 2160p)
          targetFile = `${quality.id}.mp4`;
        }

        const targetSrc = `${folderUrl}/${targetFile}`;

        if (baseUrl !== targetSrc) {
          // 1. Synchronize React state so JSX <video src={videoData.streamUrl} /> stays aligned
          setVideoData((prev) => (prev ? { ...prev, streamUrl: targetSrc } : prev));

          // 2. Restore playback position and play state once the new file is ready
          const handleMeta = () => {
            try {
              if (curTime > 0 && curTime < (videoEl.duration || Infinity)) {
                videoEl.currentTime = curTime;
                setCurrentTime(curTime);
              }
              if (currentCue > 0 && curTime >= (currentCue - 1)) {
                adTriggeredRef.current = true;
              }
              if (wasPlaying) {
                videoEl.play().catch(() => {});
              }
            } catch (err) {
              console.warn('Quality restore playback notice:', err);
            } finally {
              setTimeout(() => {
                isChangingQualityRef.current = false;
              }, 1200);
            }
          };

          const handleError = () => {
            console.warn(`Resolution file not found (${targetSrc}), reverting to:`, currentSrc);
            setVideoData((prev) => (prev ? { ...prev, streamUrl: currentSrc } : prev));
            videoEl.src = currentSrc;
            videoEl.load();
            if (curTime > 0) videoEl.currentTime = curTime;
            if (wasPlaying) videoEl.play().catch(() => {});
          };

          videoEl.addEventListener('loadedmetadata', handleMeta, { once: true });
          videoEl.addEventListener('error', handleError, { once: true });
          videoEl.src = targetSrc;
          videoEl.load();
          if (wasPlaying) {
            videoEl.play().catch(() => {});
          }
        }
      }

      // If playing via HLS adaptive stream, switch HLS level
      if (hlsRef.current && hlsRef.current.levels && hlsRef.current.levels.length > 0) {
        if (quality.id === 'auto') {
          hlsRef.current.currentLevel = -1;
        } else {
          const targetHeight = parseInt(quality.id, 10);
          const matchIdx = hlsRef.current.levels.findIndex((lvl) => lvl.height === targetHeight);
          if (matchIdx !== -1) {
            hlsRef.current.currentLevel = matchIdx;
          }
        }
      }

      videoEl.playbackRate = playbackRate;
    }

    // Trigger on-screen OSD notification pill — use real measured FPS if available
    const displayFps = realFps ? `${realFps}fps` : quality.fps;
    setQualityOsd({
      show: true,
      label: quality.fullLabel || quality.label,
      fps: displayFps,
      badge: quality.badge,
    });
    if (qualityOsdTimerRef.current) clearTimeout(qualityOsdTimerRef.current);
    qualityOsdTimerRef.current = setTimeout(() => {
      setQualityOsd((prev) => ({ ...prev, show: false }));
    }, 2400);

    showToast(`Quality set to ${quality.fullLabel} (${displayFps})`, <MdHighQuality size={20} color="#3ea6ff" />);
    closeSettingsMenu();
  };

  // YouTube-Style Button Animation Triggers
  const triggerLikeAnim = useCallback((willBeLiked) => {
    if (likeAnimTimerRef.current) clearTimeout(likeAnimTimerRef.current);
    setLikeAnimType(willBeLiked ? 'liked' : 'unliked');
    setLikeAnimKey((prev) => prev + 1);
    likeAnimTimerRef.current = setTimeout(() => {
      setLikeAnimType(null);
    }, 420);
  }, []);

  const triggerDislikeAnim = useCallback(() => {
    setIsDislikeAnimating(true);
    if (dislikeAnimTimerRef.current) clearTimeout(dislikeAnimTimerRef.current);
    dislikeAnimTimerRef.current = setTimeout(() => setIsDislikeAnimating(false), 380);
  }, []);

  const triggerSubAnim = useCallback((willBeSubscribed) => {
    if (subAnimTimerRef.current) clearTimeout(subAnimTimerRef.current);
    setSubAnimType(willBeSubscribed ? 'subscribing' : 'unsubscribing');
    setSubAnimKey((prev) => prev + 1);
    subAnimTimerRef.current = setTimeout(() => {
      setSubAnimType(null);
    }, 380);
  }, []);

  const triggerSaveAnim = useCallback(() => {
    setIsSaveAnimating(true);
    if (saveAnimTimerRef.current) clearTimeout(saveAnimTimerRef.current);
    saveAnimTimerRef.current = setTimeout(() => setIsSaveAnimating(false), 450);
  }, []);

  const handleChannelClick = useCallback((e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const handle = videoData?.handleName || videoData?.channelName || '';
    if (!handle) return;
    const cleanHandle = handle.startsWith('@') ? handle : `@${handle}`;
    const targetPath = `/channel/${encodeURIComponent(cleanHandle)}`;
    if (onNavigate) {
      onNavigate(targetPath);
    } else {
      window.history.pushState({}, '', targetPath);
      window.dispatchEvent(
        new CustomEvent('speedotube_navigate', {
          detail: { path: targetPath },
        })
      );
    }
  }, [videoData?.handleName, videoData?.channelName, onNavigate]);

  // Toggle Autoplay with Local Storage persistence
  const handleToggleAutoplay = () => {
    setAutoplayNext((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('speedotube_autoplay', String(next));
      } catch (e) {}
      showToast(`Autoplay is ${next ? 'on' : 'off'}`);
      if (!next) {
        cancelAutoplayCountdown();
      }
      return next;
    });
  };

  // Real-time Like Video / Album
  const handleLike = async () => {
    if (!isUserAuthenticated) {
      openSignInPrompt('like', videoData?.title || '');
      return;
    }
    const curAcc = activeAccount || getActiveAccount();
    const curToken = curAcc?.token || getActiveUserToken(curAcc);
    const uId = curAcc?.userId || curAcc?.id || null;
    const nextLiked = !isLiked;
    triggerLikeAnim(nextLiked);
    setIsLiked(nextLiked);
    setLikeCount((c) => (nextLiked ? c + 1 : Math.max(0, c - 1)));
    if (nextLiked && isDisliked) {
      setIsDisliked(false);
      if (videoData?.id) {
        localStorage.removeItem(getDislikeStorageKey(videoData.id, uId));
      }
    }

    if (nextLiked) {
      saveToLikedVideos(videoData, uId);
      if (videoData?.id) {
        localStorage.setItem(getLikeStorageKey(videoData.id, uId), 'true');
        localStorage.removeItem(getDislikeStorageKey(videoData.id, uId));
      }
      showToast('Added to Liked videos', <MdThumbUp color="#fff" size={20} />);
    } else {
      removeFromLikedVideos(videoData?.id, uId);
      if (videoData?.id) {
        localStorage.removeItem(getLikeStorageKey(videoData.id, uId));
      }
      showToast('Removed from Liked videos');
    }

    const endpoint = isAlbumMode
      ? `${API_BASE_URL}/api/v1/albums/${videoData?.id}/like`
      : `${API_BASE_URL}/api/v1/videos/${videoData?.id}/like`;

    try {
      if (videoData?.id) {
        let headers = getApiHeaders(true, curToken);
        let res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            ...headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ like_dislike: 'like' }),
        });

        // If 401 Unauthorized, refresh active token from active account and retry
        if (res.status === 401) {
          const freshToken = curAcc?.token || getActiveUserToken(curAcc);
          localStorage.setItem('speedotube_user_token', freshToken);
          headers = {
            ...getApiHeaders(true, freshToken),
            'Content-Type': 'application/json',
          };
          res = await fetch(endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify({ like_dislike: 'like' }),
          });
        }

        if (res.ok) {
          const json = await res.json();
          if (json?.data?.like_count !== undefined) {
            setLikeCount(json.data.like_count);
          }
          if (json?.data?.user_liked !== undefined) {
            const serverLiked = Boolean(json.data.user_liked);
            setIsLiked(serverLiked);
            if (serverLiked) {
              saveToLikedVideos(videoData, uId);
              localStorage.setItem(getLikeStorageKey(videoData.id, uId), 'true');
            } else {
              removeFromLikedVideos(videoData?.id, uId);
              localStorage.removeItem(getLikeStorageKey(videoData.id, uId));
            }
          } else {
            // Refresh from video API with current active user token
            try {
              const refreshId = videoData.videoUrlId || videoData.id;
              const refRes = await fetch(`${API_BASE_URL}/api/v1/videos/${refreshId}`, {
                headers: getApiHeaders(true, curToken),
              });
              if (refRes.ok) {
                const refJson = await refRes.json();
                const refreshedVid = refJson?.data?.video;
                if (refreshedVid) {
                  if (refreshedVid.like_count !== undefined && refreshedVid.like_count !== null) {
                    setLikeCount(parseInt(refreshedVid.like_count, 10) || 0);
                  }
                  if (refreshedVid.user_liked !== undefined) {
                    const serverLiked = Boolean(refreshedVid.user_liked);
                    setIsLiked(serverLiked);
                    if (serverLiked) {
                      saveToLikedVideos(videoData, uId);
                      localStorage.setItem(getLikeStorageKey(videoData.id, uId), 'true');
                    } else {
                      removeFromLikedVideos(videoData?.id, uId);
                      localStorage.removeItem(getLikeStorageKey(videoData.id, uId));
                    }
                  }
                  if (refreshedVid.user_disliked !== undefined) {
                    setIsDisliked(Boolean(refreshedVid.user_disliked));
                  }
                }
              }
            } catch (_) {}
          }
          if (json?.data?.user_disliked !== undefined) {
            setIsDisliked(Boolean(json.data.user_disliked));
          }
        }
      }
    } catch (e) {
      console.warn('Real-time like API failed:', e);
    }
  };

  // Real-time Dislike Video / Album
  const handleDislike = async () => {
    if (!isUserAuthenticated) {
      openSignInPrompt('dislike', videoData?.title || '');
      return;
    }
    const curAcc = activeAccount || getActiveAccount();
    const curToken = curAcc?.token || getActiveUserToken(curAcc);
    const uId = curAcc?.userId || curAcc?.id || null;
    const nextDisliked = !isDisliked;
    triggerDislikeAnim(nextDisliked);
    setIsDisliked(nextDisliked);
    if (nextDisliked) {
      if (videoData?.id) {
        localStorage.setItem(getDislikeStorageKey(videoData.id, uId), 'true');
        localStorage.removeItem(getLikeStorageKey(videoData.id, uId));
      }
    } else {
      if (videoData?.id) {
        localStorage.removeItem(getDislikeStorageKey(videoData.id, uId));
      }
    }
    if (nextDisliked && isLiked) {
      setIsLiked(false);
      removeFromLikedVideos(videoData?.id, uId);
      if (videoData?.id) {
        localStorage.removeItem(getLikeStorageKey(videoData.id, uId));
      }
      setLikeCount((c) => Math.max(0, c - 1));
    }

    const endpoint = isAlbumMode
      ? `${API_BASE_URL}/api/v1/albums/${videoData?.id}/dislike`
      : `${API_BASE_URL}/api/v1/videos/${videoData?.id}/dislike`;

    try {
      if (videoData?.id) {
        let headers = getApiHeaders(true, curToken);
        let res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            ...headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ like_dislike: 'dislike' }),
        });

        // If 401 Unauthorized, refresh active token and retry
        if (res.status === 401) {
          const freshToken = curAcc?.token || getActiveUserToken(curAcc);
          localStorage.setItem('speedotube_user_token', freshToken);
          headers = {
            ...getApiHeaders(true, freshToken),
            'Content-Type': 'application/json',
          };
          res = await fetch(endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify({ like_dislike: 'dislike' }),
          });
        }

        if (res.ok) {
          const json = await res.json();
          if (json?.data?.like_count !== undefined) {
            setLikeCount(json.data.like_count);
          }
          if (json?.data?.user_liked !== undefined) {
            const serverLiked = Boolean(json.data.user_liked);
            setIsLiked(serverLiked);
            if (!serverLiked) {
              removeFromLikedVideos(videoData?.id, uId);
              if (videoData?.id) {
                localStorage.removeItem(getLikeStorageKey(videoData.id, uId));
              }
            }
          } else {
            try {
              const refreshId = videoData.videoUrlId || videoData.id;
              const refRes = await fetch(`${API_BASE_URL}/api/v1/videos/${refreshId}`, {
                headers: getApiHeaders(true, curToken),
              });
              if (refRes.ok) {
                const refJson = await refRes.json();
                const refreshedVid = refJson?.data?.video;
                if (refreshedVid) {
                  if (refreshedVid.like_count !== undefined && refreshedVid.like_count !== null) {
                    setLikeCount(parseInt(refreshedVid.like_count, 10) || 0);
                  }
                  if (refreshedVid.user_liked !== undefined) {
                    const serverLiked = Boolean(refreshedVid.user_liked);
                    setIsLiked(serverLiked);
                    if (!serverLiked) {
                      removeFromLikedVideos(videoData?.id, uId);
                      if (videoData?.id) {
                        localStorage.removeItem(getLikeStorageKey(videoData.id, uId));
                      }
                    }
                  }
                  if (refreshedVid.user_disliked !== undefined) {
                    setIsDisliked(Boolean(refreshedVid.user_disliked));
                  }
                }
              }
            } catch (_) {}
          }
          if (json?.data?.user_disliked !== undefined) {
            setIsDisliked(Boolean(json.data.user_disliked));
          }
        }
      }
    } catch (e) {
      console.warn('Real-time dislike API failed:', e);
    }
  };

  // Real-time Subscribe / Unsubscribe Channel Connected to Backend
  const handleSubscribeToggle = async () => {
    if (!isUserAuthenticated) {
      openSignInPrompt('subscribe', videoData?.channelName || '');
      return;
    }
    if (isOwner) {
      return;
    }
    const channelId = videoData?.channelId;
    if (!channelId) return;
    const curAcc = activeAccount || getActiveAccount();
    const curToken = curAcc?.token || getActiveUserToken(curAcc);
    const nextSubscribed = !isSubscribed;
    triggerSubAnim(nextSubscribed);
    setIsSubscribed(nextSubscribed);
    setSubscriberCount((prev) => (nextSubscribed ? prev + 1 : Math.max(0, prev - 1)));

    const postBody = videoData?.id ? { videoId: Number(videoData.id) } : {};

    if (nextSubscribed) {
      showToast(`Subscribed to ${videoData?.channelName || 'channel'}`, <MdCheckCircle color="#fff" size={20} />);
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/subscribers/subscribe/channels/${channelId}`, {
          method: 'POST',
          headers: {
            ...getApiHeaders(true, curToken),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(postBody),
        });
        if (res.ok) {
          if (videoData?.handleName) {
            fetchLiveChannelSubscribers(videoData.handleName);
          }
          window.dispatchEvent(new CustomEvent('speedotube:subscription-changed', {
            detail: { channelId, isSubscribed: true }
          }));
          window.dispatchEvent(new CustomEvent('speedotube_subscription_toggled', {
            detail: { channelId, isSubscribed: true }
          }));
        } else {
          setIsSubscribed(false);
          setSubscriberCount((prev) => Math.max(0, prev - 1));
          showToast('Failed to subscribe. Please try again.');
        }
      } catch (e) {
        console.warn('Real-time subscribe failed:', e);
        setIsSubscribed(false);
        setSubscriberCount((prev) => Math.max(0, prev - 1));
      }
    } else {
      showToast('Subscription removed');
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/subscribers/unsubscribe/channels/${channelId}`, {
          method: 'POST',
          headers: {
            ...getApiHeaders(true, curToken),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(postBody),
        });
        if (res.ok) {
          if (videoData?.handleName) {
            fetchLiveChannelSubscribers(videoData.handleName);
          }
          window.dispatchEvent(new CustomEvent('speedotube:subscription-changed', {
            detail: { channelId, isSubscribed: false }
          }));
          window.dispatchEvent(new CustomEvent('speedotube_subscription_toggled', {
            detail: { channelId, isSubscribed: false }
          }));
        } else {
          setIsSubscribed(true);
          setSubscriberCount((prev) => prev + 1);
          showToast('Failed to unsubscribe. Please try again.');
        }
      } catch (e) {
        console.warn('Real-time unsubscribe failed:', e);
        setIsSubscribed(true);
        setSubscriberCount((prev) => prev + 1);
      }
    }
  };

  // Cross-component subscription synchronization listener
  useEffect(() => {
    const handleSubChange = (e) => {
      const channelId = videoData?.channelId;
      if (!channelId) return;
      if (e.detail?.channelId === channelId || String(e.detail?.channelId) === String(channelId)) {
        if (typeof e.detail.isSubscribed === 'boolean') {
          setIsSubscribed(isUserAuthenticated ? e.detail.isSubscribed : false);
        }
        if (videoData?.handleName) {
          fetchLiveChannelSubscribers(videoData.handleName);
        }
      }
    };
    window.addEventListener('speedotube:subscription-changed', handleSubChange);
    window.addEventListener('speedotube_subscription_toggled', handleSubChange);
    return () => {
      window.removeEventListener('speedotube:subscription-changed', handleSubChange);
      window.removeEventListener('speedotube_subscription_toggled', handleSubChange);
    };
  }, [videoData?.channelId, videoData?.handleName, fetchLiveChannelSubscribers, isUserAuthenticated]);

  // Cross-user switch listener to refresh owner mode, like state & comments
  useEffect(() => {
    const handleUserSwitched = (e) => {
      const switchedAcc = e.detail || getActiveAccount();
      setActiveAccountState(switchedAcc);
      const idToLoad = videoUrlId || new URLSearchParams(window.location.search).get('v') || DEFAULT_VIDEO_URL_ID;
      fetchVideoDetails(idToLoad, switchedAcc);
      if (videoData?.id) {
        fetchComments(videoData.id, switchedAcc);
      }
    };
    window.addEventListener('speedotube_user_switched', handleUserSwitched);
    window.addEventListener('speedotube_channel_updated', handleUserSwitched);
    return () => {
      window.removeEventListener('speedotube_user_switched', handleUserSwitched);
      window.removeEventListener('speedotube_channel_updated', handleUserSwitched);
    };
  }, [videoUrlId, fetchVideoDetails, videoData?.id, fetchComments]);

  // Current active user avatar for comment input
  const currentUserAvatar = normalizeMediaUrl(
    (activeAccount || userProfile)?.avatar_url ||
    (activeAccount || userProfile)?.avatarUrl ||
    (activeAccount || userProfile)?.profile_image ||
    getActiveAccount()?.avatar_url ||
    getActiveAccount()?.profile_image ||
    ''
  );

  // Dynamic comment avatar resolution (instant sync with active user channel customization)
  const getCommentAvatar = useCallback((comment) => {
    const currentAcc = activeAccount || userProfile || getActiveAccount();
    const currentH = (currentAcc?.handle || '').replace(/^@/, '').toLowerCase();
    const commentH = (comment.handle || comment.author || '').replace(/^@/, '').toLowerCase();

    // If comment belongs to the currently active logged-in user, prioritize active user's live avatar
    if (currentH && commentH && currentH === commentH) {
      const activeAvatar = currentAcc?.avatar_url || currentAcc?.avatarUrl || currentAcc?.profile_image;
      if (activeAvatar) return normalizeMediaUrl(activeAvatar);
    }

    if (comment.avatar && typeof comment.avatar === 'string' && comment.avatar.trim() !== '') {
      return normalizeMediaUrl(comment.avatar);
    }

    try {
      const customRaw = localStorage.getItem(`speedotube_customization_@${commentH}`);
      if (customRaw) {
        const custom = JSON.parse(customRaw);
        if (custom?.avatar_url || custom?.avatar_filename) return normalizeMediaUrl(custom.avatar_url || custom.avatar_filename);
      }
    } catch (e) {}

    return '';
  }, [activeAccount, userProfile]);

  // Handle Share Modal Opening
  const handleShare = () => {
    setIsShareModalOpen(true);
  };

  // Real-time Save to Watch Later Playlist Connected to Backend & Local Storage
  const handleToggleSaveToWatchLater = async () => {
    if (!isUserAuthenticated) {
      openSignInPrompt('save', videoData?.title || '');
      return;
    }
    const nextSaved = !isSavedToWatchLater;
    setIsSavedToWatchLater(nextSaved);
    const videoId = videoData?.id || 157;

    if (nextSaved) {
      saveToWatchLater(videoData);
      showToast(
        'Saved to Watch later',
        <MdBookmarkAdded color="#fff" size={20} />
      );
      try {
        await fetch(`${API_BASE_URL}/api/v1/watch-later/videos/${videoId}`, {
          method: 'POST',
          headers: getApiHeaders(true),
        });
      } catch (e) {
        console.warn('Real-time watch later add failed:', e);
      }
    } else {
      removeFromWatchLater(videoId);
      showToast('Removed from Watch later');
      try {
        await fetch(`${API_BASE_URL}/api/v1/watch-later/videos/${videoId}`, {
          method: 'DELETE',
          headers: getApiHeaders(true),
        });
      } catch (e) {
        console.warn('Real-time watch later remove failed:', e);
      }
    }
  };

  // Real-time Add Comment with double-submission protection and server sync
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!isUserAuthenticated) {
      openSignInPrompt('comment', videoData?.title || '');
      return;
    }
    if (isSubmittingCommentRef.current) return;
    const trimmed = newCommentText.trim();
    if (!trimmed) return;

    isSubmittingCommentRef.current = true;
    setIsSubmittingComment(true);

    const currentAcc = activeAccount || userProfile || getActiveAccount();
    const curToken = currentAcc?.token || getActiveUserToken(currentAcc);
    const uId = currentAcc?.userId || currentAcc?.id || null;
    const authorName = currentAcc?.name || currentAcc?.fullName || 'Speedo User';
    const authorHandle = (currentAcc?.handle || `@${authorName.toLowerCase().replace(/[^a-z0-9]/gi, '')}`).trim();

    const tempComment = {
      id: `c_${Date.now()}`,
      author: authorName,
      handle: authorHandle,
      avatar: normalizeMediaUrl(currentAcc?.avatar_url || currentAcc?.avatarUrl || currentAcc?.profile_image || ''),
      timeAgo: 'Just now',
      createdDate: new Date().toISOString(),
      text: trimmed,
      likes: 0,
      dislikes: 0,
      userLiked: false,
    };

    setComments((prev) => [
      tempComment,
      ...prev.filter(
        (c) =>
          c.text?.trim() !== trimmed ||
          ((c.handle && c.handle.toLowerCase() !== authorHandle.toLowerCase()) &&
           (c.author && c.author.toLowerCase() !== authorName.toLowerCase()))
      ),
    ]);
    setNewCommentText('');
    setIsCommentFocused(false);
    showToast('Comment added');

    if (videoData?.id) {
      const key = `speedotube_user_comments_${uId}_${videoData.id}`;
      try {
        const existing = JSON.parse(localStorage.getItem(key) || '[]');
        const filtered = existing.filter((c) => c.text?.trim() !== trimmed);
        localStorage.setItem(key, JSON.stringify([tempComment, ...filtered]));
        // Purge legacy unscoped key
        localStorage.removeItem(`speedotube_user_comments_${videoData.id}`);
      } catch (err) {}

      try {
        let headers = {
          ...getApiHeaders(true, curToken),
          'Content-Type': 'application/json',
        };
        let res = await fetch(`${API_BASE_URL}/api/v1/comments/videos/${videoData.id}`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            text: trimmed,
            author_name: authorName,
            handle_name: authorHandle,
          }),
        });

        if (res.status === 401) {
          const freshToken = curToken || getActiveAccount()?.token;
          if (freshToken) {
            localStorage.setItem('speedotube_user_token', freshToken);
            headers = {
              ...getApiHeaders(true, freshToken),
              'Content-Type': 'application/json',
            };
            res = await fetch(`${API_BASE_URL}/api/v1/comments/videos/${videoData.id}`, {
              method: 'POST',
              headers,
              body: JSON.stringify({
                text: trimmed,
                author_name: authorName,
                handle_name: authorHandle,
              }),
            });
          }
        }

        if (res.ok) {
          // Immediately purge the temp comment from localStorage so it never collides with server record
          try {
            const existing = JSON.parse(localStorage.getItem(key) || '[]');
            const cleaned = existing.filter((c) => c.id !== tempComment.id && c.text?.trim() !== trimmed);
            if (cleaned.length > 0) {
              localStorage.setItem(key, JSON.stringify(cleaned));
            } else {
              localStorage.removeItem(key);
            }
          } catch (e) {}

          await fetchComments(videoData.id, currentAcc);
        }
      } catch (err) {
        console.warn('Real-time comment creation backend error:', err);
      } finally {
        isSubmittingCommentRef.current = false;
        setIsSubmittingComment(false);
      }
    } else {
      isSubmittingCommentRef.current = false;
      setIsSubmittingComment(false);
    }
  };

  // Real-time Like Comment or Reply connected to Backend
  const handleCommentLike = async (commentId, isReply = false, parentId = null) => {
    if (!isUserAuthenticated) {
      openSignInPrompt('like', 'this comment');
      return;
    }

    if (isReply && parentId) {
      setRepliesMap((prev) => {
        const list = prev[parentId] || [];
        const updated = list.map((r) => {
          if (String(r.id) !== String(commentId)) return r;
          const nextLiked = !r.userLiked;
          return {
            ...r,
            userLiked: nextLiked,
            userDisliked: nextLiked ? false : r.userDisliked,
            likes: nextLiked ? r.likes + 1 : Math.max(0, r.likes - 1),
            dislikes: nextLiked && r.userDisliked ? Math.max(0, r.dislikes - 1) : r.dislikes,
          };
        });
        return { ...prev, [parentId]: updated };
      });
    } else {
      setComments((prev) =>
        prev.map((c) => {
          if (String(c.id) !== String(commentId)) return c;
          const nextLiked = !c.userLiked;
          return {
            ...c,
            userLiked: nextLiked,
            userDisliked: nextLiked ? false : c.userDisliked,
            likes: nextLiked ? c.likes + 1 : Math.max(0, c.likes - 1),
            dislikes: nextLiked && c.userDisliked ? Math.max(0, c.dislikes - 1) : c.dislikes,
          };
        })
      );
    }

    try {
      const curAcc = activeAccount || getActiveAccount();
      const curToken = curAcc?.token || getActiveUserToken(curAcc);
      let headers = {
        ...getApiHeaders(true, curToken),
        'Content-Type': 'application/json',
      };
      let res = await fetch(`${API_BASE_URL}/api/v1/comments/${commentId}/likes`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ like_dislike: 'like' }),
      });

      if (res.status === 401) {
        const freshToken = curToken || getActiveAccount()?.token;
        if (freshToken) {
          localStorage.setItem('speedotube_user_token', freshToken);
          headers = {
            ...getApiHeaders(true, freshToken),
            authorization: `Bearer ${freshToken}`,
            'Content-Type': 'application/json',
          };
          res = await fetch(`${API_BASE_URL}/api/v1/comments/${commentId}/likes`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ like_dislike: 'like' }),
          });
        }
      }

      if (res.ok) {
        const json = await res.json();
        if (json?.data) {
          const { user_liked, user_disliked, like_count, dislike_count } = json.data;
          if (isReply && parentId) {
            setRepliesMap((prev) => {
              const list = prev[parentId] || [];
              return {
                ...prev,
                [parentId]: list.map((r) =>
                  String(r.id) === String(commentId)
                    ? {
                        ...r,
                        userLiked: Boolean(user_liked),
                        userDisliked: Boolean(user_disliked),
                        likes: like_count !== undefined ? Number(like_count) : r.likes,
                        dislikes: dislike_count !== undefined ? Number(dislike_count) : r.dislikes,
                      }
                    : r
                ),
              };
            });
          } else {
            setComments((prev) =>
              prev.map((c) =>
                String(c.id) === String(commentId)
                  ? {
                      ...c,
                      userLiked: Boolean(user_liked),
                      userDisliked: Boolean(user_disliked),
                      likes: like_count !== undefined ? Number(like_count) : c.likes,
                      dislikes: dislike_count !== undefined ? Number(dislike_count) : c.dislikes,
                    }
                  : c
              )
            );
          }
        }
      }
    } catch (e) {
      console.warn('Real-time comment like API failed:', e);
    }
  };

  // Real-time Dislike Comment or Reply connected to Backend
  const handleCommentDislike = async (commentId, isReply = false, parentId = null) => {
    if (!isUserAuthenticated) {
      openSignInPrompt('dislike', 'this comment');
      return;
    }

    if (isReply && parentId) {
      setRepliesMap((prev) => {
        const list = prev[parentId] || [];
        const updated = list.map((r) => {
          if (String(r.id) !== String(commentId)) return r;
          const nextDisliked = !r.userDisliked;
          return {
            ...r,
            userDisliked: nextDisliked,
            userLiked: nextDisliked ? false : r.userLiked,
            likes: nextDisliked && r.userLiked ? Math.max(0, r.likes - 1) : r.likes,
            dislikes: nextDisliked ? r.dislikes + 1 : Math.max(0, r.dislikes - 1),
          };
        });
        return { ...prev, [parentId]: updated };
      });
    } else {
      setComments((prev) =>
        prev.map((c) => {
          if (String(c.id) !== String(commentId)) return c;
          const nextDisliked = !c.userDisliked;
          return {
            ...c,
            userDisliked: nextDisliked,
            userLiked: nextDisliked ? false : c.userLiked,
            likes: nextDisliked && c.userLiked ? Math.max(0, c.likes - 1) : c.likes,
            dislikes: nextDisliked ? c.dislikes + 1 : Math.max(0, c.dislikes - 1),
          };
        })
      );
    }

    try {
      const curAcc = activeAccount || getActiveAccount();
      const curToken = curAcc?.token || getActiveUserToken(curAcc);
      let headers = {
        ...getApiHeaders(true, curToken),
        'Content-Type': 'application/json',
      };
      let res = await fetch(`${API_BASE_URL}/api/v1/comments/${commentId}/likes`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ like_dislike: 'dislike' }),
      });

      if (res.status === 401) {
        const freshToken = curToken || getActiveAccount()?.token;
        if (freshToken) {
          localStorage.setItem('speedotube_user_token', freshToken);
          headers = {
            ...getApiHeaders(true, freshToken),
            authorization: `Bearer ${freshToken}`,
            'Content-Type': 'application/json',
          };
          res = await fetch(`${API_BASE_URL}/api/v1/comments/${commentId}/likes`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ like_dislike: 'dislike' }),
          });
        }
      }

      if (res.ok) {
        const json = await res.json();
        if (json?.data) {
          const { user_liked, user_disliked, like_count, dislike_count } = json.data;
          if (isReply && parentId) {
            setRepliesMap((prev) => {
              const list = prev[parentId] || [];
              return {
                ...prev,
                [parentId]: list.map((r) =>
                  String(r.id) === String(commentId)
                    ? {
                        ...r,
                        userLiked: Boolean(user_liked),
                        userDisliked: Boolean(user_disliked),
                        likes: like_count !== undefined ? Number(like_count) : r.likes,
                        dislikes: dislike_count !== undefined ? Number(dislike_count) : r.dislikes,
                      }
                    : r
                ),
              };
            });
          } else {
            setComments((prev) =>
              prev.map((c) =>
                String(c.id) === String(commentId)
                  ? {
                      ...c,
                      userLiked: Boolean(user_liked),
                      userDisliked: Boolean(user_disliked),
                      likes: like_count !== undefined ? Number(like_count) : c.likes,
                      dislikes: dislike_count !== undefined ? Number(dislike_count) : c.dislikes,
                    }
                  : c
              )
            );
          }
        }
      }
    } catch (e) {
      console.warn('Real-time comment dislike API failed:', e);
    }
  };

  // Toggle View Replies & fetch from backend if needed
  const handleToggleViewReplies = async (commentId) => {
    const isCurrentlyExpanded = Boolean(expandedReplies[commentId]);
    if (isCurrentlyExpanded) {
      setExpandedReplies((prev) => ({ ...prev, [commentId]: false }));
      return;
    }

    setExpandedReplies((prev) => ({ ...prev, [commentId]: true }));

    // If replies not already loaded, fetch from backend
    if (!repliesMap[commentId] || repliesMap[commentId].length === 0) {
      setLoadingReplies((prev) => ({ ...prev, [commentId]: true }));
      try {
        const curAcc = activeAccount || getActiveAccount();
        const curToken = curAcc?.token || getActiveUserToken(curAcc);
        let headers = getApiHeaders(true, curToken);
        let res = await fetch(`${API_BASE_URL}/api/v1/comments/${commentId}/replies`, {
          headers,
        });

        if (res.status === 401) {
          const freshToken = curToken || getActiveAccount()?.token;
          if (freshToken) {
            localStorage.setItem('speedotube_user_token', freshToken);
            headers = {
              ...headers,
              authorization: `Bearer ${freshToken}`,
            };
            res = await fetch(`${API_BASE_URL}/api/v1/comments/${commentId}/replies`, {
              headers,
            });
          }
        }

        if (res.ok) {
          const json = await res.json();
          const list = json?.data?.replies || [];
          const currentActive = activeAccount || userProfile || getActiveAccount();
          const mappedReplies = list.map((r) => {
            let author = r.channel_name || r.name || r.author_name || r.username?.replace('@', '') || 'SpeedoUser';
            if (author === 'Anonymous' || !author) author = 'SpeedoUser';
            let handle = r.handle_name || r.username || `@${author.toLowerCase().replace(/[^a-z0-9]/gi, '')}`;
            let avatar = normalizeMediaUrl(r.avatar_url) || '';

            const isCurrentUser = (currentActive?.userId && r.user_id && String(r.user_id) === String(currentActive.userId)) ||
                                  (currentActive?.id && r.user_id && String(r.user_id) === String(currentActive.id)) ||
                                  (currentActive?.phone && r.phone && String(r.phone) === String(currentActive.phone));
            if (isCurrentUser) {
              author = currentActive.name || currentActive.fullName || author;
              handle = currentActive.handle || handle;
              avatar = currentActive.avatar_url || currentActive.avatarUrl || currentActive.profile_image || avatar;
            }

            return {
              id: String(r.id),
              parentId: commentId,
              author,
              handle,
              avatar,
              timeAgo: timeAgo(r.created_date),
              createdDate: r.created_date || new Date().toISOString(),
              text: r.text,
              likes: Number(r.like_count) || 0,
              dislikes: Number(r.dislike_count) || 0,
              userLiked: Boolean(r.user_liked),
              userDisliked: Boolean(r.user_disliked),
            };
          });

          // Load any locally cached unpersisted replies
          let localReplies = [];
          try {
            const raw = localStorage.getItem(`speedotube_user_replies_${commentId}`);
            if (raw) localReplies = JSON.parse(raw) || [];
          } catch (e) {}

          const unpersisted = localReplies.filter((lr) =>
            !mappedReplies.some((mr) => String(mr.id) === String(lr.id) || (mr.text?.trim() === lr.text?.trim() && mr.author === lr.author))
          );
          try {
            if (unpersisted.length === 0) {
              localStorage.removeItem(`speedotube_user_replies_${commentId}`);
            } else {
              localStorage.setItem(`speedotube_user_replies_${commentId}`, JSON.stringify(unpersisted));
            }
          } catch (e) {}

          const finalReplies = [...mappedReplies, ...unpersisted];
          setRepliesMap((prev) => ({ ...prev, [commentId]: finalReplies }));
          setComments((prev) =>
            prev.map((c) =>
              String(c.id) === String(commentId)
                ? { ...c, hasReplies: finalReplies.length > 0, replyCount: finalReplies.length }
                : c
            )
          );
        }
      } catch (e) {
        console.warn('Failed to load replies:', e);
      } finally {
        setLoadingReplies((prev) => ({ ...prev, [commentId]: false }));
      }
    }
  };

  // Open Reply Input for a Comment (or targeted reply)
  const handleOpenReplyBox = (commentId, targetHandle = null) => {
    if (!isUserAuthenticated) {
      openSignInPrompt('comment', 'to reply');
      return;
    }
    setActiveReplyId(commentId);
    if (targetHandle) {
      const cleanHandle = targetHandle.startsWith('@') ? targetHandle : `@${targetHandle}`;
      setReplyInputText((prev) => ({
        ...prev,
        [commentId]: prev[commentId] ? prev[commentId] : `${cleanHandle} `,
      }));
    }
  };

  // Submit Reply to Comment
  const handleSubmitReply = async (commentId) => {
    if (!isUserAuthenticated) {
      openSignInPrompt('comment', 'to reply');
      return;
    }
    const text = (replyInputText[commentId] || '').trim();
    if (!text) return;

    setIsSubmittingReply((prev) => ({ ...prev, [commentId]: true }));

    const currentAcc = activeAccount || userProfile || getActiveAccount();
    const curToken = currentAcc?.token || getActiveUserToken(currentAcc);
    const authorName = currentAcc?.name || currentAcc?.fullName || 'Speedo User';
    const authorHandle = (currentAcc?.handle || `@${authorName.toLowerCase().replace(/[^a-z0-9]/gi, '')}`).trim();
    const avatarUrl = normalizeMediaUrl(currentAcc?.avatar_url || currentAcc?.avatarUrl || currentAcc?.profile_image || '');

    const tempReply = {
      id: `rep_${Date.now()}`,
      parentId: commentId,
      author: authorName,
      handle: authorHandle,
      avatar: avatarUrl,
      timeAgo: 'Just now',
      createdDate: new Date().toISOString(),
      text,
      likes: 0,
      dislikes: 0,
      userLiked: false,
      userDisliked: false,
    };

    // Optimistically update replies
    setRepliesMap((prev) => ({
      ...prev,
      [commentId]: [...(prev[commentId] || []), tempReply],
    }));
    setExpandedReplies((prev) => ({ ...prev, [commentId]: true }));
    setComments((prev) =>
      prev.map((c) =>
        String(c.id) === String(commentId)
          ? { ...c, hasReplies: true, replyCount: (c.replyCount || 0) + 1 }
          : c
      )
    );
    setReplyInputText((prev) => ({ ...prev, [commentId]: '' }));
    setActiveReplyId(null);
    showToast('Reply added');

    // Persist to local backup storage as safety net
    try {
      const repKey = `speedotube_user_replies_${commentId}`;
      const savedReplies = JSON.parse(localStorage.getItem(repKey) || '[]');
      localStorage.setItem(repKey, JSON.stringify([...savedReplies, tempReply]));
    } catch (e) {}

    try {
      let headers = {
        ...getApiHeaders(true, curToken),
        'Content-Type': 'application/json',
      };
      let res = await fetch(`${API_BASE_URL}/api/v1/comments/${commentId}/reply`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          text,
          video_id: videoData?.id || videoUrlId,
          author_name: authorName,
          handle_name: authorHandle,
        }),
      });

      if (res.status === 401) {
        const freshToken = curToken || getActiveAccount()?.token;
        if (freshToken) {
          localStorage.setItem('speedotube_user_token', freshToken);
          headers = {
            ...getApiHeaders(true, freshToken),
            authorization: `Bearer ${freshToken}`,
            'Content-Type': 'application/json',
          };
          res = await fetch(`${API_BASE_URL}/api/v1/comments/${commentId}/reply`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              text,
              video_id: videoData?.id || videoUrlId,
              author_name: authorName,
              handle_name: authorHandle,
            }),
          });
        }
      }

      if (res.ok) {
        const json = await res.json();
        const serverReply = json?.data?.reply;
        if (serverReply?.id) {
          setRepliesMap((prev) => {
            const list = prev[commentId] || [];
            return {
              ...prev,
              [commentId]: list.map((r) =>
                r.id === tempReply.id ? { ...r, id: String(serverReply.id) } : r
              ),
            };
          });
          try {
            const repKey = `speedotube_user_replies_${commentId}`;
            const saved = JSON.parse(localStorage.getItem(repKey) || '[]');
            const cleaned = saved.filter((r) => r.id !== tempReply.id && r.text?.trim() !== text);
            if (cleaned.length > 0) {
              localStorage.setItem(repKey, JSON.stringify(cleaned));
            } else {
              localStorage.removeItem(repKey);
            }
          } catch (e) {}
        }
      }
    } catch (e) {
      console.warn('Real-time comment reply API failed:', e);
    } finally {
      setIsSubmittingReply((prev) => ({ ...prev, [commentId]: false }));
    }
  };

  // Handle Ecosystem Video Click
  const handleSelectRecommendedVideo = useCallback((rec) => {
    cancelAutoplayCountdown();
    if (onNavigate) {
      onNavigate(`/watch?v=${rec.videoUrlId}`);
    } else {
      window.history.pushState({}, '', `/watch?v=${rec.videoUrlId}`);
      window.dispatchEvent(new Event('popstate'));
    }
    fetchVideoDetails(rec.videoUrlId);
  }, [cancelAutoplayCountdown, fetchVideoDetails, onNavigate]);

  // Immediately play next video from autoplay cooldown
  const handlePlayNextNow = useCallback(() => {
    if (autoplayCountdown?.nextVideo) {
      const nextVid = autoplayCountdown.nextVideo;
      cancelAutoplayCountdown();
      autoPlayOnLoadRef.current = true;
      handleSelectRecommendedVideo(nextVid);
    }
  }, [autoplayCountdown, cancelAutoplayCountdown, handleSelectRecommendedVideo]);

  // Autoplay next video on end with YouTube 5-second cooldown
  const handleVideoEnded = () => {
    setIsPlaying(false);
    lastPlaybackTimestampRef.current = null;
    adPostResumeTimeRef.current = null;
    if (!viewRegisteredRef.current) {
      checkAndRegisterLegitimateView();
    }
    if (autoplayNext && ecosystemVideos.length > 0) {
      const currentIndex = ecosystemVideos.findIndex(
        (v) => v.videoUrlId === videoData?.videoUrlId || String(v.id) === String(videoData?.id)
      );
      let nextVid = null;
      if (currentIndex !== -1 && currentIndex + 1 < ecosystemVideos.length) {
        nextVid = ecosystemVideos[currentIndex + 1];
      } else if (ecosystemVideos.length > 1) {
        nextVid = ecosystemVideos.find((v) => v.videoUrlId !== videoData?.videoUrlId) || ecosystemVideos[0];
      } else {
        nextVid = ecosystemVideos[0];
      }
      if (nextVid) {
        cancelAutoplayCountdown();
        setAutoplayCountdown({
          secondsLeft: 5,
          nextVideo: nextVid,
        });
      }
    }
  };

  // YouTube 5-Second Autoplay Cooldown Interval Effect
  useEffect(() => {
    if (!autoplayCountdown) {
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
        autoplayTimerRef.current = null;
      }
      return;
    }

    autoplayTimerRef.current = setInterval(() => {
      setAutoplayCountdown((prev) => {
        if (!prev) return null;
        if (prev.secondsLeft <= 1) {
          if (autoplayTimerRef.current) {
            clearInterval(autoplayTimerRef.current);
            autoplayTimerRef.current = null;
          }
          autoPlayOnLoadRef.current = true;
          handleSelectRecommendedVideo(prev.nextVideo);
          return null;
        }
        return {
          ...prev,
          secondsLeft: prev.secondsLeft - 1,
        };
      });
    }, 1000);

    return () => {
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
        autoplayTimerRef.current = null;
      }
    };
  }, [Boolean(autoplayCountdown), autoplayCountdown?.nextVideo?.videoUrlId, handleSelectRecommendedVideo]);


  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferPercent = duration > 0 ? (bufferedEnd / duration) * 100 : 0;

  return (
    <div className={`yt-watch-container ${isTheaterMode ? 'theater-mode' : ''}`}>
      {/* Player Stage: Spans full width in Theater mode, Left column in Default mode */}
      <div className="yt-watch-player-stage">
        {isAlbumMode ? (
          <div className="yt-album-still-player-card">
            {/* 1. The Banner in Vignette Mode From Below Exactly */}
            <div className="yt-album-vignette-banner">
              <img
                src={normalizeMediaUrl(albumData?.playboxBannerUrl || albumData?.coverUrl) || (process.env.PUBLIC_URL || '') + '/img/banner.png'}
                alt={albumData?.title || 'Album Banner'}
                className="yt-album-vignette-img"
                onError={(e) => {
                  e.currentTarget.src = (process.env.PUBLIC_URL || '') + '/img/banner.png';
                }}
              />

              {/* Bottom Vignette Overlay */}
              <div className="yt-album-vignette-overlay" />

              {/* 2. The Play and Pause Buttons Between center the Banner */}
              <div className="yt-album-banner-center">
                <div className="yt-album-banner-meta-badge">
                  <MdOutlineAlbum size={14} />
                  <span>OFFICIAL MUSIC ALBUM</span>
                </div>

                <h1 className="yt-album-banner-hero-title">
                  {albumData?.title || 'Music Album'}
                </h1>

                <div className="yt-album-banner-artist-line">
                  <span className="yt-album-artist-name">{albumData?.artist || 'Artist'}</span>
                  <span className="yt-album-dot">•</span>
                  <span>{albumData?.releaseYear || '2025'}</span>
                  <span className="yt-album-dot">•</span>
                  <span>{(albumData?.tracks || []).length} Song{(albumData?.tracks || []).length !== 1 ? 's' : ''}</span>
                </div>

                {/* Centered Large Play / Pause Button with Glow & Pulse */}
                <button
                  className={`yt-album-center-play-btn ${isAudioPlaying ? 'playing' : ''}`}
                  onClick={toggleAlbumPlay}
                  title={isAudioPlaying ? 'Pause (Space)' : 'Play Album (Space)'}
                  aria-label={isAudioPlaying ? 'Pause' : 'Play'}
                >
                  {isAudioPlaying ? (
                    <MdPause size={42} color="#ffffff" />
                  ) : (
                    <MdPlayArrow size={46} color="#ffffff" style={{ marginLeft: '4px' }} />
                  )}
                  {isAudioPlaying && <div className="yt-album-btn-pulse-ring" />}
                </button>
              </div>
            </div>

            {/* 3. The Music Tracks Below It */}
            <div className="yt-album-tracks-below">
              {/* Active Track Audio Scrubber & Controls Bar */}
              <div className="yt-album-audio-bar">
                <div className="yt-album-audio-info">
                  <span className={`yt-album-now-playing-tag ${isAudioPlaying ? 'live' : ''}`}>
                    {isAudioPlaying ? 'NOW PLAYING' : 'AUDIO TRACK'}
                  </span>
                  <span className="yt-album-active-track-name">
                    {albumData?.tracks?.[currentTrackIndex]?.title || albumData?.title || 'Track'}
                  </span>
                  <span className="yt-album-active-track-artist">
                    {albumData?.tracks?.[currentTrackIndex]?.artist || albumData?.artist || 'Artist'}
                  </span>
                </div>

                {/* Scrubber Slider */}
                <div className="yt-album-scrub-wrap">
                  <span className="yt-album-time-text">{formatTime(audioCurrentTime)}</span>
                  <div
                    className="yt-album-scrubber-track"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const clickX = e.clientX - rect.left;
                      const pct = Math.max(0, Math.min(1, clickX / rect.width));
                      if (onSeekMusic && audioDuration > 0) {
                        const newTime = pct * audioDuration;
                        onSeekMusic(newTime);
                      }
                    }}
                  >
                    <div
                      className="yt-album-scrubber-progress"
                      style={{
                        width: `${audioDuration > 0 ? (audioCurrentTime / audioDuration) * 100 : 0}%`,
                      }}
                    >
                      <div className="yt-album-scrubber-head" />
                    </div>
                  </div>
                  <span className="yt-album-time-text">
                    {formatTime(audioDuration || (albumData?.tracks?.[currentTrackIndex]?.durationSec || 243))}
                  </span>
                </div>
              </div>

              {/* Interactive Tracklist Table */}
              <div className="yt-album-tracklist-table">
                <div className="yt-tracklist-header-row">
                  <div className="yt-th-idx">#</div>
                  <div className="yt-th-title">TITLE</div>
                  <div className="yt-th-artist">ARTIST</div>
                  <div className="yt-th-duration">DURATION</div>
                </div>

                {(albumData?.tracks || []).map((track, idx) => {
                  const isThisTrackActive = currentTrackIndex === idx;
                  const isThisTrackPlaying = isThisTrackActive && isAudioPlaying;

                  return (
                    <div
                      key={track.id || idx}
                      className={`yt-track-row ${isThisTrackActive ? 'active' : ''} ${isThisTrackPlaying ? 'playing' : ''}`}
                      onClick={() => {
                        if (isThisTrackActive) {
                          toggleAlbumPlay();
                        } else {
                          handlePlayAlbumTrack(idx);
                        }
                      }}
                    >
                      <div className="yt-td-idx">
                        {isThisTrackPlaying ? (
                          <div className="yt-equalizer-bars">
                            <span className="bar bar1" />
                            <span className="bar bar2" />
                            <span className="bar bar3" />
                          </div>
                        ) : (
                          <span className="yt-track-num">{track.trackNumber || idx + 1}</span>
                        )}
                      </div>

                      <div className="yt-td-title">
                        <span className={`yt-track-title-text ${isThisTrackActive ? 'accent' : ''}`}>
                          {track.title}
                        </span>
                        <span className="yt-track-audio-badge">HD AUDIO</span>
                      </div>

                      <div className="yt-td-artist">{track.artist || albumData?.artist || 'Pratham'}</div>

                      <div className="yt-td-duration">{track.duration || '4:03'}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
        /* Custom YouTube Video Player Container */
        <div
          ref={playerContainerRef}
          className={`yt-player-box ${isFullscreen ? 'is-fullscreen' : ''} ${isTheaterMode ? 'is-theater' : ''}`}
          onMouseMove={handleMouseMovePlayer}
          onMouseLeave={handleMouseLeavePlayer}
        >
          {/* HTML5 Video Element */}
          <video
            ref={videoRef}
            poster={videoData?.thumbnailUrl}
            className="yt-html5-video"
            style={{
              filter: qualityFilter,
              imageRendering: qualityRendering,
              transition: 'filter 0.3s cubic-bezier(0.2, 0, 0, 1)',
            }}
            onClick={() => !adState.isActive && togglePlay()}
            onPlay={() => {
              lastPlaybackTimestampRef.current = performance.now();
              setIsPlaying(true);
              updateResolutionFromElement(videoRef.current);
            }}
            onPause={() => {
              lastPlaybackTimestampRef.current = null;
              setIsPlaying(false);
            }}
            onLoadedMetadata={handleLoadedMetadata}
            onLoadedData={() => updateResolutionFromElement(videoRef.current)}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnded}
            onWaiting={() => setIsBuffering(true)}
            onPlaying={() => setIsBuffering(false)}
            onError={handleVideoError}
            playsInline
          />

          {/* Central Buffering Spinner */}
          {isBuffering && (
            <div className="yt-player-buffering-spinner">
              <div className="yt-spinner-ring" />
            </div>
          )}

          {/* Central Play/Pause Click Ripple Animation (Single Smooth Trigger) */}
          {centerRipple && (
            <div className="yt-player-center-ripple" key={centerRipple.id}>
              {centerRipple.type === 'play' ? <MdPlayArrow size={64} /> : <MdPause size={64} />}
            </div>
          )}

          {/* On-Screen Display (OSD) Quality Notification Pill */}
          {qualityOsd.show && (
            <div className="yt-player-quality-osd" key={qualityOsd.label}>
              <div className="yt-osd-icon">
                <MdHighQuality size={20} color="#3ea6ff" />
              </div>
              <div className="yt-osd-text">
                <span className="yt-osd-label">{qualityOsd.label}</span>
                {qualityOsd.fps && <span className="yt-osd-fps">{qualityOsd.fps}</span>}
              </div>
            </div>
          )}

          {/* YouTube-Style Autoplay 5-Second Cooldown Overlay (Redesigned Autonav) */}
          {autoplayCountdown && !adState.isActive && (
            <div
              className="yt-autonav-overlay"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Top ambient linear countdown indicator */}
              <div className="yt-autonav-progress-track">
                <div
                  className="yt-autonav-progress-fill"
                  style={{
                    width: `${(autoplayCountdown.secondsLeft / 5) * 100}%`,
                  }}
                />
              </div>

              {/* Autonav Center Stage Card */}
              <div className="yt-autonav-card-container">
                {/* Header Line */}
                <div className="yt-autonav-header-row">
                  <div className="yt-autonav-title-badge">
                    <span>Up next in</span>
                    <strong className="yt-autonav-countdown-num">{autoplayCountdown.secondsLeft}</strong>
                  </div>
                </div>

                {/* Main Hero Thumbnail with Centered Circular Countdown Play Button */}
                <div
                  className="yt-autonav-hero-preview"
                  onClick={handlePlayNextNow}
                  title={`Play next: ${autoplayCountdown.nextVideo.title}`}
                >
                  <img
                    src={autoplayCountdown.nextVideo.thumbnailUrl || DEFAULT_FALLBACK_THUMBNAIL}
                    alt={autoplayCountdown.nextVideo.title}
                    className="yt-autonav-hero-img"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = DEFAULT_FALLBACK_THUMBNAIL;
                    }}
                  />
                  <div className="yt-autonav-hero-scrim" />

                  {/* Centered Circular SVG Countdown Spinner & Play Trigger */}
                  <div className="yt-autonav-center-play-circle">
                    <svg className="yt-autonav-countdown-svg" viewBox="0 0 68 68">
                      <circle
                        className="yt-autonav-svg-bg"
                        cx="34"
                        cy="34"
                        r="30"
                        strokeWidth="3.5"
                      />
                      <circle
                        className="yt-autonav-svg-bar"
                        cx="34"
                        cy="34"
                        r="30"
                        strokeWidth="3.5"
                        style={{
                          strokeDasharray: 188.5,
                          strokeDashoffset: 188.5 * (1 - autoplayCountdown.secondsLeft / 5),
                        }}
                      />
                    </svg>
                    <div className="yt-autonav-play-icon-inner">
                      <MdPlayArrow size={34} />
                    </div>
                  </div>

                  {autoplayCountdown.nextVideo.duration && (
                    <span className="yt-autonav-duration-pill">
                      {autoplayCountdown.nextVideo.duration}
                    </span>
                  )}
                </div>

                {/* Video Info: Title, Channel & Views */}
                <div className="yt-autonav-info-block" onClick={handlePlayNextNow}>
                  <h3 className="yt-autonav-video-title" title={autoplayCountdown.nextVideo.title}>
                    {autoplayCountdown.nextVideo.title}
                  </h3>
                  <div className="yt-autonav-meta-row">
                    <span className="yt-autonav-channel-name">{autoplayCountdown.nextVideo.channelName}</span>
                    {autoplayCountdown.nextVideo.verified && (
                      <MdCheckCircle className="yt-verified-icon" size={13} title="Verified" />
                    )}
                    {autoplayCountdown.nextVideo.views && (
                      <>
                        <span className="yt-autonav-dot">•</span>
                        <span className="yt-autonav-views-text">{autoplayCountdown.nextVideo.views}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Footer Action Buttons */}
                <div className="yt-autonav-actions-row">
                  <button
                    type="button"
                    className="yt-autonav-cancel-pill-btn"
                    onClick={cancelAutoplayCountdown}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="yt-autonav-playnow-pill-btn"
                    onClick={handlePlayNextNow}
                  >
                    <MdPlayArrow size={18} />
                    <span>Play now</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* YouTube Mid-roll In-Video Advertisement Overlay */}
          {adState.isActive && adState.banner && (
            <div className="yt-player-ad-overlay">
              {/* Direct Media Presentation in Video Playbox (YouTube Mid-roll Style) */}
              <div
                className="yt-ad-direct-media-wrap"
                onClick={() => adState.banner.link && window.open(adState.banner.link, '_blank')}
                style={{ cursor: adState.banner.link ? 'pointer' : 'default' }}
                title={adState.banner.link ? `Visit ${adState.banner.title}` : undefined}
              >
                {/\.(mp4|webm|mov|mkv|3gp)/i.test(adState.banner.ad_banner_url || '') ? (
                  <video
                    src={adState.banner.ad_banner_url}
                    className="yt-ad-direct-video"
                    autoPlay
                    playsInline
                    muted={false}
                    onCanPlay={(e) => {
                      const vid = e.currentTarget;
                      vid.play().catch(() => {
                        // If browser blocks unmuted autoplay without prior interaction, play muted
                        vid.muted = true;
                        vid.play();
                      });
                    }}
                  />
                ) : (
                  <img
                    src={normalizeMediaUrl(adState.banner.ad_banner_url)}
                    alt={adState.banner.title}
                    className="yt-ad-direct-img"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = `${process.env.PUBLIC_URL || ''}/img/banner.png`;
                    }}
                  />
                )}
              </div>

              {/* Bottom Advertiser Row + Skip Button */}
              <div className="yt-ad-bottom-bar" onClick={(e) => e.stopPropagation()}>
                <div className="yt-ad-left-attribution">
                  <span className="yt-ad-badge-pill">Ad</span>
                  <span className="yt-ad-count-text">1 of 1</span>

                  {/* YouTube Info "i" Button */}
                  <button
                    type="button"
                    className="yt-ad-info-icon-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAdState((prev) => ({ ...prev, showInfoModal: !prev.showInfoModal }));
                    }}
                    title="About this ad"
                    aria-label="About this ad"
                  >
                    <MdInfoOutline size={17} />
                  </button>

                  <div className="yt-ad-attribution-text">
                    <span className="yt-ad-attr-title">{adState.banner.title}</span>
                    {adState.banner.link && (
                      <a
                        href={adState.banner.link}
                        target="_blank"
                        rel="noreferrer"
                        className="yt-ad-attr-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span>Visit Advertiser</span>
                        <MdOpenInNew size={13} />
                      </a>
                    )}
                  </div>
                </div>

                <div className="yt-ad-right-skip">
                  {!adState.canSkip ? (
                    <div className="yt-ad-countdown-box">
                      <span>Video will play after</span>
                      <strong>{adState.countdown}</strong>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="yt-ad-skip-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSkipAd();
                      }}
                      title="Skip Ad"
                      aria-label="Skip Ad"
                    >
                      <span>Skip Ad</span>
                      <MdSkipNext size={22} />
                    </button>
                  )}
                </div>
              </div>

              {/* Bottom Yellow Progress Bar */}
              <div className="yt-ad-bottom-progress-bar">
                <div
                  className="yt-ad-bottom-progress-fill"
                  style={{ width: `${Math.min(100, (adState.elapsed / (adState.duration || 15)) * 100)}%` }}
                />
              </div>

              {/* About this ad Modal */}
              {adState.showInfoModal && (
                <div
                  className="yt-ad-info-modal-backdrop"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAdState((prev) => ({ ...prev, showInfoModal: false }));
                  }}
                >
                  <div className="yt-ad-info-modal-card" onClick={(e) => e.stopPropagation()}>
                    <div className="yt-ad-info-modal-header">
                      <h3>About this ad</h3>
                      <button
                        type="button"
                        className="yt-ad-info-close-btn"
                        onClick={() => setAdState((prev) => ({ ...prev, showInfoModal: false }))}
                      >
                        <MdClose size={18} />
                      </button>
                    </div>
                    <div className="yt-ad-info-modal-body">
                      <div>
                        <strong>Advertiser:</strong> {adState.banner.title || 'SpeedoTube Sponsored Partner'}
                      </div>
                      {adState.banner.link && (
                        <div>
                          <strong>Website:</strong>{' '}
                          <a
                            href={adState.banner.link}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: '#3ea6ff', textDecoration: 'none' }}
                          >
                            {adState.banner.link}
                          </a>
                        </div>
                      )}
                      <div>
                        <strong>Why you're seeing this ad:</strong>
                        <p style={{ margin: '4px 0 0 0', color: '#aaaaaa', fontSize: '12px' }}>
                          This advertisement was placed and managed through SpeedoTube CRM Campaign Manager based on current channel audience engagement.
                        </p>
                      </div>
                    </div>
                    <div className="yt-ad-info-modal-footer">
                      {adState.banner.link && (
                        <button
                          type="button"
                          className="yt-ad-info-visit-btn"
                          onClick={() => window.open(adState.banner.link, '_blank')}
                        >
                          Visit Site
                        </button>
                      )}
                      <button
                        type="button"
                        className="yt-ad-info-cancel-btn"
                        onClick={() => setAdState((prev) => ({ ...prev, showInfoModal: false }))}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Player Custom Controls Overlay */}
          <div
            className={`yt-player-controls-overlay ${(showControls || !isPlaying) && !adState.isActive ? 'visible' : ''}`}
            onClick={(e) => {
              if (
                e.target === e.currentTarget ||
                e.target.classList.contains('yt-player-click-surface') ||
                e.target.classList.contains('yt-player-top-shadow')
              ) {
                if (isMuted && isPlaying) {
                  handleUnmute(e);
                } else {
                  togglePlay();
                }
              }
            }}
          >
            {/* Top Shadow Gradient */}
            <div className="yt-player-top-shadow" />

            {/* YouTube Fullscreen Video Name & Channel Top Bar */}
            {isFullscreen && videoData?.title && (
              <div
                className="yt-player-fullscreen-header"
                onClick={(e) => e.stopPropagation()}
              >
                {videoData.channelAvatar ? (
                  <div
                    className="yt-player-fullscreen-avatar"
                    onClick={(e) => handleChannelClick(e)}
                    title={videoData.channelName}
                  >
                    <img
                      src={videoData.channelAvatar}
                      alt={videoData.channelName}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                ) : null}
                <div className="yt-player-fullscreen-title-box">
                  <h2 className="yt-player-fullscreen-title" title={videoData.title}>
                    {videoData.title}
                  </h2>
                  {videoData.channelName && (
                    <span
                      className="yt-player-fullscreen-channel"
                      onClick={(e) => handleChannelClick(e)}
                      title={`Go to channel ${videoData.channelName}`}
                    >
                      {videoData.channelName}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Central Transparent Click Area to Play/Pause & Unmute */}
            <div
              className="yt-player-click-surface"
              onClick={(e) => {
                e.stopPropagation();
                if (isMuted && isPlaying) {
                  handleUnmute(e);
                } else {
                  togglePlay();
                }
              }}
            />

            {/* Prominent Center Play Button when Paused or Replay when Ended */}
            {!isPlaying && !isBuffering && !adState.isActive && !autoplayCountdown && (
              <button
                type="button"
                className="yt-player-big-center-play-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if (videoRef.current) {
                    videoRef.current.muted = false;
                    setIsMuted(false);
                  }
                  togglePlay();
                }}
                aria-label={videoRef.current?.ended ? 'Replay video' : 'Play video'}
                title={videoRef.current?.ended ? 'Replay' : 'Play (k)'}
              >
                <div className="yt-big-play-icon-wrap">
                  {videoRef.current?.ended ? <MdReplay size={44} /> : <MdPlayArrow size={44} />}
                </div>
              </button>
            )}

            {/* Bottom Controls Wrap */}
            <div className="yt-player-bottom-controls-wrap" onClick={(e) => e.stopPropagation()}>
              {/* YouTube Progress Scrubber Bar */}
              <div
                ref={progressBarRef}
                className="yt-player-scrub-bar-container"
                onClick={handleScrubberClick}
                onMouseDown={handleScrubberMouseDown}
                onTouchStart={handleScrubberTouchStart}
                onMouseMove={handleScrubberMouseMove}
                onMouseLeave={handleScrubberMouseLeave}
              >
                {/* Background Track */}
                <div className="yt-scrub-track-bg">
                  {/* Buffer / Loaded Progress */}
                  <div
                    className="yt-scrub-buffer-bar"
                    style={{ width: `${Math.min(100, bufferPercent)}%` }}
                  />
                  {/* Played Progress (YouTube Red) */}
                  <div
                    className="yt-scrub-played-bar"
                    style={{ width: `${Math.min(100, progressPercent)}%` }}
                  >
                    {/* Red Circular Scrubber Handle */}
                    <div className="yt-scrub-handle" />
                  </div>

                  {/* YouTube Mid-roll Ad Cue Marker hidden per user requirement */}
                </div>

                {/* Time Preview Tooltip on Hover */}
                {scrubHoverTime !== null && (
                  <div
                    className="yt-scrub-time-tooltip"
                    style={{ left: `${scrubHoverPos}px` }}
                  >
                    {formatTime(scrubHoverTime)}
                  </div>
                )}
              </div>

              {/* Bottom Buttons Row */}
              <div className="yt-player-controls-row">
                {/* Left Controls Group */}
                <div className="yt-player-left-controls">
                  {/* Play / Pause Button */}
                  <button
                    className="yt-player-btn"
                    onClick={togglePlay}
                    aria-label={isPlaying ? 'Pause (k)' : 'Play (k)'}
                    data-yt-tooltip={isPlaying ? 'Pause (k)' : 'Play (k)'}
                    data-tooltip-align="left"
                  >
                    {isPlaying ? <MdPause size={28} /> : <MdPlayArrow size={28} />}
                  </button>

                  {/* Next Video Button */}
                  <button
                    className="yt-player-btn"
                    onClick={() => {
                      if (ecosystemVideos.length > 0) {
                        const currentIndex = ecosystemVideos.findIndex(
                          (v) => v.videoUrlId === videoData?.videoUrlId || String(v.id) === String(videoData?.id)
                        );
                        const nextIndex = (currentIndex !== -1 && currentIndex + 1 < ecosystemVideos.length) ? currentIndex + 1 : 0;
                        autoPlayOnLoadRef.current = true;
                        handleSelectRecommendedVideo(ecosystemVideos[nextIndex]);
                      }
                    }}
                    aria-label="Next (Shift+N)"
                    data-yt-tooltip="Next (Shift+N)"
                  >
                    <MdSkipNext size={26} />
                  </button>

                  {/* Volume Group with Ball-Type Knob */}
                  <div className="yt-player-volume-group">
                    <button
                      className="yt-player-btn"
                      onClick={toggleMute}
                      aria-label={isMuted || volume === 0 ? 'Unmute (m)' : 'Mute (m)'}
                      data-yt-tooltip={isMuted || volume === 0 ? 'Unmute (m)' : 'Mute (m)'}
                    >
                      {isMuted || volume === 0 ? (
                        <MdVolumeOff size={24} />
                      ) : volume < 0.5 ? (
                        <MdVolumeDown size={24} />
                      ) : (
                        <MdVolumeUp size={24} />
                      )}
                    </button>
                    <div className="yt-player-volume-slider-wrap">
                      <div className="yt-volume-track-bg">
                        <div
                          className="yt-volume-track-fill"
                          style={{ width: `${(isMuted ? 0 : volume) * 100}%` }}
                        >
                          <span className="yt-volume-ball-knob" />
                        </div>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={isMuted ? 0 : volume}
                        onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                        className="yt-player-volume-range"
                        aria-label="Volume slider"
                      />
                    </div>
                  </div>

                  {/* Time Duration Display */}
                  <div className="yt-player-time-display">
                    <span>{formatTime(currentTime)}</span>
                    <span className="yt-time-sep">/</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>

                {/* Right Controls Group */}
                <div className="yt-player-right-controls">
                  {/* Autoplay Toggle */}
                  <button
                    className={`yt-player-btn yt-player-autoplay-btn ${autoplayNext ? 'active' : ''}`}
                    onClick={handleToggleAutoplay}
                    aria-label={`Autoplay is ${autoplayNext ? 'on' : 'off'}`}
                    data-yt-tooltip={`Autoplay is ${autoplayNext ? 'on' : 'off'}`}
                  >
                    <span className="yt-autoplay-pill">
                      <span className="yt-autoplay-slider" />
                    </span>
                  </button>

                  {/* Subtitles / CC */}
                  <button
                    className="yt-player-btn"
                    aria-label="Subtitles/closed captions (c)"
                    data-yt-tooltip="Subtitles/closed captions (c)"
                  >
                    <MdSubtitles size={22} />
                  </button>

                  {/* Settings Cog with 4K/HD Badge */}
                  <div className={`yt-player-settings-wrap ${showSettingsMenu && !isSettingsClosing ? 'open' : ''}`} ref={settingsWrapRef}>
                    <button
                      className="yt-player-btn yt-settings-cog-btn"
                      onClick={toggleSettingsMenu}
                      aria-label="Settings"
                      data-yt-tooltip={showSettingsMenu ? '' : 'Settings'}
                    >
                      <div className="yt-settings-btn-inner">
                        <MdSettings size={22} />
                        {(() => {
                          const topOpt = qualityOptions.find((q) => q.id !== 'auto') || qualityOptions[0];
                          const isCurrent4K = activeQuality === '2160p' || (activeQuality === 'auto' && topOpt?.is4K);
                          const isCurrentHD = activeQuality === '1440p' || activeQuality === '1080p' || activeQuality === '720p' || (activeQuality === 'auto' && topOpt?.isHD && !topOpt?.is4K);
                          const isCurrentSD = (activeQuality === '480p' || activeQuality === '360p' || activeQuality === '240p' || activeQuality === '144p') && !(activeQuality === 'auto' && topOpt?.isHD);

                          if (isCurrent4K) {
                            return <span className="yt-gear-quality-badge badge-4k">4K</span>;
                          }
                          if (isCurrentHD) {
                            return <span className="yt-gear-quality-badge badge-hd">HD</span>;
                          }
                          if (isCurrentSD) {
                            return <span className="yt-gear-quality-badge badge-sd">SD</span>;
                          }
                          return null;
                        })()}
                      </div>
                    </button>

                    {/* Redesigned Multi-Level Settings Popup Menu with Smooth Transitions */}
                    {showSettingsMenu && (
                      <div className={`yt-player-settings-popup ${isSettingsClosing ? 'closing' : ''}`}>
                        {settingsSubmenu === 'main' && (
                          <div className={`yt-settings-view yt-settings-main-view ${submenuDirection === 'backward' ? 'slide-from-left' : 'fade-enter'}`}>
                            <div className="yt-settings-header-row">
                              <span className="yt-settings-title">Playback settings</span>
                              <button
                                className="yt-settings-close-icon-btn"
                                onClick={closeSettingsMenu}
                                aria-label="Close settings"
                              >
                                <MdClose size={16} />
                              </button>
                            </div>
                            <div className="yt-settings-menu-list">
                              {/* Playback speed option */}
                              <div
                                className="yt-settings-menu-item"
                                onClick={() => openSubmenu('speed')}
                              >
                                <div className="yt-settings-menu-left">
                                  <MdSpeed size={20} className="yt-settings-item-icon" />
                                  <span>Playback speed</span>
                                </div>
                                <div className="yt-settings-menu-right">
                                  <span className="yt-settings-val-pill">
                                    {playbackRate === 1 ? 'Normal' : `${playbackRate}x`}
                                  </span>
                                  <MdChevronRight size={20} className="yt-settings-chevron" />
                                </div>
                              </div>

                              {/* Quality option */}
                              <div
                                className="yt-settings-menu-item"
                                onClick={() => openSubmenu('quality')}
                              >
                                <div className="yt-settings-menu-left">
                                  <MdHighQuality size={20} className="yt-settings-item-icon" />
                                  <span>Quality</span>
                                </div>
                                <div className="yt-settings-menu-right">
                                  <span className="yt-settings-val-pill">
                                    {(() => {
                                      const cur = qualityOptions.find((q) => q.id === activeQuality);
                                      if (!cur) return '1080p HD • 60fps';
                                      if (cur.id === 'auto') return cur.fullLabel;
                                      return `${cur.shortLabel}${cur.fps ? ` • ${cur.fps}` : ''}`;
                                    })()}
                                  </span>
                                  <MdChevronRight size={20} className="yt-settings-chevron" />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {settingsSubmenu === 'speed' && (
                          <div className="yt-settings-view yt-settings-submenu-view slide-from-right">
                            <div className="yt-settings-sub-header">
                              <button
                                className="yt-settings-back-btn"
                                onClick={goBackToMainMenu}
                              >
                                <MdArrowBack size={18} />
                                <span>Playback speed</span>
                              </button>
                            </div>
                            <div className="yt-settings-options-list">
                              {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((rate) => {
                                const isSelected = playbackRate === rate;
                                return (
                                  <button
                                    key={rate}
                                    className={`yt-settings-option-row ${isSelected ? 'active' : ''}`}
                                    onClick={() => handleSpeedSelect(rate)}
                                  >
                                    <span className="yt-settings-check-col">
                                      {isSelected && <MdCheck size={16} />}
                                    </span>
                                    <span className="yt-settings-option-label">
                                      {rate === 1 ? 'Normal' : `${rate}x`}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {settingsSubmenu === 'quality' && (
                          <div className="yt-settings-view yt-settings-submenu-view slide-from-right">
                            <div className="yt-settings-sub-header">
                              <button
                                className="yt-settings-back-btn"
                                onClick={goBackToMainMenu}
                              >
                                <MdArrowBack size={18} />
                                <span>Quality for current video</span>
                              </button>
                            </div>
                            <div className="yt-settings-options-list">
                              {qualityOptions.map((q) => {
                                const isSelected = activeQuality === q.id;
                                // For the currently active quality, show real measured FPS if available
                                const fpsLabel = (isSelected && realFps) ? `${realFps}fps` : q.fps;
                                return (
                                  <button
                                    key={q.id}
                                    className={`yt-settings-option-row ${isSelected ? 'active' : ''}`}
                                    onClick={() => handleQualitySelect(q)}
                                  >
                                    <span className="yt-settings-check-col">
                                      {isSelected && <MdCheck size={16} />}
                                    </span>
                                    <div className="yt-settings-quality-info">
                                      <span className="yt-settings-option-label">{q.label}</span>
                                      {fpsLabel && <span className="yt-settings-fps-tag">{fpsLabel}</span>}
                                    </div>
                                    {q.badge && (
                                      <span className={`yt-settings-badge-pill ${q.id === 'auto' ? 'badge-auto' : q.is4K ? 'badge-4k' : q.isHD ? 'badge-hd' : ''}`}>
                                        {q.badge}
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Miniplayer (Picture-in-Picture) */}
                  <button
                    className="yt-player-btn"
                    onClick={togglePiP}
                    aria-label="Miniplayer (i)"
                    data-yt-tooltip="Miniplayer (i)"
                  >
                    <MdPictureInPictureAlt size={22} />
                  </button>

                  {/* Theater Mode Toggle with Fluid Transition */}
                  <button
                    className={`yt-player-btn ${isTheaterMode ? 'active' : ''}`}
                    onClick={toggleTheaterMode}
                    aria-label={isTheaterMode ? 'Default view (t)' : 'Theater mode (t)'}
                    data-yt-tooltip={isTheaterMode ? 'Default view (t)' : 'Theater mode (t)'}
                  >
                    {isTheaterMode ? <MdOutlineWebAsset size={22} /> : <MdCropLandscape size={24} />}
                  </button>

                  {/* Fullscreen Toggle */}
                  <button
                    className="yt-player-btn"
                    onClick={toggleFullscreen}
                    aria-label={isFullscreen ? 'Exit full screen (f)' : 'Full screen (f)'}
                    data-yt-tooltip={isFullscreen ? 'Exit full screen (f)' : 'Full screen (f)'}
                    data-tooltip-align="right"
                  >
                    {isFullscreen ? <MdFullscreenExit size={26} /> : <MdFullscreen size={26} />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Left Column Wrapper: Uses display: contents in Default Mode, and Flex Column in Theater Mode */}
      <div className="yt-watch-left-column">
        {/* Primary Video Column (Left: Metadata, Description Card) */}
        <div ref={primaryColRef} className="yt-watch-primary">
        {/* Video Title */}
        <h1 className="yt-watch-title">
          {videoData?.title || "Video"}
        </h1>

        {/* Channel Info & Actions Bar (Directly on Web Background) */}
        <div className="yt-watch-actions-bar">
          {/* Left: Channel Information */}
          <div className="yt-watch-channel-group">
            <div 
              className="yt-watch-channel-avatar"
              onClick={handleChannelClick}
              role="button"
              tabIndex={0}
              style={{ cursor: 'pointer' }}
              title={`Go to ${videoData?.channelName || 'channel'}`}
            >
              {videoData?.channelAvatar ? (
                <img
                  src={normalizeMediaUrl(videoData.channelAvatar)}
                  alt={videoData.channelName}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    if (e.currentTarget.nextElementSibling) {
                      e.currentTarget.nextElementSibling.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <div
                className="yt-watch-avatar-fallback"
                style={{
                  backgroundColor: getAvatarColor(videoData?.channelName || 'Pratham'),
                  display: videoData?.channelAvatar ? 'none' : 'flex',
                }}
              >
                {(videoData?.channelName || 'P').charAt(0).toUpperCase()}
              </div>
            </div>

            <div className="yt-watch-channel-meta">
              <div 
                className="yt-watch-channel-name-row"
                onClick={handleChannelClick}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                title={`Go to ${videoData?.channelName || 'channel'}`}
              >
                <span className="yt-watch-channel-name">{videoData?.channelName || 'Pratham'}</span>
                <MdCheckCircle className="yt-watch-verified-icon" size={14} title="Verified" />
              </div>
              <span className="yt-watch-sub-count">{formatSubscribersCount(subscriberCount)}</span>
            </div>

            {isOwner ? (
              <div className="yt-watch-owner-actions">
                <button
                  className="yt-watch-owner-btn"
                  onClick={() => onNavigate?.('/studio/analytics')}
                  title="View video analytics"
                >
                  <MdOutlineAnalytics size={18} />
                  <span>Analytics</span>
                </button>
                <button
                  className="yt-watch-owner-btn"
                  onClick={() => onNavigate?.('/studio/content')}
                  title="Edit video"
                >
                  <MdOutlineEdit size={16} />
                  <span>Edit video</span>
                </button>
              </div>
            ) : (
              <>
                {/* Join Button */}
                <button className="yt-watch-join-btn">Join</button>

                {/* Subscribe Button - Creative Animated Subscribe */}
                <button
                  key={`sub-btn-${subAnimKey}`}
                  className={`yt-watch-subscribe-btn yt-sub-creative ${isSubscribed ? 'subscribed' : ''} ${
                    subAnimType === 'subscribing'
                      ? 'anim-subscribing'
                      : subAnimType === 'unsubscribing'
                      ? 'anim-unsubscribing'
                      : ''
                  }`}
                  onClick={handleSubscribeToggle}
                  aria-label={isSubscribed ? 'Subscribed to this channel' : 'Subscribe to this channel'}
                >
                  {/* Creative Ambient Aura Glow */}
                  <span className="yt-sub-ambient-glow" aria-hidden="true" />
                  
                  {/* Shimmer light-beam sweep across pill */}
                  <span className="yt-sub-shimmer-sweep" aria-hidden="true" />

                  <span
                    className={`yt-sub-content-inner ${
                      isSubscribed ? 'is-subscribed' : 'is-unsubscribed'
                    } ${subAnimType ? 'anim-active' : ''}`}
                  >
                    {isSubscribed ? (
                      <>
                        <span className="yt-sub-icon-wrap yt-sub-bell-wrap">
                          <MdNotificationsActive size={17} className="yt-sub-bell-icon" />
                        </span>
                        <span className="yt-sub-text">Subscribed</span>
                      </>
                    ) : (
                      <span className="yt-sub-text">Subscribe</span>
                    )}
                  </span>

                  <span className="yt-sub-ripple-container">
                    {subAnimType === 'subscribing' && <span className="yt-sub-touch-ripple" />}
                  </span>

                  {/* Creative Sparkling Star Burst on Subscribing */}
                  {subAnimType === 'subscribing' && (
                    <span className="yt-sub-sparkle-burst" aria-hidden="true">
                      <span className="yt-sub-sparkle s1" />
                      <span className="yt-sub-sparkle s2" />
                      <span className="yt-sub-sparkle s3" />
                      <span className="yt-sub-sparkle s4" />
                      <span className="yt-sub-sparkle s5" />
                      <span className="yt-sub-sparkle s6" />
                      <span className="yt-sub-sparkle s7" />
                      <span className="yt-sub-sparkle s8" />
                    </span>
                  )}
                </button>
              </>
            )}
          </div>

          {/* Right: Actions Bar (Non-sticky separated Like/Dislike, Share, Save, More) */}
          <div className="yt-watch-buttons-group">
            {/* Like Button - YouTube-Style Animated Like Button */}
            <button
              className={`yt-watch-action-pill yt-like-pill ${isLiked ? 'active' : ''} ${
                likeAnimType === 'liked'
                  ? 'anim-pill-press'
                  : likeAnimType === 'unliked'
                  ? 'anim-pill-unpress'
                  : ''
              }`}
              onClick={handleLike}
              aria-label="I like this"
              data-yt-tooltip="I like this"
              data-tooltip-pos="bottom"
            >
              <span
                key={`like-icon-${likeAnimKey}`}
                className={`yt-action-icon-wrap ${
                  likeAnimType === 'liked'
                    ? 'pop-bounce-like'
                    : likeAnimType === 'unliked'
                    ? 'pop-bounce-unlike'
                    : ''
                }`}
              >
                {isLiked ? (
                  <MdThumbUp size={18} className="yt-thumb-icon yt-thumb-filled" />
                ) : (
                  <MdOutlineThumbUp size={18} className="yt-thumb-icon yt-thumb-outline" />
                )}

                {/* YouTube-Style Visual Feedback Halo & Subtle Sparkles on Like */}
                {likeAnimType === 'liked' && (
                  <>
                    <span className="yt-like-halo-ring" />
                    <span className="yt-sparkle-burst">
                      <span className="yt-sparkle-dot dot-1" />
                      <span className="yt-sparkle-dot dot-2" />
                      <span className="yt-sparkle-dot dot-3" />
                      <span className="yt-sparkle-dot dot-4" />
                      <span className="yt-sparkle-dot dot-5" />
                      <span className="yt-sparkle-dot dot-6" />
                    </span>
                  </>
                )}
              </span>
              <span className={`yt-like-count-text ${likeAnimType ? 'bump' : ''}`}>
                {formatLikesCount(likeCount)}
              </span>
            </button>

            {/* Dislike Button with creative spring pop */}
            <button
              className={`yt-watch-action-pill yt-dislike-pill ${isDisliked ? 'active' : ''}`}
              onClick={() => {
                triggerDislikeAnim();
                handleDislike();
              }}
              aria-label="I dislike this"
              data-yt-tooltip="I dislike this"
              data-tooltip-pos="bottom"
            >
              <span className={`yt-action-icon-wrap ${isDislikeAnimating ? 'pop-bounce-down' : ''}`}>
                {isDisliked ? <MdThumbDown size={18} /> : <MdOutlineThumbDown size={18} />}
              </span>
            </button>

            {/* Share Button */}
            <button
              className="yt-watch-action-pill"
              onClick={handleShare}
              aria-label="Share"
              data-yt-tooltip="Share"
              data-tooltip-pos="bottom"
            >
              <RiShareForwardLine size={20} />
              <span>Share</span>
            </button>

            {/* Save / Playlist Button with creative drop bounce */}
            <button
              className={`yt-watch-action-pill yt-save-pill ${isSavedToWatchLater ? 'active' : ''}`}
              onClick={() => {
                triggerSaveAnim();
                handleToggleSaveToWatchLater();
              }}
              aria-label={isSavedToWatchLater ? 'Saved in Watch later' : 'Save to playlist'}
              data-yt-tooltip={isSavedToWatchLater ? 'Saved in Watch later' : 'Save'}
              data-tooltip-pos="bottom"
            >
              <span className={`yt-action-icon-wrap ${isSaveAnimating ? 'pop-bounce-save' : ''}`}>
                {isSavedToWatchLater ? <MdBookmarkAdded size={19} /> : <MdOutlineBookmarkAdd size={19} />}
                {isSaveAnimating && isSavedToWatchLater && (
                  <span className="yt-sparkle-burst">
                    <span className="yt-sparkle-dot dot-1" />
                    <span className="yt-sparkle-dot dot-2" />
                    <span className="yt-sparkle-dot dot-3" />
                    <span className="yt-sparkle-dot dot-4" />
                  </span>
                )}
              </span>
              <span>{isSavedToWatchLater ? 'Saved' : 'Save'}</span>
            </button>

            {/* Download Button */}
            <button
              className={`yt-watch-action-pill yt-download-pill ${isDownloadedState ? 'active downloaded' : ''} ${isDownloading ? 'downloading' : ''}`}
              onClick={handleDownloadClick}
              aria-label={isDownloadedState ? 'Downloaded (Click to remove)' : 'Download video'}
              data-yt-tooltip={isDownloadedState ? 'Downloaded' : 'Download'}
              data-tooltip-pos="bottom"
            >
              <span className="yt-action-icon-wrap">
                {isDownloading ? (
                  <span className="yt-download-spinner" />
                ) : isDownloadedState ? (
                  <MdDownloadDone size={20} style={{ color: '#3ea6ff' }} />
                ) : (
                  <MdOutlineFileDownload size={20} />
                )}
              </span>
              <span>{isDownloading ? 'Downloading...' : isDownloadedState ? 'Downloaded' : 'Download'}</span>
            </button>

            {/* Report Button */}
            <button
              className="yt-watch-action-pill"
              onClick={() => setIsReportModalOpen(true)}
              aria-label="Report"
              data-yt-tooltip="Report"
              data-tooltip-pos="bottom"
            >
              <MdOutlinedFlag size={20} />
              <span>Report</span>
            </button>
          </div>
        </div>

        {/* Expandable Video Description Box - Sole Card on Page with Smooth Animation */}
        <div
          ref={descCardRef}
          className={`yt-watch-description-card ${isDescExpanded ? 'expanded' : ''}`}
          onClick={() => !isDescExpanded && setIsDescExpanded(true)}
        >
          <div className="yt-desc-header">
            <div className="yt-desc-header-left">
              <span className="yt-desc-stat">{formatViewsCount(viewsCount)}</span>
              <span className="yt-desc-stat">{formatUploadDate(videoData?.createdDate)}</span>
              <span className="yt-desc-tags">
                {displayTags}
              </span>
            </div>
            <div className="yt-desc-header-right">
              <span className="yt-desc-studio-attribution">Uploaded via Speedo Studio</span>
            </div>
          </div>

          <div className="yt-desc-content">
            <div className={`yt-desc-expandable ${isDescExpanded ? 'expanded' : ''}`}>
              {videoData?.description && videoData.description !== "Uploaded via Speedo Studio" && (
                <p className="yt-desc-text">
                  {videoData.description}
                </p>
              )}
              {musicCredits && (
                <div className="yt-desc-extra-info">
                  <hr className="yt-desc-divider" />
                  <div className="yt-music-credit-row">
                    <span className="yt-credit-label">Song</span>
                    <span className="yt-credit-val">
                      {musicCredits.song}
                    </span>
                  </div>
                  <div className="yt-music-credit-row">
                    <span className="yt-credit-label">Artists</span>
                    <span className="yt-credit-val">
                      {musicCredits.artists}
                    </span>
                  </div>
                  <div className="yt-music-credit-row">
                    <span className="yt-credit-label">Album</span>
                    <span className="yt-credit-val">
                      {musicCredits.album}
                    </span>
                  </div>
                  <div className="yt-music-credit-row">
                    <span className="yt-credit-label">Licensed to SpeedoTube by</span>
                    <span className="yt-credit-val">
                      {musicCredits.license}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <button
            className="yt-desc-toggle-btn"
            onClick={(e) => {
              e.stopPropagation();
              setIsDescExpanded((prev) => !prev);
            }}
          >
            {isDescExpanded ? 'Show less' : '...more'}
          </button>
        </div>
      </div>

      {/* Full-Width Section Covering Entire Width Horizontally in Default Mode, Displayed Immediately Below Description in Theater Mode */}
      <div ref={ecosystemRef} className="yt-watch-horizontal-ecosystem-section">
        <div className="yt-horizontal-ecosystem-header">
          <h3 className="yt-horizontal-ecosystem-title">More Videos you may Like</h3>
        </div>

        <div className="yt-horizontal-ecosystem-grid">
          {ecosystemVideos.map((rec) => (
            <div
              key={rec.id || rec.videoUrlId}
              className={`yt-horizontal-video-card ${rec.videoUrlId === videoData?.videoUrlId ? 'active-playing' : ''}`}
              onClick={() => handleSelectRecommendedVideo(rec)}
            >
              <div className="yt-horizontal-thumb-wrap">
                <img
                  src={normalizeMediaUrl(rec.thumbnailUrl) || DEFAULT_FALLBACK_THUMBNAIL}
                  alt={rec.title}
                  className="yt-horizontal-thumb-img"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = (process.env.PUBLIC_URL || '') + '/img/banner.png';
                  }}
                />
                <span className="yt-rec-duration-badge">{rec.duration}</span>
              </div>
              <div className="yt-horizontal-video-info">
                <h4 className="yt-horizontal-video-title" title={rec.title}>
                  {rec.title}
                </h4>
                <div 
                  className="yt-horizontal-channel-name"
                  onClick={(e) => {
                    e.stopPropagation();
                    const h = rec.handleName || rec.channelName || '';
                    if (!h) return;
                    const cleanH = h.startsWith('@') ? h : `@${h}`;
                    const targetPath = `/channel/${encodeURIComponent(cleanH)}`;
                    if (onNavigate) onNavigate(targetPath);
                    else {
                      window.history.pushState({}, '', targetPath);
                      window.dispatchEvent(new CustomEvent('speedotube_navigate', { detail: { path: targetPath } }));
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  title={`Go to ${rec.channelName}`}
                >
                  <span>{rec.channelName}</span>
                  {rec.verified && <MdCheckCircle className="yt-verified-icon" size={13} title="Verified" />}
                </div>
                <div className="yt-horizontal-video-meta">
                  <span>{rec.views}</span>
                  <span className="yt-bullet">•</span>
                  <span>{rec.timestamp}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

      {/* Secondary Column: Comments Section (Right Side) */}
      <div ref={secondaryColRef} className="yt-watch-secondary">
        <div className={`yt-watch-comments-section right-rail ${!isUserAuthenticated ? 'is-blurred' : ''}`}>
          {/* Comments Header */}
          <div className="yt-comments-header">
            <h2 className="yt-comments-count">{sortedComments.length} Comments</h2>
            <div className="yt-comments-sort-container" ref={sortMenuRef}>
              <button 
                type="button"
                className={`yt-comments-sort-btn ${isSortMenuOpen ? 'active' : ''}`}
                onClick={() => setIsSortMenuOpen((prev) => !prev)}
                aria-expanded={isSortMenuOpen}
                aria-haspopup="menu"
                title="Sort comments"
              >
                <MdSort size={22} />
                <span>Sort by</span>
              </button>

              {isSortMenuOpen && (
                <div className="yt-comments-sort-dropdown" role="menu">
                  <button
                    type="button"
                    className={`yt-comments-sort-option ${sortBy === 'top' ? 'selected' : ''}`}
                    onClick={() => {
                      setSortBy('top');
                      setIsSortMenuOpen(false);
                    }}
                    role="menuitem"
                  >
                    <span>Top comments</span>
                  </button>
                  <button
                    type="button"
                    className={`yt-comments-sort-option ${sortBy === 'newest' ? 'selected' : ''}`}
                    onClick={() => {
                      setSortBy('newest');
                      setIsSortMenuOpen(false);
                    }}
                    role="menuitem"
                  >
                    <span>Newest first</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Blurred Guest / Logged-Out Overlay with Sign In Callout */}
          {!isUserAuthenticated && (
            <div
              className="yt-comments-blur-overlay"
              onClick={() => openSignInPrompt('comment', videoData?.title || '')}
            >
              <div className="yt-comments-blur-card" onClick={(e) => e.stopPropagation()}>
                <div className="yt-comments-blur-icon">
                  <HiOutlineUserCircle size={44} />
                </div>
                <h3 className="yt-comments-blur-title">Want to join the conversation?</h3>
                <p className="yt-comments-blur-desc">
                  Sign in to post comments, like videos, and subscribe to your favorite channels.
                </p>
                <button
                  type="button"
                  className="yt-comments-blur-signin-btn"
                  onClick={handlePromptSignIn}
                >
                  Sign in
                </button>
              </div>
            </div>
          )}

          {/* Add a Comment Input Row */}
          <form className="yt-add-comment-form" onSubmit={handleAddComment}>
            <div className="yt-comment-user-avatar">
              {currentUserAvatar ? (
                <img
                  src={currentUserAvatar}
                  alt={(activeAccount || userProfile)?.name || 'You'}
                  className="yt-avatar-circle"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    if (e.currentTarget.nextSibling) {
                      e.currentTarget.nextSibling.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <div
                className="yt-avatar-circle"
                style={{
                  display: currentUserAvatar ? 'none' : 'flex',
                  backgroundColor:
                    getAvatarColor((activeAccount || userProfile)?.name || (activeAccount || userProfile)?.handle || 'You'),
                }}
              >
                {((activeAccount || userProfile)?.name || (activeAccount || userProfile)?.initial || 'Y').trim().charAt(0).toUpperCase()}
              </div>
            </div>

            <div className="yt-comment-input-wrap">
              <input
                type="text"
                placeholder={isUserAuthenticated ? "Add a comment..." : "Sign in to add a comment..."}
                value={newCommentText}
                readOnly={!isUserAuthenticated}
                onChange={(e) => isUserAuthenticated && setNewCommentText(e.target.value)}
                onFocus={() => {
                  if (!isUserAuthenticated) {
                    openSignInPrompt('comment', videoData?.title || '');
                    return;
                  }
                  setIsCommentFocused(true);
                }}
                className="yt-comment-input"
              />
              <div className="yt-comment-input-line" />

              {/* Action Buttons on Focus */}
              {isCommentFocused && (
                <div className="yt-comment-actions">
                  <button
                    type="button"
                    className="yt-comment-cancel-btn"
                    onClick={() => {
                      setNewCommentText('');
                      setIsCommentFocused(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!newCommentText.trim() || isSubmittingComment}
                    className={`yt-comment-submit-btn ${newCommentText.trim() && !isSubmittingComment ? 'active' : ''}`}
                  >
                    {isSubmittingComment ? 'Adding...' : 'Comment'}
                  </button>
                </div>
              )}
            </div>
          </form>

          {/* Comments List */}
          <div className="yt-comments-list">
            {sortedComments.length === 0 ? (
              <div className="yt-no-comments-container">
                <p className="yt-no-comments-title">No comments yet</p>
                <p className="yt-no-comments-subtitle">Be the first to comment on this video!</p>
              </div>
            ) : (
              sortedComments.map((comment) => {
              const cAvatar = getCommentAvatar(comment);
              return (
                <div key={comment.id} className="yt-comment-item">
                  <div 
                    className="yt-comment-avatar"
                    onClick={(e) => {
                      e.stopPropagation();
                      const h = comment.handle || comment.author;
                      if (!h) return;
                      const cleanH = h.startsWith('@') ? h : `@${h}`;
                      const targetPath = `/channel/${encodeURIComponent(cleanH)}`;
                      if (onNavigate) onNavigate(targetPath);
                      else {
                        window.history.pushState({}, '', targetPath);
                        window.dispatchEvent(new CustomEvent('speedotube_navigate', { detail: { path: targetPath } }));
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                    title={`Go to ${comment.author || comment.handle}'s channel`}
                  >
                    {cAvatar ? (
                      <img
                        src={cAvatar}
                        alt={comment.author || comment.handle}
                        className="yt-avatar-circle"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          if (e.currentTarget.nextSibling) {
                            e.currentTarget.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div
                      className="yt-avatar-circle"
                      style={{
                        display: cAvatar ? 'none' : 'flex',
                        backgroundColor: getAvatarColor(comment.author || comment.handle),
                      }}
                    >
                      {(comment.author || comment.handle || 'U').replace(/^@/, '').charAt(0).toUpperCase()}
                    </div>
                  </div>

                <div className="yt-comment-body">
                  <div className="yt-comment-author-row">
                    <span 
                      className="yt-comment-author"
                      onClick={(e) => {
                        e.stopPropagation();
                        const h = comment.handle || comment.author;
                        if (!h) return;
                        const cleanH = h.startsWith('@') ? h : `@${h}`;
                        const targetPath = `/channel/${encodeURIComponent(cleanH)}`;
                        if (onNavigate) onNavigate(targetPath);
                        else {
                          window.history.pushState({}, '', targetPath);
                          window.dispatchEvent(new CustomEvent('speedotube_navigate', { detail: { path: targetPath } }));
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                      title={`Go to ${comment.author || comment.handle}'s channel`}
                    >
                      {comment.handle
                        ? (comment.handle.startsWith('@') ? comment.handle : `@${comment.handle}`)
                        : `@${comment.author}`}
                    </span>
                    {comment.verified && (
                      <MdCheckCircle className="yt-verified-icon" size={12} title="Verified" style={{ marginLeft: 4 }} />
                    )}
                    <span className="yt-comment-time">{comment.timeAgo}</span>
                  </div>

                  <p className="yt-comment-text">{comment.text}</p>

                  <div className="yt-comment-footer">
                    <button
                      className={`yt-comment-icon-btn ${comment.userLiked ? 'active' : ''}`}
                      onClick={() => handleCommentLike(comment.id)}
                      aria-label="Like"
                      data-yt-tooltip="Like"
                      data-tooltip-pos="bottom"
                    >
                      {comment.userLiked ? <MdThumbUp size={16} /> : <MdOutlineThumbUp size={16} />}
                    </button>
                    {comment.likes > 0 && (
                      <span className="yt-comment-likes">{comment.likes.toLocaleString()}</span>
                    )}

                    <button
                      className={`yt-comment-icon-btn ${comment.userDisliked ? 'active' : ''}`}
                      onClick={() => handleCommentDislike(comment.id)}
                      aria-label="Dislike"
                      data-yt-tooltip="Dislike"
                      data-tooltip-pos="bottom"
                    >
                      {comment.userDisliked ? <MdThumbDown size={16} /> : <MdOutlineThumbDown size={16} />}
                    </button>

                    <button
                      type="button"
                      className="yt-comment-reply-btn"
                      onClick={() => handleOpenReplyBox(comment.id, comment.handle || comment.author)}
                    >
                      Reply
                    </button>
                  </div>

                  {/* Inline Reply Input Box */}
                  {activeReplyId === comment.id && (
                    <div className="yt-inline-reply-box">
                      <div className="yt-inline-reply-avatar">
                        {currentUserAvatar ? (
                          <img src={currentUserAvatar} alt="You" className="yt-avatar-circle mini" />
                        ) : (
                          <div
                            className="yt-avatar-circle mini"
                            style={{
                              backgroundColor:
                                getAvatarColor((activeAccount || userProfile)?.name || (activeAccount || userProfile)?.handle || 'You'),
                            }}
                          >
                            {((activeAccount || userProfile)?.name || (activeAccount || userProfile)?.initial || 'Y').trim().charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="yt-inline-reply-input-wrap">
                        <input
                          type="text"
                          className="yt-comment-input inline"
                          placeholder={`Reply to ${comment.handle || comment.author}...`}
                          value={replyInputText[comment.id] || ''}
                          autoFocus
                          onChange={(e) =>
                            setReplyInputText((prev) => ({ ...prev, [comment.id]: e.target.value }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && (replyInputText[comment.id] || '').trim()) {
                              e.preventDefault();
                              handleSubmitReply(comment.id);
                            }
                          }}
                        />
                        <div className="yt-inline-reply-actions">
                          <button
                            type="button"
                            className="yt-comment-cancel-btn mini"
                            onClick={() => {
                              setActiveReplyId(null);
                              setReplyInputText((prev) => ({ ...prev, [comment.id]: '' }));
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className={`yt-comment-submit-btn mini ${(replyInputText[comment.id] || '').trim() && !isSubmittingReply[comment.id] ? 'active' : ''}`}
                            disabled={!(replyInputText[comment.id] || '').trim() || isSubmittingReply[comment.id]}
                            onClick={() => handleSubmitReply(comment.id)}
                          >
                            {isSubmittingReply[comment.id] ? 'Replying...' : 'Reply'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* View / Hide Replies Button */}
                  {(comment.hasReplies || (repliesMap[comment.id] && repliesMap[comment.id].length > 0)) && (
                    <button
                      type="button"
                      className="yt-comment-view-replies-btn"
                      onClick={() => handleToggleViewReplies(comment.id)}
                    >
                      {expandedReplies[comment.id] ? <MdArrowDropUp size={20} /> : <MdArrowDropDown size={20} />}
                      <span>
                        {expandedReplies[comment.id]
                          ? 'Hide replies'
                          : `${repliesMap[comment.id]?.length || comment.replyCount || 1} ${(repliesMap[comment.id]?.length || comment.replyCount || 1) === 1 ? 'reply' : 'replies'}`}
                      </span>
                    </button>
                  )}

                  {/* Replies Thread */}
                  {expandedReplies[comment.id] && (
                    <div className="yt-comment-replies-thread">
                      {loadingReplies[comment.id] ? (
                        <div className="yt-replies-loading">Loading replies...</div>
                      ) : (
                        (repliesMap[comment.id] || []).map((reply) => {
                          const rAvatar = getCommentAvatar(reply);
                          return (
                            <div key={reply.id} className="yt-comment-item reply-item">
                              <div
                                className="yt-comment-avatar reply-avatar"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const h = reply.handle || reply.author;
                                  if (!h) return;
                                  const cleanH = h.startsWith('@') ? h : `@${h}`;
                                  const targetPath = `/channel/${encodeURIComponent(cleanH)}`;
                                  if (onNavigate) onNavigate(targetPath);
                                  else {
                                    window.history.pushState({}, '', targetPath);
                                    window.dispatchEvent(new CustomEvent('speedotube_navigate', { detail: { path: targetPath } }));
                                  }
                                }}
                                style={{ cursor: 'pointer' }}
                                title={`Go to ${reply.author || reply.handle}'s channel`}
                              >
                                {rAvatar ? (
                                  <img
                                    src={rAvatar}
                                    alt={reply.author || reply.handle}
                                    className="yt-avatar-circle mini"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      if (e.currentTarget.nextSibling) {
                                        e.currentTarget.nextSibling.style.display = 'flex';
                                      }
                                    }}
                                  />
                                ) : null}
                                <div
                                  className="yt-avatar-circle mini"
                                  style={{
                                    display: rAvatar ? 'none' : 'flex',
                                    backgroundColor: getAvatarColor(reply.author || reply.handle),
                                  }}
                                >
                                  {(reply.author || reply.handle || 'U').replace(/^@/, '').charAt(0).toUpperCase()}
                                </div>
                              </div>

                              <div className="yt-comment-body">
                                <div className="yt-comment-author-row">
                                  <span
                                    className="yt-comment-author"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const h = reply.handle || reply.author;
                                      if (!h) return;
                                      const cleanH = h.startsWith('@') ? h : `@${h}`;
                                      const targetPath = `/channel/${encodeURIComponent(cleanH)}`;
                                      if (onNavigate) onNavigate(targetPath);
                                      else {
                                        window.history.pushState({}, '', targetPath);
                                        window.dispatchEvent(new CustomEvent('speedotube_navigate', { detail: { path: targetPath } }));
                                      }
                                    }}
                                    style={{ cursor: 'pointer' }}
                                    title={`Go to ${reply.author || reply.handle}'s channel`}
                                  >
                                    {reply.handle
                                      ? (reply.handle.startsWith('@') ? reply.handle : `@${reply.handle}`)
                                      : `@${reply.author}`}
                                  </span>
                                  <span className="yt-comment-time">{reply.timeAgo}</span>
                                </div>

                                <p className="yt-comment-text">{reply.text}</p>

                                <div className="yt-comment-footer">
                                  <button
                                    type="button"
                                    className={`yt-comment-icon-btn ${reply.userLiked ? 'active' : ''}`}
                                    onClick={() => handleCommentLike(reply.id, true, comment.id)}
                                    aria-label="Like"
                                    data-yt-tooltip="Like"
                                    data-tooltip-pos="bottom"
                                  >
                                    {reply.userLiked ? <MdThumbUp size={14} /> : <MdOutlineThumbUp size={14} />}
                                  </button>
                                  {reply.likes > 0 && (
                                    <span className="yt-comment-likes">{reply.likes.toLocaleString()}</span>
                                  )}

                                  <button
                                    type="button"
                                    className={`yt-comment-icon-btn ${reply.userDisliked ? 'active' : ''}`}
                                    onClick={() => handleCommentDislike(reply.id, true, comment.id)}
                                    aria-label="Dislike"
                                    data-yt-tooltip="Dislike"
                                    data-tooltip-pos="bottom"
                                  >
                                    {reply.userDisliked ? <MdThumbDown size={14} /> : <MdOutlineThumbDown size={14} />}
                                  </button>

                                  <button
                                    type="button"
                                    className="yt-comment-reply-btn"
                                    onClick={() => handleOpenReplyBox(comment.id, reply.handle || reply.author)}
                                  >
                                    Reply
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
          </div>
        </div>
      </div>

      {/* Native YouTube-style Report Modal Dialog with Suboptions & Manual Textbox */}
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={handleCloseReportModal}
        videoData={videoData}
        currentTime={currentTime}
        showToast={showToast}
        getApiHeaders={getApiHeaders}
      />


      {/* Native YouTube-style Share Modal Dialog */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={handleCloseShareModal}
        videoData={videoData}
        videoUrlId={videoUrlId}
        currentTime={currentTime}
        showToast={showToast}
      />

      {/* Native YouTube-style Download Quality Modal */}
      {isDownloadModalOpen && (
        <div
          className={`yt-watch-download-modal-overlay ${isDownloadModalClosing ? 'exiting' : ''}`}
          onClick={closeDownloadModal}
        >
          <div
            className={`yt-watch-download-modal ${isDownloadModalClosing ? 'exiting' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="yt-watch-download-modal-header">
              <h3 className="yt-watch-download-modal-title">Download video</h3>
              <button
                className="yt-watch-download-modal-close"
                onClick={closeDownloadModal}
                aria-label="Close"
              >
                <MdClose size={20} />
              </button>
            </div>

            <div className="yt-watch-download-options">
              {QUALITY_PRESETS.map((q) => {
                const isSelected = selectedDownloadQuality === q.id;
                const estimatedSize = estimateVideoSize(
                  typeof videoData?.duration === 'number' ? videoData.duration : 180,
                  q.id
                );
                return (
                  <div
                    key={q.id}
                    className={`yt-watch-download-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => setSelectedDownloadQuality(q.id)}
                  >
                    <div className="yt-watch-download-opt-left">
                      <input
                        type="radio"
                        name="watchDownloadQuality"
                        value={q.id}
                        checked={isSelected}
                        onChange={() => setSelectedDownloadQuality(q.id)}
                      />
                      <span className="yt-watch-download-opt-name">{q.label}</span>
                    </div>
                    <span className="yt-watch-download-opt-size">{estimatedSize}</span>
                  </div>
                );
              })}
            </div>

            <label className="yt-watch-download-remember-row">
              <input
                type="checkbox"
                checked={rememberDownloadQuality}
                onChange={(e) => setRememberDownloadQuality(e.target.checked)}
              />
              <span>Remember my download quality setting</span>
            </label>

            <div className="yt-watch-download-modal-actions">
              <button
                className="yt-watch-download-cancel-btn"
                onClick={closeDownloadModal}
              >
                Cancel
              </button>
              <button
                className="yt-watch-download-confirm-btn"
                onClick={handleConfirmDownloadModal}
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Smooth Sign-In Prompt Modal for Logged-Out Interactivity */}
      <SignInPromptModal
        isOpen={signInPrompt.isOpen}
        action={signInPrompt.action}
        targetName={signInPrompt.targetName}
        isAlbum={isAlbumMode}
        onSignIn={handlePromptSignIn}
        onClose={closeSignInPrompt}
      />

      {/* Toast Notification Container */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        icon={toast.icon}
        toastKey={toast.toastKey}
        onClose={handleCloseToast}
      />
    </div>
  );
}
